import { Fragment, jsx, jsxs } from "react/jsx-runtime";
import React, {
  useState,
  useEffect,
  useCallback,
  useMemo,
  useRef
} from "react";
import {
  Classes,
  InputGroup,
  TextArea,
  Icon,
  ContextMenu,
  Menu,
  MenuItem,
  Popover,
  MenuDivider,
  Dialog,
  Alert,
  Button,
  ButtonGroup,
  Intent,
  Position,
  Tooltip,
  RadioGroup,
  Radio,
  Spinner,
  HTMLSelect,
  Collapse
} from "@blueprintjs/core";
import { useDebounce } from "use-debounce";
import axios from "axios";
import { anyToJson } from "@teselagen/bio-parsers";
import showFileDialog from "../../src/utils/showFileDialog";
import ReactList from "@teselagen/react-list";
import classNames from "classnames";
const BASE_PATTERNS = [
  "in_inventory:yes",
  "in_inventory:no",
  "in_inventory:true",
  "in_inventory:false",
  "inventory:yes",
  "inventory:no",
  "inventory:true",
  "inventory:false",
  "ininventory:yes",
  "ininventory:no",
  "ininventory:true",
  "ininventory:false",
  "folder:"
];
const getGhostSuggestion = (query, folderList = []) => {
  if (!query) return null;
  const match = query.match(/(?:^|\s)(\S+)$/);
  if (!match) return null;
  const lastToken = match[1];
  if (lastToken.length < 2) return null;
  const lowerToken = lastToken.toLowerCase();
  const folderPatterns = (folderList || []).map((f) => {
    const name = typeof f === "string" ? f : f.name || f.path || "";
    return name ? name : null;
  }).filter((name) => name && name.toLowerCase() !== "projects").map((name) => `folder:${name}`);
  const allPatterns = lowerToken === "folder:" ? BASE_PATTERNS : [...BASE_PATTERNS, ...folderPatterns];
  for (const pattern of allPatterns) {
    if (pattern.toLowerCase().startsWith(lowerToken)) {
      const suffix = pattern.slice(lastToken.length);
      if (suffix) {
        return { lastToken, suffix, pattern };
      }
    }
  }
  return null;
};
import {
  SEQUENCE_EXTENSIONS,
  ASSEMBLY_EXTENSIONS,
  JBROWSE_EXTENSIONS,
  SAM_EXTENSIONS,
  CHROMATOGRAM_EXTENSIONS,
  ALL_EDITABLE_EXTENSIONS,
  IconWithGitOverlay,
  getFileIcon
} from "./utils/fileIcons";
import { registerFileContextMenuHandler } from "./utils/fileContextMenu";
import { getFileClipboard, setFileClipboard, subscribeToFileClipboard, setRenamingState, getActiveTableSelection } from "./utils/fileClipboard";
import { matchesQuery } from "./utils/searchUtils";
import { getErrorMessage } from "./utils/errorUtils";
import { FolderPicker } from "../../src/AutoAnnotate";
import checkmarkMarker from "./assets/icons/git/markers/checkmark.png";
import modifiedMarker from "./assets/icons/git/markers/modified.png";
import stagedMarker from "./assets/icons/git/markers/staged.png";
import symlinkIcon from "./assets/icons/symlink.png";
import dnaIcon from "./assets/icons/linear_dna.png";
import circularDnaIcon from "./assets/icons/plasmid.png";
import assemblyIcon from "./assets/icons/assembly.png";
const isStaged = (status) => {
  if (!status) return false;
  const code = status.includes(":") ? status.split(":")[0] : status;
  const index = code[0];
  return index === "M" || index === "A" || index === "R" || index === "C" || index === "D" || index === "T";
};
const isUnstaged = (status) => {
  if (!status) return false;
  const code = status.includes(":") ? status.split(":")[0] : status;
  return code[1] !== " " && code[1] !== void 0;
};
const getStatusDescription = (code) => {
  const map = {
    "M": "Modified",
    "A": "Added",
    "D": "Deleted",
    "R": "Renamed",
    "C": "Copied",
    "T": "Type Changed",
    "?": "Untracked",
    "U": "Unmerged"
  };
  if (!code) return "";
  const chars = code.trim().split("").filter((c) => c !== " ");
  return chars.map((c) => map[c] || c).join(", ");
};
const hasModifiedDescendant = (path, gitStatus) => {
  if (!gitStatus) return false;
  const keys = Object.keys(gitStatus);
  if (path === "" || path === ".") return keys.length > 0;
  const prefix = path.endsWith("/") ? path : path + "/";
  return keys.some((k) => k === path || k.startsWith(prefix));
};
const hasStagedDescendant = (path, gitStatus) => {
  if (!gitStatus) return false;
  const keys = Object.keys(gitStatus);
  if (path === "" || path === ".") {
    return keys.some((k) => isStaged(gitStatus[k]));
  }
  const prefix = path.endsWith("/") ? path : path + "/";
  return keys.some(
    (k) => (k === path || k.startsWith(prefix)) && isStaged(gitStatus[k])
  );
};
const hasUnstagedDescendant = (path, gitStatus) => {
  if (!gitStatus) return false;
  const keys = Object.keys(gitStatus);
  if (path === "" || path === ".") {
    return keys.some((k) => isUnstaged(gitStatus[k]));
  }
  const prefix = path.endsWith("/") ? path : path + "/";
  return keys.some(
    (k) => (k === path || k.startsWith(prefix)) && isUnstaged(gitStatus[k])
  );
};
const getParentsOfPath = (path) => {
  const newExpanded = {};
  if (path) {
    const cleanPath = path.replace(/^projects\//, "");
    const parts = cleanPath.split("/");
    if (parts.length > 1) {
      let currentPathId = "";
      for (let i = 0; i < parts.length - 1; i++) {
        const seg = parts[i];
        currentPathId = currentPathId ? currentPathId + "/" + seg : seg;
        newExpanded[currentPathId] = true;
        newExpanded["projects/" + currentPathId] = true;
        newExpanded["./" + currentPathId] = true;
      }
    }
  }
  return newExpanded;
};
const transformToTreeNodes = (item, selectedPaths, expandedIds, idPrefix = "", onDragStart, onDragOver, onDrop, onDragLeave, dragOverPath, dragOverPosition, gitStatus, lastClickedPath, renamingPath, tempRenameValue, onRenameChange, onRenameConfirm, onRenameCancel, editingDescriptionPath, tempDescriptionValue, onDescriptionChange, onDescriptionConfirm, onDescriptionCancel, onStartRename, onStartEditDescription, optimisticDescriptions = {}, copiedItems = [], isCut = false, renamingSource = null, optimisticInventory = {}, optimisticHasComments = {}, loadingFolders = {}) => {
  const id = idPrefix + item.name;
  const isDir = item.type === "directory";
  const isSelected = selectedPaths.includes(item.path);
  const isExpanded = isDir && (expandedIds[id] || expandedIds[item.path] || false);
  const isDragOver = dragOverPath === item.path;
  const isDragOverMiddle = isDragOver && dragOverPosition === "middle";
  const isDragOverTop = isDragOver && dragOverPosition === "top";
  const isDragOverBottom = isDragOver && dragOverPosition === "bottom";
  const isCutItem = isCut && copiedItems.some((ci) => ci.path === item.path);
  const fileStatus = gitStatus ? gitStatus[item.path] : null;
  const isModified = !!fileStatus;
  const isFileStaged = isStaged(fileStatus);
  const isDirModified = isDir && hasModifiedDescendant(item.path, gitStatus);
  const isDirStaged = isDir && hasStagedDescendant(item.path, gitStatus);
  const isDirHasUnstaged = isDir && hasUnstagedDescendant(item.path, gitStatus);
  const canEditDescription = !isDir && [...SEQUENCE_EXTENSIONS, ...ASSEMBLY_EXTENSIONS, ...SAM_EXTENSIONS].some(
    (ext) => item.name.toLowerCase().endsWith(ext)
  );
  const currentDescription = optimisticDescriptions[item.path] !== void 0 ? optimisticDescriptions[item.path] : item.description;
  let statusToOverlay = item.isNewUnsaved ? null : fileStatus || (gitStatus ? "tracked" : null);
  if (isDir) {
    statusToOverlay = isDirStaged ? "M " : isDirModified ? " M" : gitStatus ? "tracked-dir" : null;
  } else if (statusToOverlay && statusToOverlay.includes(":")) {
    statusToOverlay = statusToOverlay.split(":")[0];
  }
  const label = /* @__PURE__ */ jsxs(
    "div",
    {
      draggable: true,
      className: isSelected ? "selected-file-node" : "",
      "data-testid": `file-tree-label-${item.path}`,
      "data-file-path": item.path,
      "data-git-status": statusToOverlay || "",
      onDragStart: (e) => onDragStart(e, item),
      onDragOver: (e) => onDragOver(e, item),
      onDragLeave: (e) => onDragLeave(e, item),
      onDrop: (e) => onDrop(e, item),
      title: currentDescription || "",
      style: {
        display: "flex",
        alignItems: "flex-start",
        backgroundColor: isDragOverMiddle && isDir ? "rgba(16, 107, 163, 0.2)" : "transparent",
        borderRadius: "3px",
        cursor: "pointer",
        padding: "4px 4px",
        height: "auto",
        width: "100%",
        position: "relative",
        opacity: isCutItem ? 0.5 : 1
      },
      children: [
        isDragOverTop && /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              position: "absolute",
              top: 0,
              left: 0,
              right: 0,
              height: "2px",
              backgroundColor: "black",
              zIndex: 10
            }
          }
        ),
        isDragOverBottom && /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              height: "2px",
              backgroundColor: "black",
              zIndex: 10
            }
          }
        ),
        /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              whiteSpace: "nowrap",
              marginRight: "10px",
              color: isSelected ? "white" : "inherit",
              flexShrink: 0
            },
            onClick: (e) => {
              if (isSelected && selectedPaths.length === 1 && !isDir && lastClickedPath === item.path) {
                e.stopPropagation();
                onStartRename(item);
              }
            },
            children: renamingPath === item.path && renamingSource === "explorer" ? /* @__PURE__ */ jsx(
              InputGroup,
              {
                autoFocus: true,
                small: true,
                value: tempRenameValue,
                onChange: (e) => onRenameChange(e.target.value),
                onKeyDown: (e) => {
                  if (e.key === "Enter") {
                    e.stopPropagation();
                    onRenameConfirm();
                  } else if (e.key === "Escape") {
                    e.stopPropagation();
                    onRenameCancel();
                  }
                },
                onBlur: onRenameCancel,
                onFocus: (e) => {
                  const input = e.target;
                  const val = input.value;
                  const ext = ALL_EDITABLE_EXTENSIONS.find((ex) => val.endsWith(ex)) || "";
                  const nameWithoutExt = val.slice(0, val.length - ext.length);
                  const copyMatch = nameWithoutExt.match(/_copy\d+$/);
                  if (copyMatch) {
                    const start = copyMatch.index;
                    const end = nameWithoutExt.length;
                    setTimeout(() => input.setSelectionRange(start, end), 0);
                  } else {
                    setTimeout(
                      () => input.setSelectionRange(0, nameWithoutExt.length),
                      0
                    );
                  }
                },
                "data-testid": "rename-input",
                style: { width: "150px" },
                onClick: (e) => e.stopPropagation()
              }
            ) : item.isNewUnsaved ? /* @__PURE__ */ jsx("span", { style: { fontStyle: "italic", opacity: 0.7 }, children: item.name }) : item.isSymlink ? /* @__PURE__ */ jsx("i", { children: item.name }) : item.name
          }
        ),
        /* @__PURE__ */ jsx(
          "div",
          {
            style: {
              color: isSelected ? "rgba(255, 255, 255, 0.7)" : "#888",
              fontSize: "0.85em",
              whiteSpace: "nowrap",
              flexShrink: 0,
              marginTop: "2px",
              minWidth: isSelected && canEditDescription ? "50px" : "auto",
              minHeight: isSelected && canEditDescription ? "1.2em" : "auto",
              fontStyle: !currentDescription ? "italic" : "normal",
              cursor: isSelected && canEditDescription ? "text" : "default"
            },
            onClick: (e) => {
              if (isSelected && selectedPaths.length === 1 && canEditDescription) {
                e.stopPropagation();
                onStartEditDescription(item);
              }
            },
            children: editingDescriptionPath === item.path ? /* @__PURE__ */ jsx(
              InputGroup,
              {
                "data-testid": "inline-description-input",
                autoFocus: true,
                small: true,
                value: tempDescriptionValue,
                onChange: (e) => onDescriptionChange(e.target.value),
                onKeyDown: (e) => {
                  if (e.key === "Enter") {
                    e.stopPropagation();
                    onDescriptionConfirm();
                  } else if (e.key === "Escape") {
                    e.stopPropagation();
                    onDescriptionCancel();
                  }
                },
                onBlur: onDescriptionCancel,
                onClick: (e) => e.stopPropagation(),
                style: { width: "200px" }
              }
            ) : currentDescription || (isSelected && canEditDescription ? "Add description..." : "")
          }
        )
      ]
    }
  );
  const iconStyle = { width: 16, height: 16 };
  const baseIcon = isDir && loadingFolders && loadingFolders[item.path] ? /* @__PURE__ */ jsx(Spinner, { size: 14, style: { marginRight: 5 } }) : getFileIcon(item, iconStyle);
  const resolvedInInventory = optimisticInventory[item.path] !== void 0 ? optimisticInventory[item.path] : item.inInventory;
  const resolvedHasComments = optimisticHasComments[item.path] !== void 0 ? optimisticHasComments[item.path] : item.hasComments;
  const icon = /* @__PURE__ */ jsx(
    IconWithGitOverlay,
    {
      baseIcon,
      isSymlink: item.isSymlink,
      gitStatus: statusToOverlay,
      inInventory: resolvedInInventory,
      hasComments: resolvedHasComments,
      isSelected
    }
  );
  let canGitAdd = false;
  if (isDir) {
    canGitAdd = isDirHasUnstaged;
  } else {
    canGitAdd = isUnstaged(fileStatus);
  }
  const canGitUnstage = isDir ? isDirStaged : isFileStaged;
  let canGitRevert = false;
  if (isDir) {
    if (gitStatus) {
      const keys = Object.keys(gitStatus);
      const prefix = item.path === "" || item.path === "." ? "" : item.path.endsWith("/") ? item.path : item.path + "/";
      canGitRevert = keys.some((k) => {
        if (k === item.path || k.startsWith(prefix)) {
          return gitStatus[k] !== "??";
        }
        return false;
      });
    }
  } else {
    canGitRevert = fileStatus && fileStatus !== "??";
  }
  return {
    id,
    hasCaret: isDir && item.hasChildren !== false,
    icon,
    label,
    isSelected,
    isExpanded,
    nodeData: item,
    canGitAdd,
    canGitUnstage,
    canGitRevert,
    childNodes: item.children ? item.children.map(
      (child) => transformToTreeNodes(
        child,
        selectedPaths,
        expandedIds,
        id + "/",
        onDragStart,
        onDragOver,
        onDrop,
        onDragLeave,
        dragOverPath,
        dragOverPosition,
        gitStatus,
        lastClickedPath,
        renamingPath,
        tempRenameValue,
        onRenameChange,
        onRenameConfirm,
        onRenameCancel,
        editingDescriptionPath,
        tempDescriptionValue,
        onDescriptionChange,
        onDescriptionConfirm,
        onDescriptionCancel,
        onStartRename,
        onStartEditDescription,
        optimisticDescriptions,
        copiedItems,
        isCut,
        renamingSource,
        optimisticInventory,
        optimisticHasComments,
        loadingFolders
      )
    ) : void 0
  };
};
const flattenTree = (nodes, level = 0, result = []) => {
  nodes.forEach((node) => {
    result.push({ ...node, level });
    if (node.isExpanded && node.childNodes && node.childNodes.length > 0) {
      flattenTree(node.childNodes, level + 1, result);
    }
  });
  return result;
};
export const removeNodesFromTree = (root, pathsToRemoveSet) => {
  if (!root) return root;
  const filterNode = (node) => {
    if (!node) return null;
    if (pathsToRemoveSet.has(node.path)) {
      return null;
    }
    if (node.children) {
      return {
        ...node,
        children: node.children.map(filterNode).filter(Boolean)
      };
    }
    return node;
  };
  return Array.isArray(root) ? root.map(filterNode).filter(Boolean) : filterNode(root);
};
export const filterNodes = (items, query, isRecursiveMatch = false, sequenceSearchResults = [], parentMatched = false, forceVisiblePaths = null) => {
  if (!query || !Array.isArray(items)) return items;
  return items.reduce((acc, item) => {
    const isSequenceMatch = sequenceSearchResults.includes(item.path);
    const isForcedVisible = !!forceVisiblePaths && forceVisiblePaths.has(item.path);
    const matches = matchesQuery(item, query) || isSequenceMatch || isForcedVisible;
    const isDirectMatch = matches && (!parentMatched || isSequenceMatch || isForcedVisible || matchesQuery({ ...item, path: item.name }, query));
    const showAllChildren = isRecursiveMatch || matches;
    if (item.type === "directory") {
      const children = item.children || [];
      const filteredChildren = filterNodes(
        children,
        query,
        showAllChildren,
        sequenceSearchResults,
        showAllChildren,
        forceVisiblePaths
      );
      const hasMatchInChildren = filteredChildren.some(
        (c) => c.isDirectMatch || c.hasMatchInChildren
      );
      if (showAllChildren || filteredChildren.length > 0) {
        acc.push({
          ...item,
          children: filteredChildren,
          isMatch: matches,
          isDirectMatch,
          hasMatchInChildren
        });
      }
    } else if (showAllChildren || matches) {
      acc.push({
        ...item,
        isMatch: matches,
        isDirectMatch
      });
    }
    return acc;
  }, []);
};
const applyRenameInTree = (root, oldPath, newPath) => {
  if (!root) return root;
  const patchNode = (node) => {
    if (node.path === oldPath || node.path.startsWith(oldPath + "/")) {
      const updatedPath = node.path.replace(oldPath, newPath);
      const updatedName = updatedPath.split("/").pop();
      return {
        ...node,
        path: updatedPath,
        name: updatedName,
        children: node.children ? node.children.map(patchNode) : void 0
      };
    }
    if (node.children) {
      const newChildren = node.children.map(patchNode);
      const changed = newChildren.some((c, i) => c !== node.children[i]);
      if (changed) return { ...node, children: newChildren };
    }
    return node;
  };
  return patchNode(root);
};
const findItemByPath = (root, path) => {
  if (!root) return null;
  if (Array.isArray(root)) {
    for (const item of root) {
      const found = findItemByPath(item, path);
      if (found) return found;
    }
    return null;
  }
  if (root.path === path) return root;
  if (root.children) {
    for (const child of root.children) {
      const found = findItemByPath(child, path);
      if (found) return found;
    }
  }
  return null;
};
const FileExplorer = ({ debounceDelay = 200 }) => {
  const explorerRef = useRef(null);
  const pendingNavigationRef = useRef(null);
  useEffect(() => {
    return () => {
      if (pendingNavigationRef.current) {
        clearTimeout(pendingNavigationRef.current);
        pendingNavigationRef.current = null;
      }
    };
  }, []);
  const [rawFiles, setRawFiles] = useState(() => {
    try {
      const cached = localStorage.getItem("dtx_cached_file_tree");
      if (cached) {
        const parsed = JSON.parse(cached);
        if (parsed && (parsed.children || parsed.name)) {
          return parsed;
        }
      }
    } catch (e) {
    }
    return null;
  });
  const [gitStatus, setGitStatus] = useState(() => {
    try {
      const cached = localStorage.getItem("dtx_cached_git_status");
      if (cached) return JSON.parse(cached);
    } catch (e) {
    }
    return null;
  });
  const [gitAheadCount, setGitAheadCount] = useState(0);
  const [gitBehindCount, setGitBehindCount] = useState(0);
  const [selectedPaths, setSelectedPaths] = useState([]);
  const [lastClickedPath, setLastClickedPath] = useState(null);
  const [isFolderDialogOpen, setIsFolderDialogOpen] = useState(false);
  const [newFolderName, setNewFolderName] = useState("");
  const [targetFolderPath, setTargetFolderPath] = useState("");
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [itemsToDelete, setItemsToDelete] = useState([]);
  const handleDeleteItems = useCallback((items) => {
    setItemsToDelete(items);
    setIsDeleteDialogOpen(true);
  }, []);
  const [renamingPath, setRenamingPath] = useState(null);
  const [tempRenameValue, setTempRenameValue] = useState("");
  const [renamingSource, setRenamingSource] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [loading, setLoading] = useState(() => {
    try {
      return !localStorage.getItem("dtx_cached_file_tree");
    } catch (e) {
      return true;
    }
  });
  const [loadingFolders, setLoadingFolders] = useState({});
  const [dragOverPath, setDragOverPath] = useState(null);
  const [dragOverPosition, setDragOverPosition] = useState(null);
  const [pinnedFolder, setPinnedFolder] = useState(null);
  const [stagingPaths, setStagingPaths] = useState([]);
  const [unstagingPaths, setUnstagingPaths] = useState([]);
  const [isUnstagedChangesExpanded, setIsUnstagedChangesExpanded] = useState(false);
  const [isRevertAlertOpen, setIsRevertAlertOpen] = useState(false);
  const [isReverting, setIsReverting] = useState(false);
  const [itemToRevert, setItemToRevert] = useState(null);
  const [sourcePathToRevert, setSourcePathToRevert] = useState(null);
  const [copiedItems, setCopiedItemsState] = useState([]);
  const [isCut, setIsCutState] = useState(false);
  const [unsavedNewFile, setUnsavedNewFile] = useState(null);
  useEffect(() => {
    const {
      copiedItems: initialItems,
      isCut: initialCut,
      renamingPath: initialRenaming,
      tempRenameValue: initialTemp,
      renamingSource: initialSource
    } = getFileClipboard();
    setCopiedItemsState(initialItems);
    setIsCutState(initialCut);
    setRenamingPath(initialRenaming);
    setTempRenameValue(initialTemp);
    setRenamingSource(initialSource);
    return subscribeToFileClipboard((state) => {
      console.log("[DEBUG] FileExplorer clipboard update:", state);
      setCopiedItemsState(state.copiedItems);
      setIsCutState(state.isCut);
      setRenamingPath(state.renamingPath);
      setTempRenameValue(state.tempRenameValue);
      setRenamingSource(state.renamingSource);
    });
  }, []);
  const setCopiedItems = (items) => setFileClipboard(items, false);
  const setIsCut = (cut) => {
    const { copiedItems: currentItems } = getFileClipboard();
    setFileClipboard(currentItems, cut);
  };
  const [duplicationSourcePath, setDuplicationSourcePath] = useState(null);
  const [isMoveDialogOpen, setIsMoveDialogOpen] = useState(false);
  const [moveDestination, setMoveDestination] = useState("");
  const [itemsToMove, setItemsToMove] = useState([]);
  const [isMoving, setIsMoving] = useState(false);
  const [isFileCreating, setIsFileCreating] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [isCreateFileDialogOpen, setIsCreateFileDialogOpen] = useState(false);
  const [newFileName, setNewFileName] = useState("");
  const [newFileType, setNewFileType] = useState("sequence");
  const [newFileCircular, setNewFileCircular] = useState(false);
  const [isNewItemDialogOpen, setIsNewItemDialogOpen] = useState(false);
  const [newItemType, setNewItemType] = useState("sequence");
  const [newItemTargetFolder, setNewItemTargetFolder] = useState(".");
  const [newItemName, setNewItemName] = useState("");
  const [newItemCircular, setNewItemCircular] = useState(false);
  const [newItemAssemblyMethod, setNewItemAssemblyMethod] = useState("fragment_synthesis");
  const [isNewItemAdvancedOpen, setIsNewItemAdvancedOpen] = useState(false);
  const [isNewItemCreating, setIsNewItemCreating] = useState(false);
  const [isMinimapDialogOpen, setIsMinimapDialogOpen] = useState(false);
  const [minimapReferencePath, setMinimapReferencePath] = useState("");
  const [minimapTargetPaths, setMinimapTargetPaths] = useState([]);
  const [isInversionDialogOpen, setIsInversionDialogOpen] = useState(false);
  const [inversionCheckResult, setInversionCheckResult] = useState(null);
  const [isCheckingInversion, setIsCheckingInversion] = useState(false);
  const [inversionFiles, setInversionFiles] = useState([]);
  const [assemblyComponents, setAssemblyComponents] = useState([]);
  const [isAddgeneDialogOpen, setIsAddgeneDialogOpen] = useState(false);
  const [addgeneId, setAddgeneId] = useState("");
  const [isAddgeneImporting, setIsAddgeneImporting] = useState(false);
  const [optimisticDescriptions, setOptimisticDescriptions] = useState({});
  const [optimisticInventory, setOptimisticInventory] = useState({});
  const [optimisticHasComments, setOptimisticHasComments] = useState({});
  const [isCommentsDialogOpen, setIsCommentsDialogOpen] = useState(false);
  const [commentsFilePath, setCommentsFilePath] = useState("");
  const [fileComments, setFileComments] = useState([]);
  const [isLoadingComments, setIsLoadingComments] = useState(false);
  const [newCommentText, setNewCommentText] = useState("");
  const [isPathDialogOpen, setIsPathDialogOpen] = useState(false);
  const [selectedPath, setSelectedPath] = useState("");
  const [selectedDescription, setSelectedDescription] = useState("");
  const [selectedLocus, setSelectedLocus] = useState("");
  const [dateCreated, setDateCreated] = useState(null);
  const [dateModified, setDateModified] = useState(null);
  const [sequenceLength, setSequenceLength] = useState(null);
  const [isLoadingMetadata, setIsLoadingMetadata] = useState(false);
  const descriptionRef = useRef("");
  const locusRef = useRef("");
  const [isSavingDescription, setIsSavingDescription] = useState(false);
  const [editingDescriptionPath, setEditingDescriptionPath] = useState(null);
  const [tempDescriptionValue, setTempDescriptionValue] = useState("");
  const [isBuilding, setIsBuilding] = useState(false);
  const [rawNodeData, setRawNodeData] = useState(null);
  const reactListRef = useRef(null);
  const [isCommandOutputDialogOpen, setIsCommandOutputDialogOpen] = useState(false);
  const [commandOutput, setCommandOutput] = useState("");
  const [commandTitle, setCommandTitle] = useState("");
  const [isGitCommitDialogOpen, setIsGitCommitDialogOpen] = useState(false);
  const [gitCommitDialogSize, setGitCommitDialogSize] = useState(() => {
    try {
      const saved = localStorage.getItem("gitCommitDialogSize");
      if (saved) return JSON.parse(saved);
    } catch (e) {
      console.error("Failed to parse gitCommitDialogSize from localStorage", e);
    }
    return { width: 550, height: 450 };
  });
  const [gitCommitMessage, setGitCommitMessage] = useState("");
  const [isGitOperating, setIsGitOperating] = useState(false);
  const [gitFilter, setGitFilter] = useState("");
  const handleGitCommitResize = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = gitCommitDialogSize.width;
    const startHeight = gitCommitDialogSize.height;
    const onMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;
      setGitCommitDialogSize({
        width: Math.max(400, startWidth + deltaX * 2),
        height: Math.max(300, startHeight + deltaY * 2)
      });
    };
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      setGitCommitDialogSize((currentSize) => {
        localStorage.setItem("gitCommitDialogSize", JSON.stringify(currentSize));
        return currentSize;
      });
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };
  const [isGitDiffDialogOpen, setIsGitDiffDialogOpen] = useState(false);
  const [gitDiff, setGitDiff] = useState("");
  const [gitDiffPath, setGitDiffPath] = useState("");
  const [isGitStatusRawDialogOpen, setIsGitStatusRawDialogOpen] = useState(false);
  const [gitStatusRaw, setGitStatusRaw] = useState("");
  const [isHistoryDialogOpen, setIsHistoryDialogOpen] = useState(false);
  const [historyFilePath, setHistoryFilePath] = useState(null);
  const [historyData, setHistoryData] = useState([]);
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);
  const [restoringCommit, setRestoringCommit] = useState(null);
  const [isHelpDialogOpen, setIsHelpDialogOpen] = useState(false);
  const [tipsDialogSize, setTipsDialogSize] = useState(() => {
    try {
      const saved = localStorage.getItem("tipsDialogSize");
      if (saved) {
        const parsed = JSON.parse(saved);
        return {
          width: Math.max(680, parsed.width || 680),
          height: Math.max(480, parsed.height || 480)
        };
      }
    } catch (e) {
      console.error("Failed to parse tipsDialogSize from localStorage", e);
    }
    return { width: 680, height: 480 };
  });
  const handleTipsResize = (e) => {
    e.preventDefault();
    const startX = e.clientX;
    const startY = e.clientY;
    const startWidth = tipsDialogSize.width;
    const startHeight = tipsDialogSize.height;
    const onMouseMove = (moveEvent) => {
      const deltaX = moveEvent.clientX - startX;
      const deltaY = moveEvent.clientY - startY;
      setTipsDialogSize({
        width: Math.max(400, startWidth + deltaX * 2),
        height: Math.max(300, startHeight + deltaY * 2)
      });
    };
    const onMouseUp = () => {
      document.removeEventListener("mousemove", onMouseMove);
      document.removeEventListener("mouseup", onMouseUp);
      setTipsDialogSize((currentSize) => {
        localStorage.setItem("tipsDialogSize", JSON.stringify(currentSize));
        return currentSize;
      });
    };
    document.addEventListener("mousemove", onMouseMove);
    document.addEventListener("mouseup", onMouseUp);
  };
  const getCurrentFilePath = useCallback(() => {
    const hash = window.location.hash;
    if (hash.includes("?")) {
      const queryPart = hash.split("?")[1];
      const urlParams = new URLSearchParams(queryPart);
      return urlParams.get("file");
    }
    return null;
  }, []);
  const getFileUrl = useCallback((nodePath, { hideProjects, extraParams = {} } = {}) => {
    const searchParams = new URLSearchParams(window.location.search);
    const hashParams = window.location.hash.includes("?") ? new URLSearchParams(window.location.hash.split("?")[1]) : new URLSearchParams();
    let reload = searchParams.get("reload") || hashParams.get("reload");
    let view = extraParams.view;
    let url = nodePath ? `#/Standalone?file=${encodeURIComponent(nodePath)}` : `#/Standalone`;
    if (reload) {
      url += url.includes("?") ? `&reload=${reload}` : `?reload=${reload}`;
    }
    if (view) {
      url += url.includes("?") ? `&view=${view}` : `?view=${view}`;
    }
    if (hideProjects) {
      url += url.includes("?") ? `&hideProjects=true` : `?hideProjects=true`;
    }
    Object.entries(extraParams).forEach(([key, value]) => {
      if (key !== "view") {
        url += url.includes("?") ? `&${key}=${value}` : `?${key}=${value}`;
      }
    });
    return url;
  }, []);
  const [currentFilePath, setCurrentFilePath] = useState(getCurrentFilePath());
  const [expandedIds, setExpandedIds] = useState({});
  const [searchQuery, setSearchQuery] = useState("");
  const [forceVisiblePaths, setForceVisiblePaths] = useState(() => /* @__PURE__ */ new Set());
  useEffect(() => {
    if (searchQuery === "" && forceVisiblePaths.size > 0) {
      setForceVisiblePaths(/* @__PURE__ */ new Set());
    }
  }, [searchQuery]);
  const [sequenceSearchResults, setSequenceSearchResults] = useState([]);
  const [isSearchingSequence, setIsSearchingSequence] = useState(false);
  const [suggestionDismissed, setSuggestionDismissed] = useState(false);
  const [lastToken, setLastToken] = useState("");
  const availableFolders = useMemo(() => {
    if (!rawFiles) return [];
    const list = [];
    const traverse = (node) => {
      if (node && (node.type === "folder" || node.children && node.children.length > 0)) {
        if (node.name) list.push(node.name);
      }
      if (node && node.children) {
        node.children.forEach(traverse);
      }
    };
    traverse(rawFiles);
    return list;
  }, [rawFiles]);
  const ghostSuggestion = useMemo(() => getGhostSuggestion(searchQuery, availableFolders), [searchQuery, availableFolders]);
  useEffect(() => {
    const currentToken = ghostSuggestion ? ghostSuggestion.lastToken : "";
    if (currentToken !== lastToken) {
      setSuggestionDismissed(false);
      setLastToken(currentToken);
    }
  }, [ghostSuggestion, lastToken]);
  const handleSearchKeyDown = (e) => {
    if (ghostSuggestion && !suggestionDismissed) {
      if (e.key === "Tab" || e.key === "ArrowRight") {
        e.preventDefault();
        e.stopPropagation();
        setSearchQuery((prev) => prev + ghostSuggestion.suffix);
        setSuggestionDismissed(true);
      } else if (e.key === "Enter") {
        e.preventDefault();
        e.stopPropagation();
        const fullQuery = searchQuery + ghostSuggestion.suffix;
        setSearchQuery(fullQuery);
        setSuggestionDismissed(true);
        if (fullQuery.trim()) {
          window.location.hash = getFileUrl("projects", {
            extraParams: { view: "table", search: fullQuery }
          });
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        e.stopPropagation();
        setSuggestionDismissed(true);
      }
    } else if (e.key === "Enter" && searchQuery.trim()) {
      e.preventDefault();
      window.location.hash = getFileUrl("projects", {
        extraParams: { view: "table", search: searchQuery }
      });
    }
  };
  useEffect(() => {
    if (currentFilePath) {
      setSelectedPaths((prev) => {
        if (!prev.includes(currentFilePath)) {
          return [currentFilePath];
        }
        return prev;
      });
    }
  }, [currentFilePath]);
  useEffect(() => {
  }, []);
  const [debouncedQuery] = useDebounce(searchQuery, debounceDelay);
  useEffect(() => {
    const cleanQuery = debouncedQuery ? debouncedQuery.trim() : "";
    const isDNA = cleanQuery.length >= 20 && /^[ATGCNRYKMSWBDHVatgcnrykmswbdhv]+$/.test(cleanQuery);
    const isAA = cleanQuery.length >= 6 && /^[ACDEFGHIKLMNPQRSTVWYacdefghiklmnpqrstvwy]+$/.test(cleanQuery);
    if (isDNA || isAA) {
      window.activeDNAQuery = cleanQuery;
      const searchSeq = async () => {
        setIsSearchingSequence(true);
        try {
          const res = await axios.get(
            `/api/search/sequence?query=${encodeURIComponent(cleanQuery)}`
          );
          setSequenceSearchResults(res.data.results || []);
        } catch (e) {
          console.error("Sequence search failed:", e);
          setSequenceSearchResults([]);
        } finally {
          setIsSearchingSequence(false);
        }
      };
      searchSeq();
    } else {
      window.activeDNAQuery = null;
      setSequenceSearchResults([]);
    }
  }, [debouncedQuery]);
  const searchInputRef = useRef(null);
  const currentHashRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const computeDefaultTargetFolder = useCallback(() => {
    const isTableInDOM = !!document.querySelector(".project-files-table-view");
    const isTableActive = isTableInDOM && document.querySelector(".project-files-table-view")?.contains(document.activeElement);
    const tableSel = getActiveTableSelection();
    let selectedItemPath = null;
    if (isTableActive && tableSel?.selectedPaths?.length > 0) {
      selectedItemPath = tableSel.lastClickedPath || tableSel.selectedPaths[0];
    } else if (selectedPaths && selectedPaths.length > 0) {
      selectedItemPath = lastClickedPath || selectedPaths[0];
    } else if (tableSel?.selectedPaths?.length > 0) {
      selectedItemPath = tableSel.lastClickedPath || tableSel.selectedPaths[0];
    }
    if (selectedItemPath && rawFiles) {
      const item = findItemByPath(rawFiles, selectedItemPath);
      if (item) {
        if (item.type === "directory") {
          return item.path;
        } else {
          const parent = item.path.split("/").slice(0, -1).join("/");
          return parent || ".";
        }
      }
    }
    if (pinnedFolder && pinnedFolder !== "." && pinnedFolder !== "projects") {
      return pinnedFolder;
    }
    return ".";
  }, [selectedPaths, lastClickedPath, rawFiles, pinnedFolder]);
  const openNewItemDialog = useCallback(() => {
    const defaultFolder = computeDefaultTargetFolder();
    setNewItemTargetFolder(defaultFolder);
    setNewItemType("sequence");
    setNewItemName("");
    setNewItemCircular(false);
    setNewItemAssemblyMethod("fragment_synthesis");
    setIsNewItemAdvancedOpen(false);
    setIsNewItemDialogOpen(true);
  }, [computeDefaultTargetFolder]);
  const isAnythingStaged = useMemo(() => {
    return hasStagedDescendant(".", gitStatus);
  }, [gitStatus]);
  const isAnythingCommittable = useMemo(() => {
    if (!gitStatus) return false;
    return Object.keys(gitStatus).length > 0;
  }, [gitStatus]);
  useEffect(() => {
    const handleKeyDown2 = (e) => {
      const key = e.key ? e.key.toLowerCase() : "";
      if (e.altKey && (key === "k" || e.code === "KeyK")) {
        e.preventDefault();
        e.stopPropagation();
        if (searchInputRef.current) {
          searchInputRef.current.focus();
          searchInputRef.current.select?.();
        } else {
          const input = document.querySelector(
            '[data-testid="search-input"] input, [data-testid="search-input"], .ve-file-explorer-search input, input[placeholder*="Search by name/desc/DNA"]'
          );
          input?.focus();
          input?.select?.();
        }
      } else if (e.altKey && (key === "n" || e.code === "KeyN")) {
        e.preventDefault();
        e.stopPropagation();
        openNewItemDialog();
      }
    };
    window.addEventListener("keydown", handleKeyDown2, true);
    return () => window.removeEventListener("keydown", handleKeyDown2, true);
  }, [openNewItemDialog]);
  useEffect(() => {
    const handleKeyDown2 = (e) => {
      if (e.key === "Escape") {
        if (isGitDiffDialogOpen) {
          e.preventDefault();
          e.stopPropagation();
          setIsGitDiffDialogOpen(false);
          return;
        } else if (isGitCommitDialogOpen) {
          e.preventDefault();
          e.stopPropagation();
          setIsGitCommitDialogOpen(false);
          return;
        }
      }
      if (e.altKey && e.key.toLowerCase() === "c") {
        const openDialog = document.querySelector(".bp3-overlay-open .bp3-dialog") || document.querySelector(".bp3-overlay-open .bp3-alert") || Array.from(document.querySelectorAll(".bp3-dialog, .bp3-alert")).find(
          (d) => d.offsetParent !== null
        );
        if (openDialog) {
          const commitBtn = Array.from(
            openDialog.querySelectorAll(".bp3-button")
          ).find((b) => b.textContent.trim() === "Commit");
          if (commitBtn) {
            e.preventDefault();
            e.stopPropagation();
            commitBtn.click();
            return;
          }
        }
      }
      if (e.key === "Enter" || e.key === "Escape") {
        const openDialog = document.querySelector(".bp3-overlay-open .bp3-dialog") || document.querySelector(".bp3-overlay-open .bp3-alert") || document.querySelector(".bp3-overlay-open .bp3-popover") || Array.from(document.querySelectorAll(".bp3-dialog, .bp3-alert")).find(
          (d) => d.offsetParent !== null
        );
        if (!openDialog) return;
        if (e.key === "Enter") {
          const activeTag = document.activeElement?.tagName.toLowerCase();
          if (activeTag === "input" || activeTag === "textarea") return;
          const confirmBtn = openDialog.querySelector(".bp3-button.bp3-intent-danger") || openDialog.querySelector(".bp3-button.bp3-intent-primary") || openDialog.querySelector(".bp3-button.bp3-intent-success");
          if (confirmBtn) {
            e.preventDefault();
            e.stopPropagation();
            confirmBtn.click();
            return;
          }
        }
        const closeX = openDialog.querySelector(".bp3-dialog-close-button");
        if (closeX) {
          e.preventDefault();
          e.stopPropagation();
          closeX.click();
          return;
        }
        const closeBtn = Array.from(
          openDialog.querySelectorAll(".bp3-button")
        ).find((b) => {
          const text = b.textContent?.trim().toLowerCase();
          return text === "close" || text === "cancel";
        });
        if (closeBtn) {
          e.preventDefault();
          e.stopPropagation();
          closeBtn.click();
          return;
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown2, true);
    return () => window.removeEventListener("keydown", handleKeyDown2, true);
  }, [isGitDiffDialogOpen, isGitCommitDialogOpen]);
  useEffect(() => {
    if (!loading && scrollContainerRef.current) {
      scrollContainerRef.current.scrollLeft = 0;
    }
  }, [loading]);
  const handleScroll = useCallback((e) => {
  }, []);
  useEffect(() => {
    const startBuild = () => setIsBuilding(true);
    const stopBuild = () => setIsBuilding(false);
    window.addEventListener("build-started", startBuild);
    window.addEventListener("build-finished", stopBuild);
    return () => {
      window.removeEventListener("build-started", startBuild);
      window.removeEventListener("build-finished", stopBuild);
    };
  }, []);
  const customSortRef = useRef({});
  useEffect(() => {
    if (debouncedQuery) {
      customSortRef.current = {};
    }
  }, [debouncedQuery]);
  const applyCustomSort = useCallback((root) => {
    if (!root) return root;
    const sortNode = (node) => {
      let newNode = { ...node };
      if (newNode.children) {
        newNode.children = newNode.children.map(sortNode);
        const order = customSortRef.current[newNode.path || "."];
        if (order) {
          const orderMap = new Map(order.map((name, i) => [name, i]));
          newNode.children.sort((a, b) => {
            const aIndex = orderMap.has(a.name) ? orderMap.get(a.name) : 999999;
            const bIndex = orderMap.has(b.name) ? orderMap.get(b.name) : 999999;
            if (aIndex !== bIndex) return aIndex - bIndex;
            if (a.type !== b.type) return a.type === "directory" ? -1 : 1;
            return a.name.localeCompare(b.name);
          });
        }
      }
      return newNode;
    };
    return Array.isArray(root) ? root.map(sortNode) : sortNode(root);
  }, []);
  const rawFilesRef = useRef(rawFiles);
  rawFilesRef.current = rawFiles;
  const expandedIdsRef = useRef(expandedIds);
  expandedIdsRef.current = expandedIds;
  const fetchFiles = useCallback(async (options = {}) => {
    const { isInitial = false } = options;
    if (isInitial && !rawFilesRef.current) {
      try {
        const expandedPaths = Object.keys(expandedIdsRef.current || {}).filter(
          (k) => expandedIdsRef.current[k] && !k.startsWith(".")
        );
        const shallowUrl = `/api/files?depth=1${expandedPaths.length > 0 ? `&expand_paths=${encodeURIComponent(expandedPaths.join(","))}` : ""}&t=${Date.now()}`;
        const shallowRes = await axios.get(shallowUrl);
        if (shallowRes.data && shallowRes.data.files) {
          setRawFiles(applyCustomSort(shallowRes.data.files));
          setGitStatus(shallowRes.data.git_status || {});
          setGitAheadCount(shallowRes.data.git_ahead_count || 0);
          setGitBehindCount(shallowRes.data.git_behind_count || 0);
          setLoading(false);
        }
      } catch (e) {
        console.error("Shallow file fetch error:", e);
      }
    }
    try {
      let response;
      try {
        const dbRes = await axios.get(`/api/db/tree?t=${Date.now()}`);
        if (dbRes && dbRes.data && (dbRes.data.files || dbRes.data.tree)) {
          response = dbRes;
        }
      } catch (dbErr) {
      }
      if (!response) {
        response = await axios.get(`/api/files?t=${Date.now()}`);
      }
      if (response.data && response.data.files) {
        setRawFiles(applyCustomSort(response.data.files));
        setGitStatus(response.data.git_status || {});
        setGitAheadCount(response.data.git_ahead_count || 0);
        setGitBehindCount(response.data.git_behind_count || 0);
        try {
          localStorage.setItem("dtx_cached_file_tree", JSON.stringify(response.data.files));
          if (response.data.git_status) {
            localStorage.setItem("dtx_cached_git_status", JSON.stringify(response.data.git_status));
          }
        } catch (e) {
        }
        window.dispatchEvent(
          new CustomEvent("file-tree-updated", { detail: response.data })
        );
      } else {
        setRawFiles(applyCustomSort(response.data));
        setGitStatus({});
        setGitAheadCount(0);
        setGitBehindCount(0);
        try {
          localStorage.setItem("dtx_cached_file_tree", JSON.stringify(response.data));
        } catch (e) {
        }
        window.dispatchEvent(
          new CustomEvent("file-tree-updated", {
            detail: { files: response.data }
          })
        );
      }
    } catch (error) {
      console.error("Error fetching files:", error);
    } finally {
      setLoading(false);
    }
  }, [applyCustomSort]);
  useEffect(() => {
    const handleFileSaved = () => {
      fetchFiles();
    };
    const handleRefreshRequested = () => {
      fetchFiles();
    };
    window.addEventListener("file-saved", handleFileSaved);
    window.addEventListener("file-tree-refresh-requested", handleRefreshRequested);
    const pollHash = async () => {
      try {
        const response = await axios.get(
          "/api/files/hash"
        );
        const newHash = response.data.hash;
        if (currentHashRef.current && newHash !== currentHashRef.current) {
          fetchFiles();
        }
        currentHashRef.current = newHash;
      } catch (error) {
        console.error("Error polling files hash:", error);
      }
    };
    const intervalId = setInterval(pollHash, 5e3);
    pollHash();
    return () => {
      clearInterval(intervalId);
      window.removeEventListener("file-saved", handleFileSaved);
      window.removeEventListener("file-tree-refresh-requested", handleRefreshRequested);
    };
  }, [fetchFiles]);
  useEffect(() => {
    const hash = window.location.hash;
    if (hash.includes("new=true") && hash.includes("file=")) {
      const urlParams = new URLSearchParams(hash.split("?")[1] || "");
      const newFilePath = urlParams.get("file");
      const type = urlParams.get("type");
      const circular = urlParams.get("circular") === "true";
      if (newFilePath) {
        setUnsavedNewFile({ path: newFilePath, type, circular });
      }
    }
  }, [currentFilePath]);
  useEffect(() => {
    const handleFileSaved = () => {
      setUnsavedNewFile(null);
    };
    const handleFileDiscarded = () => {
      setUnsavedNewFile(null);
    };
    window.addEventListener("file-saved", handleFileSaved);
    window.addEventListener("file-discarded", handleFileDiscarded);
    return () => {
      window.removeEventListener("file-saved", handleFileSaved);
      window.removeEventListener("file-discarded", handleFileDiscarded);
    };
  }, []);
  useEffect(() => {
    fetchFiles({ isInitial: true });
  }, [fetchFiles]);
  useEffect(() => {
    const handleHashChange = () => {
      const newPath = getCurrentFilePath();
      setCurrentFilePath(newPath);
      if (newPath && searchQuery) {
        if (forceVisiblePaths.has(newPath)) {
          return;
        }
        const item = findItemByPath(rawFiles, newPath);
        if (item) {
          const isSequenceMatch = sequenceSearchResults && sequenceSearchResults.includes(item.path);
          if (matchesQuery(item, searchQuery) || isSequenceMatch) {
            return;
          }
          if (item.type === "directory") {
            const filtered = filterNodes(
              [item],
              searchQuery,
              false,
              sequenceSearchResults,
              false,
              forceVisiblePaths
            );
            if (filtered.length > 0) {
              return;
            }
          }
          setSearchQuery("");
        } else {
          if (!matchesQuery(newPath, searchQuery)) {
            setSearchQuery("");
          }
        }
      }
    };
    window.addEventListener("hashchange", handleHashChange);
    return () => window.removeEventListener("hashchange", handleHashChange);
  }, [getCurrentFilePath, searchQuery, rawFiles, sequenceSearchResults, forceVisiblePaths]);
  const prevFilePathRef = useRef(null);
  const hasExpandedInitial = useRef(false);
  const lastScrolledPathRef = useRef(null);
  useEffect(() => {
    if (rawFiles && currentFilePath) {
      const pathChanged = currentFilePath !== prevFilePathRef.current;
      if (!hasExpandedInitial.current || pathChanged) {
        setExpandedIds((prev) => ({ ...prev, ...getParentsOfPath(currentFilePath) }));
        hasExpandedInitial.current = true;
        prevFilePathRef.current = currentFilePath;
      }
    }
  }, [currentFilePath, rawFiles]);
  const isFirstScrollRef = useRef(true);
  const handleDuplicate = useCallback(
    async (item, targetDir, source = "explorer") => {
      if (item.path.endsWith("sam.json") || item.path.endsWith("jbrowse.zip")) {
        window.toastr?.error(
          `Duplicating ${item.path.endsWith("sam.json") ? "sam.json" : "jbrowse.zip"} files is not allowed`
        );
        return;
      }
      try {
        const response = await axios.post("/api/file/duplicate", {
          path: item.path,
          target_dir: targetDir
        });
        const { newPath } = response.data;
        await fetchFiles();
        if (newPath) {
          const newItem = {
            path: newPath,
            name: newPath.split("/").pop(),
            type: "file"
          };
          setDuplicationSourcePath(item.path);
          setTimeout(() => {
            setRenamingState(newItem.path, newItem.name, source);
          }, 100);
        }
      } catch (error) {
        console.error("Error duplicating file:", error);
        window.toastr?.error(`Error duplicating file: ${getErrorMessage(error)}`);
      }
    },
    [fetchFiles, getFileUrl]
  );
  const handlePaste = useCallback(
    async (targetFolder) => {
      if (!copiedItems || copiedItems.length === 0) return;
      try {
        if (isCut) {
          for (const item of copiedItems) {
            await axios.post("/api/file/move", {
              old_path: item.path,
              new_path: targetFolder.path
            });
          }
          setCopiedItems([]);
          setIsCut(false);
        } else {
          for (const item of copiedItems) {
            await handleDuplicate(item, targetFolder.path);
          }
        }
        await fetchFiles();
        setExpandedIds((prev) => ({ ...prev, [targetFolder.path]: true }));
      } catch (error) {
        console.error("Error pasting file:", error);
        window.toastr?.error(`Error pasting file: ${getErrorMessage(error)}`);
      }
    },
    [copiedItems, isCut, fetchFiles, handleDuplicate]
  );
  const handlePasteAlias = useCallback(
    async (targetFolder) => {
      if (!copiedItems || copiedItems.length === 0) return;
      try {
        for (const item of copiedItems) {
          const linkName = item.name;
          const linkPath = targetFolder.path === "." || targetFolder.path === "" ? linkName : `${targetFolder.path}/${linkName}`;
          const targetPath = item.isSymlink ? item.symlinkTarget : item.path;
          await axios.post("/api/file/symlink", {
            target_path: targetPath,
            link_path: linkPath
          });
        }
        await fetchFiles();
        setExpandedIds((prev) => ({ ...prev, [targetFolder.path]: true }));
      } catch (error) {
        console.error("Error pasting alias:", error);
        window.toastr?.error(`Error pasting alias: ${getErrorMessage(error)}`);
      }
    },
    [copiedItems, fetchFiles]
  );
  const handleGoToAliasSource = useCallback(
    async (targetPath) => {
      const parts = targetPath.split("/");
      if (parts.length > 1) {
        setExpandedIds((prev) => {
          const newExpanded = { ...prev };
          let currentPathId = "";
          for (let i = 0; i < parts.length - 1; i++) {
            currentPathId = currentPathId ? currentPathId + "/" + parts[i] : parts[i];
            newExpanded[currentPathId] = true;
          }
          return newExpanded;
        });
      }
      const targetItem = findItemByPath(rawFiles, targetPath);
      const isDir = targetItem && targetItem.type === "directory";
      window.location.hash = getFileUrl(targetPath, isDir ? { extraParams: { view: "table" } } : {});
    },
    [getFileUrl, setExpandedIds, rawFiles]
  );
  const handleDragStart = useCallback((e, item) => {
    let pathsToDrag = [item.path];
    if (selectedPaths.includes(item.path)) {
      pathsToDrag = selectedPaths;
    }
    e.dataTransfer.setData("itemPaths", JSON.stringify(pathsToDrag));
    e.dataTransfer.effectAllowed = "move";
  }, [selectedPaths]);
  const handleDragOver = useCallback((e, item) => {
    e.preventDefault();
    e.stopPropagation();
    const rect = e.currentTarget.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const height = rect.height;
    let position = "middle";
    if (y < height * 0.25) {
      position = "top";
    } else if (y > height * 0.75) {
      position = "bottom";
    }
    e.dataTransfer.dropEffect = "move";
    setDragOverPath(item.path);
    setDragOverPosition(position);
  }, []);
  const handleDragLeave = useCallback((e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragOverPath(null);
    setDragOverPosition(null);
  }, []);
  const handleDrop = useCallback(
    async (e, targetItem) => {
      e.preventDefault();
      e.stopPropagation();
      setDragOverPath(null);
      const position = dragOverPosition;
      setDragOverPosition(null);
      let targetFolderPath2 = targetItem.path;
      if (targetItem.type !== "directory" || position === "top" || position === "bottom") {
        targetFolderPath2 = targetItem.path.split("/").slice(0, -1).join("/") || ".";
      }
      if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
        await uploadFiles(e.dataTransfer.files, targetFolderPath2);
        return;
      }
      const sourcePathsStr = e.dataTransfer.getData("itemPaths");
      if (!sourcePathsStr || sourcePathsStr === "") return;
      const sourcePaths = JSON.parse(sourcePathsStr);
      const performMove = async () => {
        try {
          let updatedHash = false;
          for (const sourcePath of sourcePaths) {
            if (sourcePath === targetFolderPath2) continue;
            const sourceParent = sourcePath.split("/").slice(0, -1).join("/") || ".";
            if (sourceParent === targetFolderPath2) continue;
            await axios.post("/api/file/move", {
              old_path: sourcePath,
              new_path: targetFolderPath2
            });
            if (!updatedHash && currentFilePath && (currentFilePath === sourcePath || currentFilePath.startsWith(sourcePath + "/"))) {
              const filename = sourcePath.split("/").pop();
              const newPathForCurrentFile = currentFilePath.replace(
                sourcePath,
                targetFolderPath2 === "." || targetFolderPath2 === "" ? filename : `${targetFolderPath2}/${filename}`
              );
              window.location.hash = getFileUrl(newPathForCurrentFile);
              updatedHash = true;
            }
          }
          fetchFiles();
        } catch (error) {
          console.error("Error moving files:", error);
          window.toastr?.error(`Error moving files: ${getErrorMessage(error)}`);
        }
      };
      const involvesCurrentFile = sourcePaths.some(
        (sp) => currentFilePath && (currentFilePath === sp || currentFilePath.startsWith(sp + "/"))
      );
      if (involvesCurrentFile) {
        const event = new CustomEvent("file-move-check", {
          detail: {
            path: sourcePaths.find((sp) => currentFilePath === sp || currentFilePath.startsWith(sp + "/")),
            callback: performMove
          }
        });
        window.dispatchEvent(event);
      } else {
        performMove();
      }
    },
    [fetchFiles, currentFilePath, getFileUrl, dragOverPosition]
  );
  const handleRenameCancel = useCallback(() => {
    setRenamingState(null);
    setDuplicationSourcePath(null);
    if (explorerRef.current) {
      explorerRef.current.focus();
    }
  }, [setDuplicationSourcePath]);
  const handleRenameConfirm = useCallback(async () => {
    const item = findItemByPath(rawFiles, renamingPath);
    const finalNewNameValue = tempRenameValue;
    if (!item || !finalNewNameValue.trim()) {
      handleRenameCancel();
      return;
    }
    const oldPath = item.path;
    let finalNewName = finalNewNameValue.trim().replace(/[^a-zA-Z0-9\-\.]+/g, "_").replace(/^_+|_+$/g, "");
    if (item.type === "file") {
      const originalExt = ALL_EDITABLE_EXTENSIONS.find(
        (ext) => oldPath.toLowerCase().endsWith(ext.toLowerCase())
      );
      if (originalExt && !ALL_EDITABLE_EXTENSIONS.some(
        (ext) => finalNewName.toLowerCase().endsWith(ext.toLowerCase())
      )) {
        finalNewName += originalExt;
      }
    }
    const parts = oldPath.split("/");
    parts[parts.length - 1] = finalNewName;
    const newPath = parts.join("/");
    if (newPath === oldPath) {
      handleRenameCancel();
      return;
    }
    if (duplicationSourcePath && newPath === duplicationSourcePath) {
      return;
    }
    const performRename = async () => {
      try {
        await axios.post("/api/file/move", {
          old_path: oldPath,
          new_path: newPath
        });
        setRenamingState(null);
        setDuplicationSourcePath(null);
        if (searchQuery) {
          setForceVisiblePaths((prev) => new Set(prev).add(newPath));
        }
        setRawFiles((prev) => {
          if (!prev) return prev;
          const parentPath = oldPath.split("/").slice(0, -1).join("/") || ".";
          const parentNode = findItemByPath(prev, parentPath);
          if (parentNode && parentNode.children) {
            const oldName = oldPath.split("/").pop();
            const newName = newPath.split("/").pop();
            const oldGenGb = oldName.toLowerCase().endsWith(".assembly") ? oldName.slice(0, -".assembly".length) + ".gen.gb" : null;
            const newGenGb = newName.toLowerCase().endsWith(".assembly") ? newName.slice(0, -".assembly".length) + ".gen.gb" : null;
            const currentOrder = parentNode.children.map((c) => {
              if (c.name === oldName) return newName;
              if (oldGenGb && newGenGb && c.name === oldGenGb) return newGenGb;
              return c.name;
            });
            customSortRef.current[parentPath] = currentOrder;
          }
          let patched = applyRenameInTree(prev, oldPath, newPath);
          if (oldPath.toLowerCase().endsWith(".assembly")) {
            const oldGenGbPath = oldPath.slice(0, -".assembly".length) + ".gen.gb";
            const newGenGbPath = newPath.slice(0, -".assembly".length) + ".gen.gb";
            patched = applyRenameInTree(patched, oldGenGbPath, newGenGbPath);
          }
          return patched;
        });
        setGitStatus((prev) => {
          if (!prev) return prev;
          const next = {};
          for (const [k, v] of Object.entries(prev)) {
            const newKey = k === oldPath || k.startsWith(oldPath + "/") ? k.replace(oldPath, newPath) : k;
            next[newKey] = v;
          }
          return next;
        });
        setSelectedPaths(
          (prev) => prev.map(
            (p) => p === oldPath || p.startsWith(oldPath + "/") ? p.replace(oldPath, newPath) : p
          )
        );
        setLastClickedPath(
          (prev) => prev && (prev === oldPath || prev.startsWith(oldPath + "/")) ? prev.replace(oldPath, newPath) : prev
        );
        if (currentFilePath && (currentFilePath === oldPath || currentFilePath.startsWith(oldPath + "/"))) {
          const newPathForCurrentFile = currentFilePath.replace(
            oldPath,
            newPath
          );
          window.location.hash = getFileUrl(newPathForCurrentFile);
        }
      } catch (error) {
        console.error("Error renaming item:", error);
        window.toastr?.error(`Error renaming item: ${getErrorMessage(error)}`);
      }
    };
    const involvesCurrentFile = currentFilePath && (currentFilePath === oldPath || currentFilePath.startsWith(oldPath + "/"));
    if (involvesCurrentFile) {
      const event = new CustomEvent("file-move-check", {
        detail: {
          path: oldPath,
          callback: performRename
        }
      });
      window.dispatchEvent(event);
    } else {
      await performRename();
    }
  }, [
    rawFiles,
    renamingPath,
    tempRenameValue,
    currentFilePath,
    getFileUrl,
    setRenamingPath,
    setTempRenameValue,
    setRawFiles,
    setGitStatus,
    setSelectedPaths,
    setLastClickedPath,
    duplicationSourcePath,
    handleRenameCancel,
    searchQuery,
    setForceVisiblePaths
  ]);
  const uploadFiles = useCallback(async (files, targetFolderPath2) => {
    const formData = new FormData();
    Array.from(files).forEach((file) => {
      formData.append("file", file);
    });
    formData.append("targetPath", targetFolderPath2);
    try {
      const response = await axios.post("/api/file/upload", formData, {
        headers: { "Content-Type": "multipart/form-data" }
      });
      if (response.data.success && response.data.saved_paths && response.data.saved_paths.length > 0) {
        fetchFiles();
        window.location.hash = getFileUrl(response.data.saved_paths[0]);
        return response.data.saved_paths;
      }
      return [];
    } catch (error) {
      console.error("Error uploading files:", error);
      window.toastr?.error(`Error uploading files: ${getErrorMessage(error)}`);
      throw error;
    }
  }, [fetchFiles, getFileUrl]);
  const handlePasteFromClipboard = useCallback(async (e) => {
    if (e.clipboardData?.files && e.clipboardData.files.length > 0) {
      e.preventDefault();
      e.stopPropagation();
      let targetFolderPath2 = ".";
      if (lastClickedPath && rawFiles) {
        const selectedItem = findItemByPath(rawFiles, lastClickedPath);
        if (selectedItem) {
          targetFolderPath2 = selectedItem.type === "directory" ? selectedItem.path : selectedItem.path.split("/").slice(0, -1).join("/") || ".";
        }
      }
      await uploadFiles(e.clipboardData.files, targetFolderPath2);
      return;
    }
    let jsonData = e.clipboardData?.getData("application/json");
    if (jsonData) {
      try {
        const parsed = JSON.parse(jsonData);
        if (parsed && parsed.type === "dtx_file_clipboard" && Array.isArray(parsed.copiedItems) && parsed.copiedItems.length > 0) {
          e.preventDefault();
          e.stopPropagation();
          setFileClipboard(parsed.copiedItems, !!parsed.isCut);
          let targetFolderPath2 = ".";
          if (lastClickedPath && rawFiles) {
            const selectedItem = findItemByPath(rawFiles, lastClickedPath);
            if (selectedItem) {
              targetFolderPath2 = selectedItem.type === "directory" ? selectedItem.path : selectedItem.path.split("/").slice(0, -1).join("/") || ".";
            }
          }
          await handlePaste({ path: targetFolderPath2 });
          return;
        }
      } catch (err) {
      }
    }
  }, [lastClickedPath, rawFiles, uploadFiles, handlePaste]);
  useEffect(() => {
    const handleRemotePaste = (e) => {
      const { targetFolder } = e.detail;
      const { copiedItems: currentCopiedItems } = getFileClipboard();
      const itemsToPaste = currentCopiedItems && currentCopiedItems.length > 0 ? currentCopiedItems : copiedItems;
      if (itemsToPaste && itemsToPaste.length > 0) {
        handlePaste({ path: targetFolder || "." });
      }
    };
    const handleRemoteDelete = (e) => {
      const { items } = e.detail;
      if (items && items.length > 0) {
        handleDeleteItems(items);
      }
    };
    const handleRemoteCopy = (e) => {
      const { items, cut } = e.detail;
      if (items && items.length > 0) {
        setFileClipboard(items, !!cut);
        const message = items.length === 1 ? `${cut ? "Cut" : "Copied"} ${items[0].name}` : `${cut ? "Cut" : "Copied"} ${items.length} items`;
        window.toastr?.success(message);
      }
    };
    const handleRemoteRenameConfirm = () => {
      handleRenameConfirm();
    };
    const handleRemoteRenameCancel = () => {
      handleRenameCancel();
    };
    const handleRemoteRenameChange = (e) => {
      const { renamingPath: currentPath, renamingSource: currentSource } = getFileClipboard();
      if (currentPath) {
        setRenamingState(currentPath, e.detail.value, currentSource);
      }
    };
    window.addEventListener("file-operation-paste", handleRemotePaste);
    window.addEventListener("file-operation-delete", handleRemoteDelete);
    window.addEventListener("file-operation-copy", handleRemoteCopy);
    window.addEventListener("file-operation-rename-confirm", handleRemoteRenameConfirm);
    window.addEventListener("file-operation-rename-cancel", handleRemoteRenameCancel);
    window.addEventListener("file-operation-rename-change", handleRemoteRenameChange);
    return () => {
      window.removeEventListener("file-operation-paste", handleRemotePaste);
      window.removeEventListener("file-operation-delete", handleRemoteDelete);
      window.removeEventListener("file-operation-copy", handleRemoteCopy);
      window.removeEventListener("file-operation-rename-confirm", handleRemoteRenameConfirm);
      window.removeEventListener("file-operation-rename-cancel", handleRemoteRenameCancel);
      window.removeEventListener("file-operation-rename-change", handleRemoteRenameChange);
    };
  }, [copiedItems, handlePaste, handleDeleteItems, handleRenameConfirm, handleRenameCancel, renamingPath]);
  const handleDescriptionCancel = useCallback(() => {
    setEditingDescriptionPath(null);
    setTempDescriptionValue("");
    if (explorerRef.current) {
      explorerRef.current.focus();
    }
  }, []);
  const handleRenameChange = useCallback((value) => {
    setTempRenameValue(value);
    const { renamingPath: currentPath, renamingSource: currentSource } = getFileClipboard();
    if (currentPath) {
      setRenamingState(currentPath, value, currentSource);
    }
  }, []);
  const onStartRename = useCallback((item) => {
    setRenamingState(item.path, item.name, "explorer");
  }, []);
  const onStartEditDescription = useCallback(
    (item) => {
      setEditingDescriptionPath(item.path);
      const description = optimisticDescriptions[item.path] !== void 0 ? optimisticDescriptions[item.path] : item.description;
      setTempDescriptionValue(description || "");
    },
    [optimisticDescriptions]
  );
  const handleSaveDescription = useCallback(
    async (options = {}) => {
      const targetPath = options.path || rawNodeData?.path;
      const descToSave = options.description !== void 0 ? options.description : descriptionRef.current;
      const locusToSave = options.locus !== void 0 ? options.locus : locusRef.current;
      if (!targetPath) return;
      setOptimisticDescriptions((prev) => ({
        ...prev,
        [targetPath]: descToSave
      }));
      setEditingDescriptionPath(null);
      setIsPathDialogOpen(false);
      window.dispatchEvent(
        new CustomEvent("file-saving", { detail: { path: targetPath } })
      );
      setIsSavingDescription(true);
      try {
        const encodedPath = targetPath.split("/").map((segment) => encodeURIComponent(segment)).join("/");
        const response = await axios.post(
          `/api/file/description/${encodedPath}`,
          {
            description: descToSave,
            locus: locusToSave
          }
        );
        window.toastr?.success("Description saved successfully!");
        if (response.data.build_output) {
          console.log("[BUILD OUTPUT]", response.data.build_output);
        }
        await fetchFiles();
        setOptimisticDescriptions((prev) => {
          const next = { ...prev };
          delete next[targetPath];
          return next;
        });
        window.dispatchEvent(
          new CustomEvent("file-saved", {
            detail: {
              path: targetPath,
              last_modified: response.data.last_modified,
              description: descToSave
            }
          })
        );
        setTempDescriptionValue("");
      } catch (error) {
        console.error("Error saving description:", error);
        window.toastr?.error("Failed to save description: " + error.message);
        window.dispatchEvent(
          new CustomEvent("file-save-failed", { detail: { path: targetPath } })
        );
        setOptimisticDescriptions((prev) => {
          const next = { ...prev };
          delete next[targetPath];
          return next;
        });
      } finally {
        setIsSavingDescription(false);
      }
    },
    [rawNodeData, fetchFiles]
  );
  const handleToggleInventoryMulti = useCallback(async (itemsToToggle) => {
    if (itemsToToggle.length === 0) return;
    const allInInventory = itemsToToggle.every(
      (i) => optimisticInventory[i.path] !== void 0 ? optimisticInventory[i.path] : i.inInventory
    );
    const newStatus = !allInInventory;
    const performToggle = async () => {
      window.isInventoryToggling = true;
      setOptimisticInventory((prev) => {
        const next = { ...prev };
        itemsToToggle.forEach((i) => {
          next[i.path] = newStatus;
        });
        return next;
      });
      const currentToggledItem = itemsToToggle.find((i) => i.path === currentFilePath);
      if (currentToggledItem) {
        window.dispatchEvent(
          new CustomEvent("file-inventory-toggling", {
            detail: {
              path: currentFilePath,
              in_inventory: newStatus
            }
          })
        );
      }
      try {
        const promises = itemsToToggle.map(async (item) => {
          const encodedPath = item.path.split("/").map((segment) => encodeURIComponent(segment)).join("/");
          const response = await axios.post(`/api/file/inventory/${encodedPath}`, {
            in_inventory: newStatus
          });
          return { item, response };
        });
        const results = await Promise.all(promises);
        const successes = results.filter((r) => r.response.data.success);
        const failures = results.filter((r) => !r.response.data.success);
        if (successes.length > 0) {
          window.toastr?.success(
            `Successfully ${newStatus ? "marked" : "removed"} ${successes.length} item(s) ${newStatus ? "in" : "from"} inventory.`
          );
          if (currentFilePath) {
            const buildOutputObj = results.find((r) => r.response.data.build_output || r.response.data.alignment_output);
            window.dispatchEvent(
              new CustomEvent("file-reverted", {
                detail: {
                  path: currentFilePath,
                  build_output: buildOutputObj?.response.data.build_output,
                  alignment_output: buildOutputObj?.response.data.alignment_output,
                  squelchPopup: true
                }
              })
            );
          }
        }
        if (failures.length > 0) {
          failures.forEach((f) => {
            window.toastr?.error(`Failed to update ${f.item.name}: ${f.response.data.error || "Unknown error"}`);
          });
        }
      } catch (error) {
        console.error("Error updating inventory:", error);
        window.toastr?.error(error.response?.data?.error || error.message || "Failed to update inventory status");
        if (currentFilePath) {
          window.dispatchEvent(
            new CustomEvent("file-save-failed", { detail: { path: currentFilePath } })
          );
        }
      } finally {
        await fetchFiles();
        setOptimisticInventory((prev) => {
          const next = { ...prev };
          itemsToToggle.forEach((i) => {
            delete next[i.path];
          });
          return next;
        });
        window.isInventoryToggling = false;
      }
    };
    const currentIsToggled = currentFilePath && itemsToToggle.some((i) => i.path === currentFilePath);
    if (currentIsToggled) {
      const event = new CustomEvent("file-move-check", {
        detail: {
          path: currentFilePath,
          callback: performToggle
        }
      });
      window.dispatchEvent(event);
    } else {
      performToggle();
    }
  }, [fetchFiles, currentFilePath, optimisticInventory]);
  const handleToggleInventory = useCallback(async (item) => {
    await handleToggleInventoryMulti([item]);
  }, [handleToggleInventoryMulti]);
  const processedFiles = useMemo(() => {
    if (!rawFiles) return null;
    let pf = rawFiles;
    let targetUnsaved = unsavedNewFile;
    const hash = window.location.hash;
    if (hash.includes("new=true") && hash.includes("file=")) {
      const urlParams = new URLSearchParams(hash.split("?")[1] || "");
      const newFilePath = urlParams.get("file");
      const type = urlParams.get("type");
      const circular = urlParams.get("circular") === "true";
      if (newFilePath) {
        targetUnsaved = { path: newFilePath, type, circular };
      }
    }
    if (targetUnsaved && targetUnsaved.path) {
      const newFilePath = targetUnsaved.path;
      const type = targetUnsaved.type;
      const circular = targetUnsaved.circular;
      pf = JSON.parse(JSON.stringify(rawFiles));
      const inject = (root, path) => {
        const cleanPath = path.replace(/^projects\//, "");
        const segments = cleanPath.split("/");
        const filename = segments.pop();
        const parentPath = segments.join("/");
        const findAndAdd = (curr) => {
          const currCleanPath = curr.path ? curr.path.replace(/^projects\//, "") : "";
          if (currCleanPath === parentPath || parentPath === "" && (curr.path === "." || curr.path === "projects" || curr.path === "")) {
            if (!curr.children) curr.children = [];
            if (!curr.children.some((c) => c.path === cleanPath || c.path === path)) {
              curr.children.push({
                name: filename,
                type: "file",
                path: cleanPath,
                isNewUnsaved: true,
                circular: type === "sequence" ? circular : void 0
              });
              curr.children.sort((a, b) => {
                const aIsDir = a.type === "directory" || a.isDir;
                const bIsDir = b.type === "directory" || b.isDir;
                if (aIsDir !== bIsDir) return aIsDir ? -1 : 1;
                return a.name.localeCompare(b.name);
              });
            }
            return true;
          }
          if (curr.children) {
            for (const child of curr.children) {
              if (findAndAdd(child)) return true;
            }
          }
          return false;
        };
        if (!findAndAdd(root) && (parentPath === "" || parentPath === "." || parentPath === "projects")) {
          if (!root.children) root.children = [];
          if (!root.children.some((c) => c.path === cleanPath || c.path === path)) {
            root.children.push({
              name: filename,
              type: "file",
              path: cleanPath,
              isNewUnsaved: true,
              circular: type === "sequence" ? circular : void 0
            });
            root.children.sort((a, b) => {
              const aIsDir = a.type === "directory" || a.isDir;
              const bIsDir = b.type === "directory" || b.isDir;
              if (aIsDir !== bIsDir) return aIsDir ? -1 : 1;
              return a.name.localeCompare(b.name);
            });
          }
        }
      };
      inject(pf, newFilePath);
    }
    return pf;
  }, [rawFiles, currentFilePath, unsavedNewFile]);
  useEffect(() => {
    if (processedFiles) {
      window.dispatchEvent(
        new CustomEvent("file-tree-updated", {
          detail: { files: processedFiles, git_status: gitStatus }
        })
      );
    }
  }, [processedFiles, gitStatus]);
  const lastExpansionQueryRef = useRef("");
  useEffect(() => {
    if (debouncedQuery === "" && lastExpansionQueryRef.current !== "") {
      setExpandedIds(getParentsOfPath(currentFilePath));
      lastExpansionQueryRef.current = "";
    } else if (debouncedQuery && processedFiles && processedFiles.children && debouncedQuery !== lastExpansionQueryRef.current) {
      const filteredChildren = filterNodes(
        processedFiles.children,
        debouncedQuery,
        false,
        sequenceSearchResults
      );
      const newExpandedIds = {};
      const expandMatching = (items, prefix = "") => {
        if (!Array.isArray(items)) return;
        items.forEach((item) => {
          const id = prefix + item.name;
          if (item.type === "directory") {
            if (item.isDirectMatch || item.hasMatchInChildren) {
              newExpandedIds[id] = true;
              expandMatching(item.children, id + "/");
            }
          }
        });
      };
      expandMatching(filteredChildren, "");
      setExpandedIds((prev) => ({ ...prev, ...newExpandedIds }));
      lastExpansionQueryRef.current = debouncedQuery;
    }
  }, [debouncedQuery, processedFiles, sequenceSearchResults, currentFilePath]);
  const nodes = useMemo(() => {
    if (!processedFiles) return [];
    let filteredRoot = processedFiles;
    if (pinnedFolder && processedFiles) {
      if (pinnedFolder === "." || pinnedFolder === "projects") {
        filteredRoot = processedFiles;
      } else {
        const pinnedNode = findItemByPath(processedFiles, pinnedFolder);
        if (pinnedNode) {
          filteredRoot = pinnedNode;
        }
      }
    }
    const queryToUse = searchQuery === "" ? "" : debouncedQuery;
    if (queryToUse && filteredRoot.children) {
      const filteredChildren = filterNodes(
        filteredRoot.children,
        debouncedQuery,
        false,
        sequenceSearchResults,
        false,
        forceVisiblePaths
      );
      filteredRoot = {
        ...filteredRoot,
        children: filteredChildren
      };
    }
    const children = filteredRoot.children || [];
    const idPrefix = pinnedFolder && pinnedFolder !== "." && pinnedFolder !== "projects" ? pinnedFolder + "/" : "";
    return children.map(
      (child) => transformToTreeNodes(
        child,
        selectedPaths,
        expandedIds,
        idPrefix,
        handleDragStart,
        handleDragOver,
        handleDrop,
        handleDragLeave,
        dragOverPath,
        dragOverPosition,
        gitStatus,
        lastClickedPath,
        renamingPath,
        tempRenameValue,
        handleRenameChange,
        handleRenameConfirm,
        handleRenameCancel,
        editingDescriptionPath,
        tempDescriptionValue,
        setTempDescriptionValue,
        () => handleSaveDescription({
          path: editingDescriptionPath,
          description: tempDescriptionValue
        }),
        handleDescriptionCancel,
        onStartRename,
        onStartEditDescription,
        optimisticDescriptions,
        copiedItems,
        isCut,
        renamingSource,
        optimisticInventory,
        optimisticHasComments,
        loadingFolders
      )
    );
  }, [
    processedFiles,
    selectedPaths,
    expandedIds,
    debouncedQuery,
    handleDragStart,
    handleDragOver,
    handleDrop,
    handleDragLeave,
    dragOverPath,
    dragOverPosition,
    gitStatus,
    lastClickedPath,
    renamingPath,
    tempRenameValue,
    handleRenameConfirm,
    handleRenameCancel,
    editingDescriptionPath,
    tempDescriptionValue,
    handleSaveDescription,
    handleDescriptionCancel,
    onStartRename,
    onStartEditDescription,
    optimisticDescriptions,
    optimisticInventory,
    optimisticHasComments,
    copiedItems,
    pinnedFolder,
    isCut,
    loadingFolders,
    sequenceSearchResults,
    searchQuery,
    forceVisiblePaths
  ]);
  const flattenedNodes = useMemo(() => {
    return flattenTree(nodes);
  }, [nodes]);
  const fileInfoMap = useMemo(() => {
    const map = {};
    const traverse = (node) => {
      if (node.path) {
        map[node.path] = node;
      }
      if (node.children) {
        node.children.forEach(traverse);
      }
    };
    if (rawFiles) traverse(rawFiles);
    return map;
  }, [rawFiles]);
  useEffect(() => {
    if (currentFilePath && reactListRef.current && flattenedNodes.length > 0) {
      if (currentFilePath === lastScrolledPathRef.current) {
        return;
      }
      const attemptScroll = (retryCount = 0) => {
        const list = reactListRef.current;
        if (!list) return;
        const index = flattenedNodes.findIndex((n) => n.nodeData.path === currentFilePath);
        if (index !== -1) {
          const [first, last] = list.getVisibleRange();
          if (index < first || index > last) {
            list.scrollTo(index);
          }
          if (retryCount === 0) {
            setTimeout(() => attemptScroll(1), 100);
          }
          lastScrolledPathRef.current = currentFilePath;
        } else if (retryCount < 8) {
          setTimeout(() => attemptScroll(retryCount + 1), (retryCount + 1) * 200);
        }
      };
      const timer = setTimeout(() => attemptScroll(0), 400);
      return () => clearTimeout(timer);
    }
  }, [currentFilePath, flattenedNodes]);
  const allVisiblePaths = useMemo(() => {
    const paths = [];
    const flatten = (items) => {
      items.forEach((item) => {
        paths.push(item.nodeData.path);
        if (item.isExpanded && item.childNodes) {
          flatten(item.childNodes);
        }
      });
    };
    flatten(nodes);
    return paths;
  }, [nodes]);
  const handleNodeClick = useCallback(
    (node, path, e = {}, options = {}) => {
      explorerRef.current?.focus();
      if (pendingNavigationRef.current) {
        clearTimeout(pendingNavigationRef.current);
        pendingNavigationRef.current = null;
      }
      const isMultiSelect = e.ctrlKey || e.metaKey;
      const isRangeSelect = e.shiftKey;
      const path_str = node?.nodeData?.path;
      if (!path_str) return;
      if (isRangeSelect && lastClickedPath) {
        const startIndex = allVisiblePaths.indexOf(lastClickedPath);
        const endIndex = allVisiblePaths.indexOf(path_str);
        if (startIndex !== -1 && endIndex !== -1) {
          const start = Math.min(startIndex, endIndex);
          const end = Math.max(startIndex, endIndex);
          const rangePaths = allVisiblePaths.slice(start, end + 1);
          setSelectedPaths((prev) => {
            const newSet = /* @__PURE__ */ new Set([...prev, ...rangePaths]);
            return Array.from(newSet);
          });
          return;
        }
      }
      setLastClickedPath(path_str);
      if (isMultiSelect) {
        setSelectedPaths((prev) => {
          if (prev.includes(path_str)) {
            return prev.filter((p) => p !== path_str);
          } else {
            return [...prev, path_str];
          }
        });
        return;
      }
      setSelectedPaths([path_str]);
      const triggerNavigation = () => {
        if (!node.hasCaret) {
          if (node.nodeData.type === "file" && ALL_EDITABLE_EXTENSIONS.some((ext) => node.nodeData.name.endsWith(ext))) {
            window.location.hash = getFileUrl(node.nodeData.path);
          }
        } else {
          window.location.hash = getFileUrl(node.nodeData.path, { extraParams: { view: "table" } });
        }
      };
      if (options.immediate) {
        triggerNavigation();
      } else {
        pendingNavigationRef.current = setTimeout(() => {
          triggerNavigation();
          pendingNavigationRef.current = null;
        }, debounceDelay);
      }
    },
    [allVisiblePaths, lastClickedPath, debounceDelay]
  );
  const handleNodeExpand = useCallback(async (node) => {
    const isCurrentlyExpanded = expandedIds[node.id] || node.nodeData && expandedIds[node.nodeData.path] || false;
    const willExpand = !isCurrentlyExpanded;
    setExpandedIds((prev) => ({
      ...prev,
      [node.id]: willExpand,
      ...node.nodeData?.path ? { [node.nodeData.path]: willExpand } : {}
    }));
    const item = node.nodeData;
    if (willExpand && item && item.type === "directory" && item.isLoaded === false) {
      try {
        setLoadingFolders((prev) => ({ ...prev, [item.path]: true }));
        const res = await axios.get(
          `/api/files?path=${encodeURIComponent(item.path)}&depth=1&t=${Date.now()}`
        );
        if (res.data && res.data.files && res.data.files.children) {
          const loadedChildren = res.data.files.children;
          setRawFiles((prev) => {
            if (!prev) return prev;
            const updateChildren = (n) => {
              if (!n) return n;
              if (n.path === item.path) {
                return {
                  ...n,
                  children: loadedChildren,
                  isLoaded: true,
                  hasChildren: loadedChildren.length > 0
                };
              }
              if (n.children) {
                return {
                  ...n,
                  children: n.children.map(updateChildren)
                };
              }
              return n;
            };
            return applyCustomSort(
              Array.isArray(prev) ? prev.map(updateChildren) : updateChildren(prev)
            );
          });
        }
      } catch (err) {
        console.error("Error loading folder contents:", err);
      } finally {
        setLoadingFolders((prev) => {
          const next = { ...prev };
          delete next[item.path];
          return next;
        });
      }
    }
  }, [expandedIds, applyCustomSort]);
  const handleKeyDown = useCallback(
    (e) => {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp" && e.key !== "ArrowRight" && e.key !== "ArrowLeft" && e.key !== "Enter")
        return;
      if (e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") {
        if (e.key !== "ArrowDown") return;
      }
      e.preventDefault();
      const currentIndex = flattenedNodes.findIndex(
        (n) => n.nodeData.path === lastClickedPath
      );
      let nextIndex = -1;
      if (e.key === "ArrowDown") {
        nextIndex = currentIndex === -1 ? 0 : Math.min(flattenedNodes.length - 1, currentIndex + 1);
      } else if (e.key === "ArrowUp") {
        nextIndex = currentIndex === -1 ? flattenedNodes.length - 1 : Math.max(0, currentIndex - 1);
      } else if (e.key === "ArrowRight") {
        const node = flattenedNodes[currentIndex];
        if (node && node.hasCaret && !node.isExpanded) {
          handleNodeExpand(node);
          return;
        } else {
          nextIndex = Math.min(flattenedNodes.length - 1, currentIndex + 1);
        }
      } else if (e.key === "ArrowLeft") {
        const node = flattenedNodes[currentIndex];
        if (node && node.hasCaret && node.isExpanded) {
          handleNodeExpand(node);
          return;
        } else if (node) {
          const parts = node.nodeData.path.split("/");
          if (parts.length > 1) {
            parts.pop();
            const parentPath = parts.join("/");
            const parentNode = flattenedNodes.find((n) => n.nodeData.path === parentPath);
            if (parentNode) {
              if (parentNode.isExpanded) {
                handleNodeExpand(parentNode);
              }
              handleNodeClick(parentNode, [], {}, { immediate: true });
              return;
            }
          }
          nextIndex = Math.max(0, currentIndex - 1);
        } else {
          nextIndex = Math.max(0, currentIndex - 1);
        }
      } else if (e.key === "Enter") {
        const node = flattenedNodes[currentIndex];
        if (node) {
          if (node.hasCaret) {
            handleNodeExpand(node);
          } else {
            handleNodeClick(node, [], {}, { immediate: true });
          }
        }
        return;
      }
      if (nextIndex !== -1 && flattenedNodes[nextIndex]) {
        handleNodeClick(flattenedNodes[nextIndex], [], {});
      }
    },
    [flattenedNodes, lastClickedPath, handleNodeClick, handleNodeExpand]
  );
  const handleNodeDoubleClick = (node, e) => {
    if (pendingNavigationRef.current) {
      clearTimeout(pendingNavigationRef.current);
      pendingNavigationRef.current = null;
    }
    if (node.hasCaret) {
      handleNodeExpand(node);
    } else {
      handleNodeClick(node, [], {}, { immediate: true });
    }
  };
  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return;
    try {
      const folderName = newFolderName.trim().replace(/[^a-zA-Z0-9\-\.]+/g, "_").replace(/^_+|_+$/g, "");
      const path = `${targetFolderPath}/${folderName}`;
      await axios.post("/api/folder", { path });
      setIsFolderDialogOpen(false);
      setNewFolderName("");
      if (searchQuery) {
        setForceVisiblePaths((prev) => new Set(prev).add(path));
      }
      fetchFiles();
      if (targetFolderPath && targetFolderPath !== "." && targetFolderPath !== "") {
        setExpandedIds((prev) => ({
          ...prev,
          [targetFolderPath.split("/").pop()]: true
        }));
      }
    } catch (error) {
      console.error("Error creating folder:", error);
      window.toastr?.error(`Error creating folder: ${getErrorMessage(error)}`);
    }
  };
  const handleCreateFile = async () => {
    if (!newFileName.trim()) return;
    setIsFileCreating(true);
    try {
      let fileName = newFileName.trim().replace(/[^a-zA-Z0-9\-\.]+/g, "_").replace(/^_+|_+$/g, "");
      const extension = newFileType === "sequence" ? ".gb" : ".assembly";
      if (!fileName.endsWith(extension)) {
        fileName += extension;
      }
      const path = `${targetFolderPath}/${fileName}`;
      let content;
      if (newFileType === "sequence") {
        content = {
          name: fileName.replace(extension, ""),
          sequence: "",
          circular: newFileCircular,
          features: []
        };
      } else {
        if (assemblyComponents && assemblyComponents.length > 0) {
          content = "method: restriction\nsequence_components:\n";
          assemblyComponents.forEach((comp) => {
            content += `  - ${comp}
`;
          });
        } else {
          content = "method: restriction\nsequence_components: []\n";
        }
      }
      setIsCreateFileDialogOpen(false);
      setNewFileName("");
      setAssemblyComponents([]);
      let newFileUrl = `#/Standalone?file=${encodeURIComponent(path)}&new=true&type=${newFileType}`;
      if (newFileType === "sequence") {
        newFileUrl += `&circular=${newFileCircular}`;
      } else if (newFileType === "assembly" && assemblyComponents && assemblyComponents.length > 0) {
        newFileUrl += `&components=${encodeURIComponent(JSON.stringify(assemblyComponents))}`;
      }
      window.location.hash = newFileUrl;
    } catch (error) {
      console.error("Error creating file:", error);
      window.toastr?.error(`Error creating file: ${getErrorMessage(error)}`);
    } finally {
      setIsFileCreating(false);
    }
  };
  const handleImportSequence = useCallback(
    async (folderPath) => {
      showFileDialog({
        multiple: true,
        onSelect: async (files) => {
          setIsImporting(true);
          try {
            const savedPaths = await uploadFiles(files, folderPath);
            const msg = savedPaths && savedPaths.length === 1 ? `Imported ${savedPaths[0].split("/").pop()}` : "Import complete";
            window.toastr?.success(msg, {
              action: savedPaths && savedPaths.length > 0 ? {
                text: "Open",
                onClick: () => {
                  window.location.hash = getFileUrl(savedPaths[0]);
                }
              } : void 0
            });
          } catch (e) {
            console.error("Error importing files:", e);
            window.toastr?.error(`Error importing files: ${e.message}`);
          } finally {
            setIsImporting(false);
          }
        }
      });
    },
    [uploadFiles]
  );
  const handleImportFromAddgene = useCallback((folderPath) => {
    setTargetFolderPath(folderPath || ".");
    setAddgeneId("");
    setIsAddgeneDialogOpen(true);
  }, []);
  const executeAddgeneImport = useCallback(async () => {
    if (!addgeneId.trim()) return;
    setIsAddgeneImporting(true);
    try {
      const response = await axios.post("/api/import/addgene", {
        addgene_id: addgeneId.trim(),
        targetPath: targetFolderPath
      });
      if (response.data.success) {
        const filePath = `${targetFolderPath}/${response.data.filename}`;
        window.toastr?.success(`Imported ${response.data.filename}`, {
          action: {
            text: "Open",
            onClick: () => {
              window.location.hash = getFileUrl(filePath);
            }
          }
        });
        window.location.hash = getFileUrl(filePath);
        setIsAddgeneDialogOpen(false);
        fetchFiles();
      } else {
        throw new Error(response.data.error || "Import failed");
      }
    } catch (e) {
      console.error("Error importing from Addgene:", e);
      window.toastr?.error(`Error importing from Addgene: ${getErrorMessage(e)}`);
    } finally {
      setIsAddgeneImporting(false);
    }
  }, [addgeneId, targetFolderPath, fetchFiles]);
  const handleCreateNewItem = async () => {
    if (!newItemName.trim()) return;
    setIsNewItemCreating(true);
    try {
      const rawName = newItemName.trim().replace(/[^a-zA-Z0-9\-\.]+/g, "_").replace(/^_+|_+$/g, "");
      const folder = newItemTargetFolder && newItemTargetFolder !== "." ? newItemTargetFolder : "";
      if (newItemType === "folder") {
        const path = folder ? `${folder}/${rawName}` : rawName;
        await axios.post("/api/folder", { path });
        setIsNewItemDialogOpen(false);
        setNewItemName("");
        if (searchQuery) {
          setForceVisiblePaths((prev) => new Set(prev).add(path));
        }
        fetchFiles();
        if (folder && folder !== "") {
          setExpandedIds((prev) => ({
            ...prev,
            [folder.split("/").pop()]: true
          }));
        }
      } else {
        const extension = newItemType === "sequence" ? ".gb" : ".assembly";
        let fileName = rawName;
        if (!fileName.endsWith(extension)) {
          fileName += extension;
        }
        const path = folder ? `${folder}/${fileName}` : fileName;
        setIsNewItemDialogOpen(false);
        setNewItemName("");
        let newFileUrl = `#/Standalone?file=${encodeURIComponent(
          path
        )}&new=true&type=${newItemType}`;
        if (newItemType === "sequence") {
          newFileUrl += `&circular=${newItemCircular}`;
        } else if (newItemType === "assembly") {
          newFileUrl += `&method=${encodeURIComponent(newItemAssemblyMethod)}`;
        }
        window.location.hash = newFileUrl;
      }
    } catch (error) {
      console.error("Error creating item:", error);
      window.toastr?.error(`Error creating item: ${getErrorMessage(error)}`);
    } finally {
      setIsNewItemCreating(false);
    }
  };
  useEffect(() => {
    const handleOpen = () => {
      openNewItemDialog();
    };
    window.addEventListener("open-new-item-dialog", handleOpen);
    return () => window.removeEventListener("open-new-item-dialog", handleOpen);
  }, [openNewItemDialog]);
  const handleMoveItems = useCallback(async () => {
    if (!moveDestination && moveDestination !== "") {
    }
    setIsMoving(true);
    try {
      for (const item of itemsToMove) {
        const oldPath = item.path;
        const filename = oldPath.split("/").pop();
        const newPath = moveDestination === "." || moveDestination === "" ? filename : `${moveDestination}/${filename}`;
        await axios.post("/api/file/move", {
          old_path: oldPath,
          new_path: newPath
        });
      }
      window.toastr?.success(
        `Successfully moved ${itemsToMove.length} item${itemsToMove.length > 1 ? "s" : ""}`
      );
      setIsMoveDialogOpen(false);
      fetchFiles();
    } catch (error) {
      console.error("Error moving items:", error);
      window.toastr?.error(
        "Failed to move items: " + (error.response?.data?.error || error.message)
      );
    } finally {
      setIsMoving(false);
    }
  }, [moveDestination, itemsToMove, fetchFiles]);
  const handleDeleteConfirm = useCallback(async () => {
    if (itemsToDelete.length === 0) return;
    const currentItemsToDelete = [...itemsToDelete];
    const pathsToDeleteSet = new Set(currentItemsToDelete.map((item) => item.path));
    setIsDeleteDialogOpen(false);
    setItemsToDelete([]);
    setIsDeleting(false);
    setRawFiles((prev) => {
      if (!prev) return prev;
      const updated = removeNodesFromTree(prev, pathsToDeleteSet);
      try {
        localStorage.setItem("dtx_cached_file_tree", JSON.stringify(updated));
      } catch (e) {
      }
      return updated;
    });
    setSelectedPaths((prev) => prev.filter((p) => !pathsToDeleteSet.has(p)));
    const isViewingDeleted = currentItemsToDelete.some(
      (item) => item.path === currentFilePath || item.type === "directory" && currentFilePath?.startsWith(item.path + "/")
    );
    if (isViewingDeleted) {
      const hashParams = new URLSearchParams(window.location.hash.split("?")[1] || "");
      const isTableView = hashParams.get("view") === "table";
      if (isTableView && currentFilePath) {
        const parts = currentFilePath.split("/");
        parts.pop();
        const parentFolder = parts.join("/") || "projects";
        window.location.hash = getFileUrl(parentFolder, { extraParams: { view: "table" } });
      } else {
        window.location.hash = getFileUrl(null);
      }
    }
    (async () => {
      try {
        for (const item of currentItemsToDelete) {
          const isDir = item.type === "directory";
          const endpoint = isDir ? "folder" : "file";
          const encodedPath = item.path.split("/").map((segment) => encodeURIComponent(segment)).join("/");
          await axios.delete(`/api/${endpoint}/${encodedPath}`);
        }
        fetchFiles({ skipShallow: true });
      } catch (error) {
        console.error("Error deleting items:", error);
        window.toastr?.error(`Error deleting items: ${getErrorMessage(error)}`);
        fetchFiles();
      }
    })();
  }, [itemsToDelete, currentFilePath, getFileUrl, fetchFiles]);
  const handleGitAdd = useCallback(
    async (nodePaths) => {
      const paths = Array.isArray(nodePaths) ? nodePaths : [nodePaths];
      try {
        for (const p of paths) {
          await axios.post("/api/git/add", {
            path: p
          });
        }
        fetchFiles();
      } catch (error) {
        console.error("Error adding to git:", error);
        window.toastr?.error(`Error adding to git: ${getErrorMessage(error)}`);
      }
    },
    [fetchFiles]
  );
  const handleGitUnstage = useCallback(
    async (nodePaths) => {
      const paths = Array.isArray(nodePaths) ? nodePaths : [nodePaths];
      try {
        for (const p of paths) {
          await axios.post("/api/git/unstage", {
            path: p
          });
        }
        fetchFiles();
      } catch (error) {
        console.error("Error unstaging from git:", error);
        window.toastr?.error(`Error unstaging from git: ${getErrorMessage(error)}`);
      }
    },
    [fetchFiles]
  );
  const handleGitCheckout = useCallback((nodePath) => {
    setItemToRevert(nodePath);
    setIsRevertAlertOpen(true);
  }, []);
  const handleRevertConfirm = async () => {
    if (!itemToRevert) return;
    setIsReverting(true);
    window.dispatchEvent(
      new CustomEvent("file-saving", { detail: { path: itemToRevert } })
    );
    try {
      const response = await axios.post("/api/git/revert_file", {
        path: itemToRevert,
        sourcePath: sourcePathToRevert
      });
      fetchFiles();
      window.dispatchEvent(
        new CustomEvent("file-reverted", {
          detail: {
            path: itemToRevert,
            build_output: response.data.build_output,
            alignment_output: response.data.alignment_output
          }
        })
      );
    } catch (error) {
      console.error("Error reverting git changes:", error);
      window.toastr?.error(`Error reverting changes: ${getErrorMessage(error)}`);
      window.dispatchEvent(
        new CustomEvent("file-save-failed", { detail: { path: itemToRevert } })
      );
    } finally {
      setIsReverting(false);
      setIsRevertAlertOpen(false);
      setItemToRevert(null);
      setSourcePathToRevert(null);
    }
  };
  const handleRevertCancel = () => {
    setIsRevertAlertOpen(false);
    setItemToRevert(null);
    setSourcePathToRevert(null);
  };
  const handleViewGitDiff = useCallback(async (nodePath) => {
    try {
      const response = await axios.get(`/api/git/diff?file=${encodeURIComponent(nodePath)}`);
      setGitDiff(response.data.diff);
      setGitDiffPath(nodePath);
      setIsGitDiffDialogOpen(true);
    } catch (error) {
      console.error("Error fetching git diff:", error);
      window.toastr?.error(`Error fetching git diff: ${getErrorMessage(error)}`);
    }
  }, []);
  const handleViewRevisionHistory = useCallback(async (path) => {
    setHistoryFilePath(path);
    setIsHistoryDialogOpen(true);
    setIsLoadingHistory(true);
    setHistoryData([]);
    try {
      const response = await axios.get("/api/git/history", { params: { file: path } });
      if (response.data.history) {
        setHistoryData(response.data.history);
      }
    } catch (e) {
      console.error("Error fetching history:", e);
      window.toastr?.error("Failed to load revision history");
    } finally {
      setIsLoadingHistory(false);
    }
  }, []);
  const handleRestoreRevision = useCallback(async (commitHash) => {
    setRestoringCommit(commitHash);
    try {
      const response = await axios.post("/api/git/restore", { path: historyFilePath, commit: commitHash });
      if (response.data.success) {
        window.toastr?.success("File restored successfully");
        setIsHistoryDialogOpen(false);
        fetchFiles();
      }
    } catch (e) {
      console.error("Error restoring revision:", e);
      window.toastr?.error("Failed to restore revision");
    } finally {
      setRestoringCommit(null);
    }
  }, [historyFilePath, fetchFiles]);
  const handleViewRevision = useCallback(async (commitHash) => {
    try {
      const response = await axios.get("/api/git/file_at_commit", {
        params: { file: historyFilePath, commit: commitHash }
      });
      if (response.data.tmp_path) {
        const url = getFileUrl(response.data.tmp_path, { hideProjects: true, extraParams: { readOnly: true } });
        window.open(url, "_blank");
      }
    } catch (e) {
      console.error("Error checking out revision:", e);
      window.toastr?.error("Failed to load temporary revision file");
    }
  }, [historyFilePath, getFileUrl]);
  const handleGitAddAll = useCallback(async () => {
    setCommandTitle("Git Add All");
    setCommandOutput("");
    setIsCommandOutputDialogOpen(true);
    setIsGitOperating(true);
    try {
      const response = await axios.post("/api/git/add_all");
      const { stdout, stderr, success } = response.data;
      setCommandOutput(`STDOUT:
${stdout}

STDERR:
${stderr}`);
      if (success) {
        window.toastr?.success("Git add all completed successfully");
        fetchFiles();
      } else {
        window.toastr?.warning("Git add all completed with issues");
      }
    } catch (error) {
      console.error("Error during git add all:", error);
      window.toastr?.error("Failed to run git add all: " + error.message);
      setIsCommandOutputDialogOpen(false);
    } finally {
      setIsGitOperating(false);
    }
  }, [fetchFiles]);
  const handleGitPull = useCallback(async () => {
    setCommandTitle("Git Pull");
    setCommandOutput("");
    setIsCommandOutputDialogOpen(true);
    setIsGitOperating(true);
    try {
      const response = await axios.post("/api/git/pull");
      const { stdout, stderr, success } = response.data;
      setCommandOutput(`STDOUT:
${stdout}

STDERR:
${stderr}`);
      if (success) {
        window.toastr?.success("Git pull completed successfully");
        fetchFiles();
      } else {
        window.toastr?.warning("Git pull completed with issues");
      }
    } catch (error) {
      console.error("Error during git pull:", error);
      window.toastr?.error("Failed to run git pull: " + error.message);
      setIsCommandOutputDialogOpen(false);
    } finally {
      setIsGitOperating(false);
    }
  }, [fetchFiles]);
  const handleGitStatus = useCallback(async () => {
    setIsGitStatusRawDialogOpen(true);
    setIsGitOperating(true);
    try {
      const response = await axios.get("/api/git/status_raw");
      setGitStatusRaw(response.data.stdout || "");
    } catch (error) {
      console.error("Error during git status:", error);
      window.toastr?.error("Failed to run git status: " + error.message);
      setIsGitStatusRawDialogOpen(false);
    } finally {
      setIsGitOperating(false);
    }
  }, []);
  const renderClickableGitStatus = useCallback(
    (text) => {
      if (!text) return text;
      const lines = text.split("\n");
      return lines.map((line, lineIdx) => {
        const renameMatch = line.match(/^(\s+renamed:\s+)(.*)\s+->\s+(.*)$/);
        if (renameMatch) {
          const [, prefix, oldPath, newPath] = renameMatch;
          const newPathClean = newPath.trim();
          if (newPathClean.startsWith("projects/")) {
            const pathForLink = newPathClean.endsWith("/") ? newPathClean.slice(0, -1) : newPathClean;
            const parts = pathForLink.split("/");
            const filename = parts.pop() || "";
            const dir = parts.join("/");
            const trailingSlash = newPathClean.endsWith("/") ? "/" : "";
            return /* @__PURE__ */ jsxs(React.Fragment, { children: [
              prefix,
              oldPath,
              " ",
              " -> ",
              " ",
              dir,
              dir ? "/" : "",
              /* @__PURE__ */ jsx(
                "span",
                {
                  style: {
                    color: "#106ba3",
                    cursor: "pointer",
                    textDecoration: "underline"
                  },
                  onClick: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const pathForNav = newPathClean.startsWith("projects/") ? newPathClean.substring(9) : newPathClean;
                    setIsGitStatusRawDialogOpen(false);
                    window.location.hash = getFileUrl(pathForNav);
                  },
                  children: filename
                }
              ),
              trailingSlash,
              "\n"
            ] }, lineIdx);
          }
        }
        const generalMatch = line.match(
          /^(\s+)(?:(modified|new file|deleted|copied|typechange):(\s+))?(.+)$/
        );
        if (generalMatch) {
          const [, prefix, type, spaces, filePath] = generalMatch;
          const filePathClean = filePath.trim();
          if (filePathClean.startsWith("projects/")) {
            const pathForLink = filePathClean.endsWith("/") ? filePathClean.slice(0, -1) : filePathClean;
            const parts = pathForLink.split("/");
            const filename = parts.pop() || "";
            const dir = parts.join("/");
            const trailingSlash = filePathClean.endsWith("/") ? "/" : "";
            return /* @__PURE__ */ jsxs(React.Fragment, { children: [
              prefix,
              type === "modified" ? /* @__PURE__ */ jsxs(
                "span",
                {
                  style: {
                    color: "#106ba3",
                    cursor: "pointer",
                    textDecoration: "underline"
                  },
                  onClick: () => handleViewGitDiff(filePathClean),
                  children: [
                    type,
                    ":"
                  ]
                }
              ) : type ? `${type}:` : null,
              spaces,
              dir,
              dir ? "/" : "",
              type === "deleted" ? filename : /* @__PURE__ */ jsx(
                "span",
                {
                  style: {
                    color: "#106ba3",
                    cursor: "pointer",
                    textDecoration: "underline"
                  },
                  onClick: (e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    const pathForNav = filePathClean.startsWith("projects/") ? filePathClean.substring(9) : filePathClean;
                    setIsGitStatusRawDialogOpen(false);
                    window.location.hash = getFileUrl(pathForNav);
                  },
                  children: filename
                }
              ),
              trailingSlash,
              "\n"
            ] }, lineIdx);
          }
        }
        return /* @__PURE__ */ jsxs(React.Fragment, { children: [
          line,
          "\n"
        ] }, lineIdx);
      });
    },
    [getFileUrl, handleViewGitDiff]
  );
  const handleUnstage = useCallback(async (path) => {
    setUnstagingPaths((prev) => [...prev, path]);
    setGitStatus((prev) => {
      const next = { ...prev };
      if (next[path]) {
        const code = next[path];
        if (code[0] === "A") next[path] = "??";
        else if (code[0] !== " " && code[0] !== "?") next[path] = " " + code[0];
      }
      return next;
    });
    try {
      await axios.post("/api/git/unstage", { path });
      fetchFiles();
    } catch (e) {
      console.error("Error unstaging file:", e);
      window.toastr?.error(`Failed to unstage ${path}: ${e.message}`);
      fetchFiles();
    } finally {
      setUnstagingPaths((prev) => prev.filter((p) => p !== path));
    }
  }, [fetchFiles]);
  const handleStage = useCallback(async (path) => {
    setStagingPaths((prev) => [...prev, path]);
    setGitStatus((prev) => {
      const next = { ...prev };
      if (next[path]) {
        const code = next[path];
        if (code === "??") next[path] = "A ";
        else if (code[0] === " ") next[path] = code[1] + " ";
      }
      return next;
    });
    try {
      await axios.post("/api/git/add", { path });
      fetchFiles();
    } catch (e) {
      console.error("Error staging file:", e);
      window.toastr?.error(`Failed to stage ${path}: ${e.message}`);
      fetchFiles();
    } finally {
      setStagingPaths((prev) => prev.filter((p) => p !== path));
    }
  }, [fetchFiles]);
  const handleStageAll = useCallback(async (paths) => {
    if (!paths || paths.length === 0) return;
    setStagingPaths((prev) => [...prev, ...paths]);
    setGitStatus((prev) => {
      const next = { ...prev };
      paths.forEach((path) => {
        if (next[path]) {
          const code = next[path];
          if (code === "??") next[path] = "A ";
          else if (code[0] === " ") next[path] = code[1] + " ";
        }
      });
      return next;
    });
    try {
      await Promise.all(paths.map((path) => axios.post("/api/git/add", { path })));
      fetchFiles();
    } catch (e) {
      console.error("Error staging files:", e);
      window.toastr?.error(`Failed to stage files: ${e.message}`);
      fetchFiles();
    } finally {
      setStagingPaths((prev) => prev.filter((p) => !paths.includes(p)));
    }
  }, [fetchFiles]);
  const handleUnstageAll = useCallback(async (paths) => {
    if (!paths || paths.length === 0) return;
    setUnstagingPaths((prev) => [...prev, ...paths]);
    setGitStatus((prev) => {
      const next = { ...prev };
      paths.forEach((path) => {
        if (next[path]) {
          const code = next[path];
          if (code[0] === "A") next[path] = "??";
          else if (code[0] !== " " && code[0] !== "?") next[path] = " " + code[0];
        }
      });
      return next;
    });
    try {
      await Promise.all(paths.map((path) => axios.post("/api/git/unstage", { path })));
      fetchFiles();
    } catch (e) {
      console.error("Error unstaging files:", e);
      window.toastr?.error(`Failed to unstage files: ${e.message}`);
      fetchFiles();
    } finally {
      setUnstagingPaths((prev) => prev.filter((p) => !paths.includes(p)));
    }
  }, [fetchFiles]);
  const handleGitDiff = useCallback(async (path) => {
    setGitDiffPath(path);
    setGitDiff("");
    setIsGitDiffDialogOpen(true);
    try {
      const response = await axios.get("/api/git/diff", { params: { file: path } });
      setGitDiff(response.data.diff || "No changes detected.");
    } catch (e) {
      console.error("Error fetching git diff:", e);
      setGitDiff(`Error fetching diff: ${e.response?.data?.error || e.message}`);
    }
  }, []);
  const handleGitRevertFile = useCallback(async (path, sourcePath) => {
    setItemToRevert(path);
    setSourcePathToRevert(sourcePath);
    setIsRevertAlertOpen(true);
  }, []);
  const handleGitCommit = useCallback(async () => {
    const commitMsg = gitCommitMessage.trim();
    if (!commitMsg) return;
    setIsGitCommitDialogOpen(false);
    setGitCommitMessage("");
    setIsGitOperating(true);
    try {
      const response = await axios.post("/api/git/commit", {
        message: commitMsg,
        all: !isAnythingStaged
      });
      const { stdout, stderr, success } = response.data || {};
      if (success) {
        window.toastr?.success("Git commit completed successfully");
        fetchFiles();
      } else {
        setCommandTitle("Git Commit");
        setCommandOutput(`STDOUT:
${stdout || ""}

STDERR:
${stderr || ""}`);
        setIsCommandOutputDialogOpen(true);
        window.toastr?.warning("Git commit completed with issues");
      }
    } catch (error) {
      console.error("Error during git commit:", error);
      const errData = error?.response?.data;
      const stdout = errData?.stdout || "";
      const stderr = errData?.stderr || errData?.error || error.message || "";
      setCommandTitle("Git Commit");
      setCommandOutput(`STDOUT:
${stdout}

STDERR:
${stderr}`);
      setIsCommandOutputDialogOpen(true);
      window.toastr?.error("Failed to run git commit: " + (errData?.error || error.message));
    } finally {
      setIsGitOperating(false);
    }
  }, [fetchFiles, gitCommitMessage, isAnythingStaged]);
  const handleGitPush = useCallback(async () => {
    setCommandTitle("Git Push");
    setCommandOutput("");
    setIsCommandOutputDialogOpen(true);
    setIsGitOperating(true);
    try {
      const response = await axios.post("/api/git/push");
      const { stdout, stderr, success } = response.data;
      setCommandOutput(`STDOUT:
${stdout}

STDERR:
${stderr}`);
      if (success) {
        window.toastr?.success("Git push completed successfully");
        fetchFiles();
      } else {
        window.toastr?.warning("Git push completed with issues");
      }
    } catch (error) {
      console.error("Error during git push:", error);
      window.toastr?.error("Failed to run git push: " + error.message);
      setIsCommandOutputDialogOpen(false);
    } finally {
      setIsGitOperating(false);
    }
  }, [fetchFiles]);
  const handleGitSync = useCallback(async () => {
    setCommandTitle("Git Sync");
    setCommandOutput("");
    setIsCommandOutputDialogOpen(true);
    setIsGitOperating(true);
    try {
      await axios.post("/api/git/fetch");
      const pullRes = await axios.post("/api/git/pull");
      if (!pullRes.data.success) {
        setCommandOutput(`PULL FAILED:
STDOUT:
${pullRes.data.stdout}

STDERR:
${pullRes.data.stderr}`);
        window.toastr?.error("Sync failed during Pull");
        fetchFiles();
        return;
      }
      const pushRes = await axios.post("/api/git/push");
      const { stdout, stderr, success } = pushRes.data;
      setCommandOutput(`PULL SUCCESS

PUSH RESULT:
STDOUT:
${stdout}

STDERR:
${stderr}`);
      if (success) {
        window.toastr?.success("Git sync completed successfully");
        fetchFiles();
      } else {
        window.toastr?.warning("Git sync completed (Pull ok, Push had issues)");
        fetchFiles();
      }
    } catch (error) {
      console.error("Error during git sync:", error);
      window.toastr?.error("Failed to run git sync: " + error.message);
      setIsCommandOutputDialogOpen(false);
    } finally {
      setIsGitOperating(false);
    }
  }, [fetchFiles]);
  const handleMinimapAlignment = useCallback((itemsToUse) => {
    if (!itemsToUse.every((i) => /\.(gb|gbk|genbank|gen\.gb|ab1|fasta|fa)$/i.test(i.path))) {
      window.toastr?.error("All selected files must be sequence files (.gb, .gbk, .fasta, .fa) or Sanger (.ab1) files to align");
      return;
    }
    const hasSeqFiles = itemsToUse.some((i) => /\.(gb|gbk|genbank|gen\.gb|fasta|fa)$/i.test(i.path));
    if (!hasSeqFiles) {
      window.toastr?.error("At least one sequence file must be selected to act as the reference");
      return;
    }
    const filePaths = itemsToUse.map((i) => i.path);
    setMinimapTargetPaths(filePaths);
    const defaultRef = filePaths.find((p) => /\.(gb|gbk|genbank|gen\.gb|fasta|fa)$/i.test(p)) || filePaths[0];
    setMinimapReferencePath(defaultRef);
    setIsMinimapDialogOpen(true);
  }, []);
  const handleInversionCheck = useCallback(async (itemsToUse) => {
    if (itemsToUse.length !== 2) {
      window.toastr?.error("Exactly two files must be selected to check for inversions.");
      return;
    }
    const filePaths = itemsToUse.map((i) => i.path);
    setInversionFiles(filePaths);
    setIsInversionDialogOpen(true);
    setInversionCheckResult(null);
    setIsCheckingInversion(true);
    try {
      const response = await axios.post("/api/tools/check_inversion", {
        files: filePaths
      });
      setInversionCheckResult(response.data.warnings || []);
    } catch (e) {
      window.toastr?.error("Inversion check failed: " + (e.response?.data?.error || e.message));
      setIsInversionDialogOpen(false);
    } finally {
      setIsCheckingInversion(false);
    }
  }, []);
  const handleCreateAssemblyFromFiles = useCallback(async (items) => {
    const firstPath = items[0].path;
    const targetDir = firstPath.split("/").slice(0, -1).join("/") || ".";
    setTargetFolderPath(targetDir);
    setNewFileType("assembly");
    setNewFileName("new_assembly");
    setAssemblyComponents(items.map((i) => i.name));
    setIsCreateFileDialogOpen(true);
  }, []);
  const handleContextMenuInternal = useCallback(
    (e, fileItems, options = {}) => {
      if (e) e.preventDefault();
      const rawInputItems = (Array.isArray(fileItems) ? fileItems : [fileItems]).filter(Boolean);
      if (rawInputItems.length === 0) return;
      const items = rawInputItems.map((item) => {
        if (!item) return item;
        if (item.path) {
          if (item.path === "." || item.path === "") {
            return {
              path: ".",
              name: "projects",
              type: "directory",
              children: Array.isArray(rawFiles?.children) ? rawFiles.children : Array.isArray(rawFiles) ? rawFiles : [],
              ...item
            };
          }
          const found = findItemByPath(rawFiles, item.path);
          if (found) {
            return { ...found, ...item };
          }
        }
        if (!item.name && item.path) {
          return { ...item, name: item.path.split("/").pop() };
        }
        return item;
      }).filter(Boolean);
      if (items.length === 0) return;
      const isMultiple = items.length > 1;
      const firstItem = items[0];
      if (!firstItem) return;
      const isDir = !isMultiple && firstItem.type === "directory";
      const isNewUnsaved = !isMultiple && firstItem.isNewUnsaved;
      const isProjectsRoot = isDir && (firstItem.path === "." || firstItem.path === "" || firstItem.name === "projects" && !firstItem.path.includes("/"));
      const source = options.source || (options.canGitAdd !== void 0 ? "explorer" : "table");
      let { canGitAdd, canGitUnstage, canGitRevert } = options;
      if (canGitAdd === void 0 && gitStatus) {
        if (isMultiple) {
          canGitAdd = items.some((item) => {
            const status = gitStatus[item.path];
            return item.type === "directory" ? hasUnstagedDescendant(item.path, gitStatus) : isUnstaged(status);
          });
          canGitUnstage = items.some((item) => {
            const status = gitStatus[item.path];
            return item.type === "directory" ? hasStagedDescendant(item.path, gitStatus) : isStaged(status);
          });
          canGitRevert = false;
        } else {
          const status = gitStatus[firstItem.path];
          if (isDir) {
            canGitAdd = hasUnstagedDescendant(firstItem.path, gitStatus);
            canGitUnstage = hasStagedDescendant(firstItem.path, gitStatus);
            canGitRevert = Object.keys(gitStatus).some(
              (k) => (k === firstItem.path || k.startsWith(firstItem.path + "/")) && gitStatus[k] !== "??"
            );
          } else {
            canGitAdd = isUnstaged(status);
            canGitUnstage = isStaged(status);
            canGitRevert = status && status !== "??";
          }
        }
      }
      const targetChildren = Array.isArray(firstItem.children) ? firstItem.children : Array.isArray(rawFiles?.children) ? rawFiles.children : Array.isArray(rawFiles) ? rawFiles : [];
      const showExpandAll = isDir && (() => {
        const checkHasUnexpanded = (node) => {
          if (node.type !== "directory" || !node.children) return false;
          if (!expandedIds[node.path]) return true;
          return node.children.some((child) => checkHasUnexpanded(child));
        };
        if (!targetChildren || targetChildren.length === 0) return false;
        return targetChildren.some((child) => checkHasUnexpanded(child));
      })();
      const showCollapseAll = isDir && (() => {
        const checkHasExpanded = (node) => {
          if (node.type !== "directory" || !node.children) return false;
          if (expandedIds[node.path]) return true;
          return node.children.some((child) => checkHasExpanded(child));
        };
        if (!targetChildren || targetChildren.length === 0) return false;
        return targetChildren.some((child) => checkHasExpanded(child));
      })();
      const handleExpandAll = () => {
        const newExpanded = { ...expandedIds };
        if (!isProjectsRoot) {
          newExpanded[firstItem.path] = true;
        }
        const recurse = (node) => {
          if (node.type === "directory") {
            newExpanded[node.path] = true;
            if (node.children) node.children.forEach(recurse);
          }
        };
        if (targetChildren) targetChildren.forEach(recurse);
        setExpandedIds(newExpanded);
      };
      const handleCollapseAll = () => {
        const newExpanded = { ...expandedIds };
        const recurse = (node) => {
          if (node.type === "directory") {
            delete newExpanded[node.path];
            if (node.children) node.children.forEach(recurse);
          }
        };
        if (!isProjectsRoot) {
          delete newExpanded[firstItem.path];
        }
        if (targetChildren) targetChildren.forEach(recurse);
        setExpandedIds(newExpanded);
      };
      const getRecursiveFiles = (item) => {
        if (!item) return [];
        if (item.type === "file") return [item];
        let files = [];
        if (item.children) {
          item.children.forEach((child) => {
            files = files.concat(getRecursiveFiles(child));
          });
        }
        return files;
      };
      const effectiveDownloadItems = items.reduce((acc, item) => {
        return acc.concat(getRecursiveFiles(item));
      }, []);
      const isDownloadMultiple = effectiveDownloadItems.length > 1;
      const handleDownload = async (options2 = {}) => {
        try {
          const useRecursive = options2.merge_gb || options2.as_products;
          const response = await axios.post(
            "/api/download",
            {
              paths: (useRecursive ? effectiveDownloadItems : items).map(
                (i) => i.path
              ),
              ...options2
            },
            { responseType: "blob" }
          );
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          const contentDisposition = response.headers["content-disposition"];
          let fileName = options2.merge_gb ? "merged.gb" : isMultiple || isDir ? "download.zip" : firstItem.name || (firstItem.path ? firstItem.path.split("/").pop() : "download");
          if (contentDisposition) {
            const fileNameMatch = contentDisposition.match(
              /filename="?([^";]+)"?/
            );
            if (fileNameMatch) {
              fileName = fileNameMatch[1];
            }
          }
          link.setAttribute("download", fileName);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
        } catch (error) {
          console.error("Download failed:", error);
          window.toastr?.error("Download failed: " + error.message);
        }
      };
      const anyAssembly = effectiveDownloadItems.some(
        (i) => ASSEMBLY_EXTENSIONS.some((ex) => i.path.endsWith(ex))
      );
      const allAssembly = effectiveDownloadItems.length > 0 && effectiveDownloadItems.every(
        (i) => ASSEMBLY_EXTENSIONS.some((ex) => i.path.endsWith(ex))
      );
      const allSequence = effectiveDownloadItems.length > 0 && effectiveDownloadItems.every(
        (i) => i.type === "file" && SEQUENCE_EXTENSIONS.some((ex) => i.path.endsWith(ex))
      );
      const sequenceOrAssemblyItems = items.filter(
        (i) => i.type === "file" && [...SEQUENCE_EXTENSIONS, ...ASSEMBLY_EXTENSIONS].some(
          (ext) => (i.name || i.path || "").toLowerCase().endsWith(ext)
        )
      );
      const allInInventory = sequenceOrAssemblyItems.length > 0 && sequenceOrAssemblyItems.every(
        (i) => optimisticInventory[i.path] !== void 0 ? optimisticInventory[i.path] : i.inInventory
      );
      const isSequenceOrAssembly = !isMultiple && !isDir && [...SEQUENCE_EXTENSIONS, ...ASSEMBLY_EXTENSIONS].some(
        (ext) => (firstItem.name || firstItem.path || "").toLowerCase().endsWith(ext)
      );
      const resolvedInInventory = isSequenceOrAssembly && (optimisticInventory[firstItem.path] !== void 0 ? optimisticInventory[firstItem.path] : firstItem.inInventory);
      const resolvedHasComments = isSequenceOrAssembly && (optimisticHasComments[firstItem.path] !== void 0 ? optimisticHasComments[firstItem.path] : firstItem.hasComments);
      const getDisplayExtension = (item) => {
        if (!item || !item.path) return "file";
        if (item.path.endsWith(".sam.json")) return "sam.json";
        if (item.path.endsWith(".jbrowse.zip")) return "jbrowse.zip";
        if (item.path.endsWith(".gen.gb")) return "gen.gb";
        const parts = (item.name || item.path).split(".");
        return parts.length > 1 ? parts[parts.length - 1] : "file";
      };
      const handleShowDetails = async () => {
        const displayPath = firstItem.path === "." || firstItem.path === "" ? "projects" : `projects/${firstItem.path}`;
        setSelectedPath(displayPath);
        const desc = optimisticDescriptions[firstItem.path] !== void 0 ? optimisticDescriptions[firstItem.path] : firstItem.description || "";
        setSelectedDescription(desc);
        descriptionRef.current = desc;
        setRawNodeData(firstItem);
        setIsPathDialogOpen(true);
        setIsLoadingMetadata(true);
        try {
          const encodedPath = firstItem.path.split("/").map((segment) => encodeURIComponent(segment)).join("/");
          const response = await axios.get(`/api/file/metadata/${encodedPath}`);
          setDateCreated(response.data.created);
          setDateModified(response.data.last_modified);
          setSequenceLength(response.data.sequence_length);
          if (response.data.description !== void 0) {
            if (optimisticDescriptions[firstItem.path] === void 0) {
              setSelectedDescription(response.data.description || "");
              descriptionRef.current = response.data.description || "";
            }
          }
          if (response.data.locus !== void 0) {
            setSelectedLocus(response.data.locus || "");
            locusRef.current = response.data.locus || "";
          }
        } catch (error) {
          console.error("Error fetching metadata:", error);
        } finally {
          setIsLoadingMetadata(false);
        }
      };
      if (isNewUnsaved) {
        ContextMenu.show(
          /* @__PURE__ */ jsx(Menu, { children: /* @__PURE__ */ jsx(
            MenuItem,
            {
              disabled: true,
              icon: "info-sign",
              text: "Unsaved File (Save to enable actions)"
            }
          ) }),
          { left: e.clientX, top: e.clientY }
        );
        return;
      }
      const anyDir = items.some((i) => i.type === "directory");
      const canPasteIntoTarget = isDir && copiedItems.length > 0 && (!isProjectsRoot || copiedItems.every((i) => i.type === "directory"));
      ContextMenu.show(
        /* @__PURE__ */ jsxs(Menu, { children: [
          canPasteIntoTarget && /* @__PURE__ */ jsx(
            MenuItem,
            {
              icon: "link",
              text: copiedItems.length > 1 ? `Paste Alias for ${copiedItems.length} Items` : "Paste Alias",
              onClick: () => {
                const pasteTarget = isDir ? firstItem : (() => {
                  const parts = firstItem.path.split("/");
                  if (parts.length <= 1)
                    return { path: ".", type: "directory" };
                  const parentPath = parts.slice(0, -1).join("/");
                  return findItemByPath(rawFiles, parentPath) || {
                    path: ".",
                    type: "directory"
                  };
                })();
                handlePasteAlias(pasteTarget);
              }
            }
          ),
          canPasteIntoTarget && /* @__PURE__ */ jsx(Menu.Divider, {}),
          !isMultiple && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsxs(
              MenuItem,
              {
                icon: "document-open",
                text: "Open",
                onClick: () => {
                  window.location.hash = getFileUrl(
                    firstItem.path,
                    isDir ? { extraParams: { view: "table" } } : {}
                  );
                },
                children: [
                  /* @__PURE__ */ jsx(
                    MenuItem,
                    {
                      icon: isDir ? "folder-open" : "document",
                      text: isDir ? "Folder" : "File",
                      onClick: () => {
                        window.location.hash = getFileUrl(
                          firstItem.path,
                          isDir ? { extraParams: { view: "table" } } : {}
                        );
                      }
                    }
                  ),
                  !isDir && firstItem.path.endsWith(".gen.gb") && /* @__PURE__ */ jsx(
                    MenuItem,
                    {
                      icon: "code-block",
                      text: "Open Assembly File",
                      onClick: () => {
                        const assemblyPath = firstItem.path.replace(
                          ".gen.gb",
                          ".assembly"
                        );
                        window.location.hash = getFileUrl(assemblyPath);
                      }
                    }
                  ),
                  !isDir && firstItem.isSymlink && /* @__PURE__ */ jsx(
                    MenuItem,
                    {
                      icon: "share",
                      text: "Alias Source",
                      onClick: () => {
                        if (firstItem.isBrokenLink) {
                          window.toastr?.error(
                            `Alias source is missing: ${firstItem.symlinkTarget}`
                          );
                          return;
                        }
                        handleGoToAliasSource(firstItem.symlinkTarget);
                      }
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    MenuItem,
                    {
                      icon: "document-open",
                      text: "in new tab",
                      onClick: () => {
                        const url = getFileUrl(firstItem.path, {
                          hideProjects: true,
                          extraParams: isDir ? { view: "table" } : {}
                        });
                        window.open(url, "_blank");
                      }
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    MenuItem,
                    {
                      icon: "application",
                      text: "in new window",
                      onClick: () => {
                        const url = getFileUrl(firstItem.path, {
                          hideProjects: true,
                          extraParams: isDir ? { view: "table" } : {}
                        });
                        window.open(
                          url,
                          "_blank",
                          "toolbar=yes,scrollbars=yes,resizable=yes,top=100,left=100,width=1200,height=800"
                        );
                      }
                    }
                  )
                ]
              }
            ),
            /* @__PURE__ */ jsx(Menu.Divider, {})
          ] }),
          sequenceOrAssemblyItems.length > 0 && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "properties",
                text: isMultiple ? allInInventory ? "Remove selected from inventory" : "Mark selected in inventory" : resolvedInInventory ? "Remove from inventory" : "Mark In Inventory",
                onClick: () => handleToggleInventoryMulti(sequenceOrAssemblyItems)
              }
            ),
            /* @__PURE__ */ jsx(Menu.Divider, {})
          ] }),
          !isMultiple && isSequenceOrAssembly && /* @__PURE__ */ jsxs(Fragment, { children: [
            /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "comment",
                text: resolvedHasComments ? "View Comments" : "Add Comment",
                onClick: () => handleOpenCommentsDialog(firstItem.path)
              }
            ),
            /* @__PURE__ */ jsx(Menu.Divider, {})
          ] }),
          isDir && !isProjectsRoot && pinnedFolder !== firstItem.path && /* @__PURE__ */ jsx(
            MenuItem,
            {
              icon: "pin",
              text: "Pin Folder as Root",
              onClick: () => setPinnedFolder(firstItem.path)
            }
          ),
          isDir && pinnedFolder === firstItem.path && /* @__PURE__ */ jsx(
            MenuItem,
            {
              icon: "cross",
              text: "Unpin Folder",
              onClick: () => setPinnedFolder(null)
            }
          ),
          isDir && showExpandAll && /* @__PURE__ */ jsx(
            MenuItem,
            {
              icon: "expand-all",
              text: "Expand All",
              onClick: handleExpandAll
            }
          ),
          isDir && showCollapseAll && /* @__PURE__ */ jsx(
            MenuItem,
            {
              icon: "collapse-all",
              text: "Collapse All",
              onClick: handleCollapseAll
            }
          ),
          isDir && (showExpandAll || showCollapseAll) && /* @__PURE__ */ jsx(Menu.Divider, {}),
          /* @__PURE__ */ jsxs(MenuItem, { icon: "eye-open", text: "View", children: [
            !isMultiple && /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "info-sign",
                text: "Details",
                onClick: handleShowDetails
              }
            ),
            !isMultiple && !isDir && (!gitStatus || gitStatus[firstItem.path] !== "??") && !firstItem.isNewUnsaved && /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "history",
                text: "Revision History",
                onClick: () => handleViewRevisionHistory(firstItem.path)
              }
            ),
            !isMultiple && !isDir && firstItem.path.endsWith(".gb") && /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "link",
                text: "Bound Sequences",
                onClick: () => {
                  window.location.hash = getFileUrl(null, {
                    extraParams: {
                      view: "bound-sequences",
                      primer: encodeURIComponent(firstItem.path)
                    }
                  });
                }
              }
            ),
            !isMultiple && !isDir && /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "graph",
                text: "Ancestry Graph",
                onClick: () => {
                  const hash = window.location.hash;
                  let queryParams = "&view=graph";
                  if (hash.includes("?")) {
                    const urlParams = new URLSearchParams(hash.split("?")[1]);
                    const reload = urlParams.get("reload");
                    if (reload) {
                      queryParams += `&reload=${reload}`;
                    }
                  }
                  window.location.hash = `#/Standalone?file=${firstItem.path}${queryParams}`;
                }
              }
            ),
            firstItem.isSymlink && /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "share",
                text: "Alias Source",
                onClick: () => {
                  if (firstItem.isBrokenLink) {
                    window.toastr?.error(
                      `Alias source is missing: ${firstItem.symlinkTarget}`
                    );
                    return;
                  }
                  handleGoToAliasSource(firstItem.symlinkTarget);
                }
              }
            ),
            canGitRevert && /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "code",
                text: "View Git Diff",
                onClick: () => handleViewGitDiff(firstItem.path)
              }
            )
          ] }),
          /* @__PURE__ */ jsxs(MenuItem, { icon: "duplicate", text: "Copy", children: [
            !anyDir && /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "duplicate",
                disabled: items.some((i) => i.path.endsWith("sam.json") || i.path.endsWith("jbrowse.zip")),
                label: items.some((i) => i.path.endsWith("sam.json") || i.path.endsWith("jbrowse.zip")) ? "Restricted" : "",
                text: isMultiple ? `Copy ${items.length} Items` : "Copy",
                onClick: () => {
                  setCopiedItems(items);
                  setIsCut(false);
                  window.toastr?.success(
                    `${isMultiple ? items.length + " items" : "Item"} copied to clipboard`
                  );
                  const names = items.map((i) => i.name || (i.path ? i.path.split("/").pop() : "")).join("\n");
                  navigator.clipboard?.writeText(names);
                }
              }
            ),
            !isMultiple && !isDir && /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "duplicate",
                text: "Duplicate",
                disabled: firstItem.path.endsWith("sam.json") || firstItem.path.endsWith("jbrowse.zip"),
                label: firstItem.path.endsWith("sam.json") || firstItem.path.endsWith("jbrowse.zip") ? "Restricted" : "",
                onClick: () => handleDuplicate(firstItem, void 0, source)
              }
            ),
            /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "clipboard",
                text: "Copy Path",
                onClick: () => {
                  const paths = items.map(
                    (i) => i.path === "." || i.path === "" ? "projects" : `projects/${i.path}`
                  ).join(" ");
                  navigator.clipboard.writeText(paths);
                  window.toastr?.success("Path copied to clipboard");
                }
              }
            ),
            /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "clipboard",
                text: "Copy URL",
                onClick: () => {
                  const urls = items.map((item) => {
                    const nodePath = item.path;
                    const extraParams = item.type === "directory" ? { view: "table" } : {};
                    const fileUrl = getFileUrl(nodePath, { extraParams });
                    return window.location.origin + window.location.pathname + fileUrl;
                  }).join("\n");
                  navigator.clipboard?.writeText(urls);
                  window.toastr?.success(
                    `${isMultiple ? items.length + " URLs" : "URL"} copied to clipboard`
                  );
                }
              }
            )
          ] }),
          /* @__PURE__ */ jsxs(MenuItem, { icon: "edit", text: "Edit", children: [
            !isMultiple && !isDir && /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "duplicate",
                text: "Duplicate",
                disabled: firstItem.path.endsWith("sam.json") || firstItem.path.endsWith("jbrowse.zip"),
                label: firstItem.path.endsWith("sam.json") || firstItem.path.endsWith("jbrowse.zip") ? "Restricted" : "",
                onClick: () => handleDuplicate(firstItem, void 0, source)
              }
            ),
            canPasteIntoTarget && /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "clipboard",
                text: copiedItems.length > 1 ? `Paste ${copiedItems.length} Items` : "Paste",
                onClick: () => handlePaste(firstItem)
              }
            ),
            canPasteIntoTarget && /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "link",
                text: copiedItems.length > 1 ? `Paste Alias for ${copiedItems.length} Items` : "Paste Alias",
                onClick: () => handlePasteAlias(firstItem)
              }
            ),
            !isProjectsRoot && /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "cut",
                text: isMultiple ? `Cut ${items.length} Items` : "Cut",
                onClick: () => {
                  setCopiedItems(items);
                  setIsCut(true);
                  window.toastr?.success(
                    `${isMultiple ? items.length + " items" : "Item"} cut to clipboard`
                  );
                  const names = items.map((i) => i.name || (i.path ? i.path.split("/").pop() : "")).join("\n");
                  navigator.clipboard?.writeText(names);
                }
              }
            ),
            !anyDir && /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "duplicate",
                text: isMultiple ? `Copy ${items.length} Items` : "Copy",
                onClick: () => {
                  setCopiedItems(items);
                  setIsCut(false);
                  window.toastr?.success(
                    `${isMultiple ? items.length + " items" : "Item"} copied to clipboard`
                  );
                  const names = items.map((i) => i.name || (i.path ? i.path.split("/").pop() : "")).join("\n");
                  navigator.clipboard?.writeText(names);
                }
              }
            ),
            !isMultiple && !isProjectsRoot && /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "edit",
                text: "Rename",
                onClick: () => {
                  setRenamingState(firstItem.path, firstItem.name || (firstItem.path ? firstItem.path.split("/").pop() : ""), source);
                }
              }
            ),
            !isMultiple && !isProjectsRoot && /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "move",
                text: "Move",
                onClick: () => {
                  setItemsToMove(items);
                  setMoveDestination("");
                  setIsMoveDialogOpen(true);
                }
              }
            ),
            !isMultiple && /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "info-sign",
                text: "Details",
                onClick: handleShowDetails
              }
            ),
            !isProjectsRoot && /* @__PURE__ */ jsx(Menu.Divider, {}),
            !isProjectsRoot && /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "trash",
                text: isMultiple ? `Delete ${items.length} items` : `Delete ${isDir ? "Folder" : "File"}`,
                intent: Intent.DANGER,
                onClick: () => {
                  setItemsToDelete(items);
                  setIsDeleteDialogOpen(true);
                }
              }
            )
          ] }),
          /* @__PURE__ */ jsxs(MenuItem, { icon: "share", text: "Share", children: [
            /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "clipboard",
                text: "Copy Path",
                onClick: () => {
                  const paths = items.map(
                    (i) => i.path === "." || i.path === "" ? "projects" : `projects/${i.path}`
                  ).join(" ");
                  navigator.clipboard.writeText(paths);
                  window.toastr?.success("Path copied to clipboard");
                }
              }
            ),
            /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "clipboard",
                text: "Copy URL",
                onClick: () => {
                  const urls = items.map((item) => {
                    const nodePath = item.path;
                    const extraParams = item.type === "directory" ? { view: "table" } : {};
                    const fileUrl = getFileUrl(nodePath, { extraParams });
                    return window.location.origin + window.location.pathname + fileUrl;
                  }).join("\n");
                  navigator.clipboard?.writeText(urls);
                  window.toastr?.success(
                    `${isMultiple ? items.length + " URLs" : "URL"} copied to clipboard`
                  );
                }
              }
            )
          ] }),
          isDir && /* @__PURE__ */ jsxs(MenuItem, { icon: "plus", text: "New", children: [
            /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "folder-new",
                text: "Create New Folder",
                onClick: () => {
                  setTargetFolderPath(firstItem.path);
                  setIsFolderDialogOpen(true);
                }
              }
            ),
            !isProjectsRoot && /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "document",
                text: "New Sequence",
                "data-testid": "new-sequence-item",
                onClick: () => {
                  setTargetFolderPath(firstItem.path);
                  setNewFileType("sequence");
                  setIsCreateFileDialogOpen(true);
                }
              }
            ),
            !isProjectsRoot && /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "code-block",
                text: "New Assembly",
                onClick: () => {
                  setTargetFolderPath(firstItem.path);
                  setNewFileType("assembly");
                  setIsCreateFileDialogOpen(true);
                }
              }
            )
          ] }),
          isDir && !isProjectsRoot && /* @__PURE__ */ jsxs(MenuItem, { icon: "import", text: "Import", children: [
            /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "document",
                text: "Import Sequence",
                onClick: () => handleImportSequence(firstItem.path)
              }
            ),
            /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "database",
                text: "From Addgene",
                onClick: () => handleImportFromAddgene(firstItem.path)
              }
            )
          ] }),
          /* @__PURE__ */ jsxs(MenuItem, { icon: "download", text: "Download", children: [
            !isDir && /* @__PURE__ */ jsxs(Fragment, { children: [
              !isMultiple && !allSequence && !allAssembly && effectiveDownloadItems.length > 0 && /* @__PURE__ */ jsx(
                MenuItem,
                {
                  text: getDisplayExtension(effectiveDownloadItems[0]),
                  onClick: () => handleDownload()
                }
              ),
              allSequence && /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsx(
                  MenuItem,
                  {
                    text: "gb",
                    onClick: () => handleDownload(isMultiple ? { merge_gb: true } : {})
                  }
                ),
                /* @__PURE__ */ jsx(
                  MenuItem,
                  {
                    text: "fasta",
                    onClick: () => handleDownload({
                      format: "fasta",
                      merge_gb: isMultiple
                    })
                  }
                )
              ] }),
              allAssembly && /* @__PURE__ */ jsx(MenuItem, { text: "assembly", onClick: () => handleDownload() })
            ] }),
            (isMultiple || isDir) && /* @__PURE__ */ jsx(MenuItem, { text: "zip", onClick: () => handleDownload() }),
            anyAssembly && /* @__PURE__ */ jsxs(MenuItem, { text: isMultiple ? "products" : "product", children: [
              (isMultiple || isDir) && /* @__PURE__ */ jsx(
                MenuItem,
                {
                  text: "zip",
                  onClick: () => handleDownload({ as_products: true })
                }
              ),
              !isDir && /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsx(
                  MenuItem,
                  {
                    text: "gb",
                    onClick: () => handleDownload({
                      as_products: true,
                      merge_gb: isMultiple
                    })
                  }
                ),
                /* @__PURE__ */ jsx(
                  MenuItem,
                  {
                    text: "fasta",
                    onClick: () => handleDownload({
                      as_products: true,
                      format: "fasta",
                      merge_gb: isMultiple
                    })
                  }
                )
              ] })
            ] })
          ] }),
          items.every(
            (i) => [...SEQUENCE_EXTENSIONS, ...CHROMATOGRAM_EXTENSIONS].some((ex) => i.path.endsWith(ex))
          ) && items.length === 2 && /* @__PURE__ */ jsx(
            MenuItem,
            {
              icon: "swap-horizontal",
              text: "Check for Inversion",
              onClick: () => handleInversionCheck(items)
            }
          ),
          items.every(
            (i) => [...SEQUENCE_EXTENSIONS, ...CHROMATOGRAM_EXTENSIONS].some((ex) => i.path.endsWith(ex))
          ) && items.length >= 2 && /* @__PURE__ */ jsx(
            MenuItem,
            {
              icon: "align-left",
              text: "Generate SAM Alignment",
              onClick: () => handleMinimapAlignment(items)
            }
          ),
          items.every(
            (i) => SEQUENCE_EXTENSIONS.some((ex) => i.path.endsWith(ex))
          ) && items.length >= 2 && /* @__PURE__ */ jsx(
            MenuItem,
            {
              icon: "code-block",
              text: "Create Assembly from Selected Files",
              onClick: () => handleCreateAssemblyFromFiles(items)
            }
          ),
          (canGitAdd || canGitRevert || canGitUnstage || isDir && !isMultiple) && /* @__PURE__ */ jsxs(MenuItem, { icon: "git-branch", text: "Git", children: [
            canGitAdd && /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "git-commit",
                text: "Git Add",
                onClick: () => handleGitAdd(items.map((i) => i.path))
              }
            ),
            canGitRevert && /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "code",
                text: "View Git Diff",
                onClick: () => handleViewGitDiff(firstItem.path)
              }
            ),
            canGitUnstage && /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "undo",
                text: "Git Unstage",
                onClick: () => handleGitUnstage(items.map((i) => i.path))
              }
            ),
            isDir && !isMultiple && /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "changes",
                text: "Git Status / Fetch",
                onClick: handleGitStatus
              }
            ),
            canGitRevert && /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "history",
                text: "Revert Changes",
                intent: Intent.WARNING,
                onClick: () => handleGitCheckout(firstItem.path)
              }
            ),
            !isMultiple && !isDir && (!gitStatus || gitStatus[firstItem.path] !== "??") && (!firstItem || !firstItem.isNewUnsaved) && /* @__PURE__ */ jsx(
              MenuItem,
              {
                icon: "history",
                text: "Revision History",
                onClick: () => handleViewRevisionHistory(firstItem.path)
              }
            )
          ] })
        ] }),
        { left: e.clientX, top: e.clientY }
      );
    },
    [
      gitStatus,
      getFileUrl,
      handleDuplicate,
      handleGitAdd,
      handleGitCheckout,
      handleGitUnstage,
      copiedItems,
      handlePaste,
      handlePasteAlias,
      handleGoToAliasSource,
      handleImportSequence,
      expandedIds,
      handleMinimapAlignment,
      handleCreateAssemblyFromFiles,
      handleViewGitDiff,
      rawFiles
    ]
  );
  const handleOpenCommentsDialog = async (path) => {
    setCommentsFilePath(path);
    setIsCommentsDialogOpen(true);
    setIsLoadingComments(true);
    setNewCommentText("");
    try {
      const encodedPath = path.split("/").map(encodeURIComponent).join("/");
      const response = await axios.get(`/api/file/metadata/${encodedPath}`);
      setFileComments(response.data.comments || []);
      setOptimisticHasComments((prev) => ({ ...prev, [path]: response.data.comments && response.data.comments.length > 0 }));
    } catch (error) {
      console.error("Error fetching metadata for comments:", error);
    } finally {
      setIsLoadingComments(false);
    }
  };
  const handleAddComment = async () => {
    if (!newCommentText.trim() || !commentsFilePath) return;
    const dateStr = (/* @__PURE__ */ new Date()).toISOString().split("T")[0];
    const fullComment = `[${dateStr}] ${newCommentText.trim()}`;
    const newComments = [...fileComments, fullComment];
    setFileComments(newComments);
    setOptimisticHasComments((prev) => ({ ...prev, [commentsFilePath]: true }));
    setNewCommentText("");
    let assocAssembly = null;
    if (commentsFilePath.endsWith(".assembly")) {
      assocAssembly = commentsFilePath;
    } else if (commentsFilePath.endsWith(".gen.gb")) {
      assocAssembly = commentsFilePath.substring(0, commentsFilePath.length - 7) + ".assembly";
    }
    const isCurrentFileAffected = commentsFilePath === currentFilePath || assocAssembly && assocAssembly === currentFilePath;
    if (isCurrentFileAffected) {
      window.dispatchEvent(
        new CustomEvent("file-comments-updating", {
          detail: {
            path: currentFilePath,
            comments: newComments
          }
        })
      );
    }
    window.isInventoryToggling = true;
    try {
      const encodedPath = commentsFilePath.split("/").map(encodeURIComponent).join("/");
      const response = await axios.post(`/api/file/comments/${encodedPath}`, { comment: fullComment });
      if (isCurrentFileAffected) {
        window.dispatchEvent(
          new CustomEvent("file-reverted", {
            detail: {
              path: currentFilePath,
              build_output: response.data.build_output,
              alignment_output: response.data.alignment_output,
              squelchPopup: true
            }
          })
        );
      }
    } catch (error) {
      console.error("Error adding comment:", error);
      window.toastr?.error("Failed to add comment.");
      setFileComments(fileComments);
      setOptimisticHasComments((prev) => ({ ...prev, [commentsFilePath]: fileComments.length > 0 }));
      if (isCurrentFileAffected) {
        window.dispatchEvent(
          new CustomEvent("file-save-failed", { detail: { path: currentFilePath } })
        );
      }
    } finally {
      await fetchFiles();
      window.isInventoryToggling = false;
    }
  };
  const handleDeleteComment = async (index) => {
    if (!commentsFilePath) return;
    const newComments = fileComments.filter((_, i) => i !== index);
    setFileComments(newComments);
    setOptimisticHasComments((prev) => ({ ...prev, [commentsFilePath]: newComments.length > 0 }));
    let assocAssembly = null;
    if (commentsFilePath.endsWith(".assembly")) {
      assocAssembly = commentsFilePath;
    } else if (commentsFilePath.endsWith(".gen.gb")) {
      assocAssembly = commentsFilePath.substring(0, commentsFilePath.length - 7) + ".assembly";
    }
    const isCurrentFileAffected = commentsFilePath === currentFilePath || assocAssembly && assocAssembly === currentFilePath;
    if (isCurrentFileAffected) {
      window.dispatchEvent(
        new CustomEvent("file-comments-updating", {
          detail: {
            path: currentFilePath,
            comments: newComments
          }
        })
      );
    }
    window.isInventoryToggling = true;
    try {
      const encodedPath = commentsFilePath.split("/").map(encodeURIComponent).join("/");
      const response = await axios.delete(`/api/file/comments/${encodedPath}`, { data: { index } });
      if (isCurrentFileAffected) {
        window.dispatchEvent(
          new CustomEvent("file-reverted", {
            detail: {
              path: currentFilePath,
              build_output: response.data.build_output,
              alignment_output: response.data.alignment_output,
              squelchPopup: true
            }
          })
        );
      }
    } catch (error) {
      console.error("Error deleting comment:", error);
      window.toastr?.error("Failed to delete comment.");
      setFileComments(fileComments);
      setOptimisticHasComments((prev) => ({ ...prev, [commentsFilePath]: fileComments.length > 0 }));
      if (isCurrentFileAffected) {
        window.dispatchEvent(
          new CustomEvent("file-save-failed", { detail: { path: currentFilePath } })
        );
      }
    } finally {
      await fetchFiles();
      window.isInventoryToggling = false;
    }
  };
  useEffect(() => {
    registerFileContextMenuHandler(handleContextMenuInternal);
    window.showFileExplorerContextMenu = (e, fileItem) => {
      handleContextMenuInternal(e, fileItem);
    };
    return () => {
      delete window.showFileExplorerContextMenu;
    };
  }, [handleContextMenuInternal]);
  const handleNodeContextMenu = (node, path, e) => {
    e.stopPropagation();
    explorerRef.current?.focus();
    const fileItem = node.nodeData;
    if (!fileItem) return;
    if (fileItem.isNewUnsaved) {
      e.preventDefault();
      window.dispatchEvent(
        new CustomEvent("file-move-check", {
          detail: { path: fileItem.path, callback: () => {
          } }
        })
      );
      return;
    }
    let itemsToUse = [fileItem];
    if (selectedPaths.includes(fileItem.path)) {
      itemsToUse = selectedPaths.map((p) => p === fileItem.path ? fileItem : findItemByPath(rawFiles, p)).filter(Boolean);
    } else {
      setSelectedPaths([fileItem.path]);
    }
    if (itemsToUse.length === 0) return;
    handleContextMenuInternal(e, itemsToUse, {
      canGitAdd: node.canGitAdd,
      canGitUnstage: node.canGitUnstage,
      canGitRevert: node.canGitRevert
    });
  };
  const handleEmptySpaceContextMenu = useCallback((e) => {
    const isPinned = pinnedFolder && pinnedFolder !== "." && pinnedFolder !== "projects";
    const targetPath = isPinned ? pinnedFolder : ".";
    const folderItem = isPinned ? findItemByPath(rawFiles, targetPath) || { path: targetPath, type: "directory", name: targetPath.split("/").pop() } : { path: ".", name: "projects", type: "directory" };
    handleContextMenuInternal(e, folderItem);
  }, [handleContextMenuInternal, pinnedFolder, rawFiles]);
  const getIsExplorerFocused = useCallback(() => {
    const activeEl = document.activeElement;
    if (!activeEl) return true;
    const activeTag = activeEl.tagName?.toLowerCase();
    if (activeEl.isContentEditable || activeTag === "input" || activeTag === "textarea") {
      return false;
    }
    if (document.querySelector('[data-test="monaco-selection-length"]')) {
      return false;
    }
    const isMonacoFocused = !!document.querySelector(".monaco-editor.focused") || !!document.querySelector(
      ".monaco-editor, .monaco-diff-editor, .ove-monaco-status-bar, .ove-yaml-toolbar, .ove-yaml-header"
    )?.contains(activeEl) || !!activeEl?.closest?.(
      ".monaco-editor, .monaco-diff-editor, .ove-monaco-status-bar, .ove-yaml-toolbar, .ove-yaml-header"
    );
    if (isMonacoFocused) {
      return false;
    }
    const selection = window.getSelection();
    if (selection && !selection.isCollapsed && selection.toString().length > 0) {
      if (!explorerRef.current || !explorerRef.current.contains(activeEl)) {
        return false;
      }
    }
    if (explorerRef.current && explorerRef.current.contains(activeEl)) {
      return true;
    }
    const isTableFocused = !!document.querySelector(".project-files-table-view")?.contains(activeEl) || !!activeEl?.closest?.(".project-files-table-view");
    if (isTableFocused) {
      return false;
    }
    const isOVEFocused = !!document.querySelector(
      ".veEditor, .veRowView, .veCircularView, .veLinearView, .alignmentView"
    )?.contains(activeEl) || !!activeEl?.closest?.(
      ".veEditor, .veRowView, .veCircularView, .veLinearView, .alignmentView"
    );
    if (isOVEFocused) {
      return false;
    }
    const isDialogFocused = !!document.querySelector(".bp3-dialog-container, .bp3-overlay")?.contains(activeEl) || !!activeEl?.closest?.(".bp3-dialog-container, .bp3-overlay");
    if (isDialogFocused) {
      return false;
    }
    return true;
  }, []);
  useEffect(() => {
    const handleKeyDown2 = (e) => {
      if (e.altKey && e.key.toLowerCase() === "c") {
        if (!isGitCommitDialogOpen && isAnythingCommittable) {
          e.preventDefault();
          setIsGitCommitDialogOpen(true);
          return;
        }
      }
      if (e.altKey && e.key.toLowerCase() === "p") {
        e.preventDefault();
        const toggleBtn = Array.from(document.querySelectorAll("button")).find(
          (b) => b.textContent.includes("Project Files") && (b.textContent.includes("Hide") || b.textContent.includes("Show"))
        );
        if (toggleBtn) {
          toggleBtn.click();
          return;
        }
      }
      if (e.altKey && (e.key === "?" || e.key === "/")) {
        e.preventDefault();
        setIsHelpDialogOpen(true);
        return;
      }
      if (e.altKey && (e.key?.toLowerCase() === "n" || e.code === "KeyN")) {
        e.preventDefault();
        openNewItemDialog();
        return;
      }
      if (e.key === "Delete") {
        if (!getIsExplorerFocused()) return;
        if (selectedPaths.length > 0 && !isDeleteDialogOpen && rawFiles) {
          const items = selectedPaths.map((path) => findItemByPath(rawFiles, path)).filter(Boolean);
          if (items.length > 0) {
            setItemsToDelete(items);
            setIsDeleteDialogOpen(true);
          }
        }
      }
      if (e.key === "Enter" && isDeleteDialogOpen && !isDeleting) {
        e.preventDefault();
        handleDeleteConfirm();
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "x") {
        if (!getIsExplorerFocused()) return;
        if (selectedPaths.length > 0 && rawFiles) {
          const items = selectedPaths.map((path) => findItemByPath(rawFiles, path)).filter((item) => item);
          if (items.length > 0) {
            setCopiedItems(items);
            setIsCut(true);
            const message = items.length === 1 ? `Cut ${items[0].name}` : `Cut ${items.length} items`;
            window.toastr?.success(message);
            const names = items.map((i) => i.name).join("\n");
            navigator.clipboard?.writeText(names);
          }
        }
      }
      if ((e.ctrlKey || e.metaKey) && (e.key?.toLowerCase() === "c" || e.code === "KeyC")) {
        if (e.defaultPrevented || !getIsExplorerFocused()) return;
        if (!selectedPaths || selectedPaths.length === 0) {
          console.warn("[FileExplorer Ctrl+C] Ignored because selectedPaths is empty.");
          return;
        }
        if (!rawFiles) {
          console.warn("[FileExplorer Ctrl+C] Ignored because rawFiles is null/undefined.");
          return;
        }
        const allItems = selectedPaths.map((path) => findItemByPath(rawFiles, path)).filter((item) => item);
        console.log("[FileExplorer Ctrl+C] Resolved items for selectedPaths:", {
          selectedPaths,
          resolvedItemsCount: allItems.length,
          allItems
        });
        const restricted = allItems.filter((i) => i.path.endsWith("sam.json") || i.path.endsWith("jbrowse.zip"));
        const items = allItems.filter((i) => !i.path.endsWith("sam.json") && !i.path.endsWith("jbrowse.zip"));
        if (restricted.length > 0 && items.length === 0) {
          console.warn("[FileExplorer Ctrl+C] All selected items are restricted.");
          window.toastr?.error(`Copying ${restricted.length > 1 ? "these files" : restricted[0].path.split("/").pop()} is not allowed`);
          return;
        }
        if (items.length > 0) {
          console.log("[FileExplorer Ctrl+C] Successfully copied items to clipboard:", items);
          setCopiedItems(items);
          setIsCut(false);
          const message = items.length === 1 ? `Copied ${items[0].name}` : `Copied ${items.length} items`;
          window.toastr?.success(message);
          const names = items.map((i) => i.name || (i.path ? i.path.split("/").pop() : "")).join("\n");
          navigator.clipboard?.writeText(names);
        } else {
          console.warn("[FileExplorer Ctrl+C] No valid items could be resolved from selectedPaths:", selectedPaths);
        }
      }
      if ((e.ctrlKey || e.metaKey) && e.key === "v") {
        if (!getIsExplorerFocused()) return;
        const { copiedItems: currentCopiedItems } = getFileClipboard();
        const itemsToPaste = currentCopiedItems && currentCopiedItems.length > 0 ? currentCopiedItems : copiedItems;
        if (itemsToPaste && itemsToPaste.length > 0) {
          e.preventDefault();
          let targetDir = ".";
          if (lastClickedPath && rawFiles) {
            const selectedItem = findItemByPath(rawFiles, lastClickedPath);
            if (selectedItem) {
              targetDir = selectedItem.type === "directory" ? selectedItem.path : selectedItem.path.split("/").slice(0, -1).join("/") || ".";
            }
          }
          handlePaste({ path: targetDir });
        }
      }
      if (e.key === "F2") {
        if (!getIsExplorerFocused()) return;
        if (selectedPaths.length === 1 && rawFiles) {
          const item = findItemByPath(rawFiles, selectedPaths[0]);
          if (item) {
            e.preventDefault();
            setRenamingState(item.path, item.name, "explorer");
          }
        }
      }
      if (e.altKey && e.key.toLowerCase() === "u") {
        if (!getIsExplorerFocused()) return;
        if (lastClickedPath && rawFiles) {
          const item = findItemByPath(rawFiles, lastClickedPath);
          if (item) {
            e.preventDefault();
            const folderPath = item.type === "directory" ? item.path : item.path.split("/").slice(0, -1).join("/") || ".";
            handleImportSequence(folderPath);
          }
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown2);
    return () => window.removeEventListener("keydown", handleKeyDown2);
  }, [
    selectedPaths,
    rawFiles,
    nodes,
    copiedItems,
    isDeleting,
    isDeleteDialogOpen,
    lastClickedPath,
    allVisiblePaths,
    getFileUrl,
    handlePaste,
    setIsCut,
    handleImportSequence,
    isGitCommitDialogOpen,
    isGitDiffDialogOpen,
    isAnythingCommittable,
    openNewItemDialog,
    getIsExplorerFocused
  ]);
  const renderItem = useCallback((index, key) => {
    const node = flattenedNodes[index];
    if (!node) return null;
    return /* @__PURE__ */ jsx(
      "div",
      {
        "data-testid": `file-tree-row-${node.nodeData?.path || node.id}`,
        "data-file-tree-row": node.nodeData?.path || node.id,
        "data-node-id": `node-${node.id}`,
        "data-row-testid": `file-tree-row-${node.nodeData?.path || node.id}`,
        "data-file-path": node.nodeData?.path || "",
        className: classNames(Classes.TREE_NODE, {
          [Classes.TREE_NODE_SELECTED]: node.isSelected,
          [Classes.TREE_NODE_EXPANDED]: node.isExpanded
        }),
        style: { height: "36px", overflow: "hidden" },
        onMouseDown: (e) => {
          if (e.target.tagName !== "INPUT" && e.target.tagName !== "TEXTAREA") {
            explorerRef.current?.focus();
          }
        },
        onContextMenu: (e) => handleNodeContextMenu(node, [], e),
        onClick: (e) => handleNodeClick(node, [], e),
        onDoubleClick: (e) => handleNodeDoubleClick(node, e),
        children: /* @__PURE__ */ jsxs(
          "div",
          {
            "data-testid": `node-${node.id}`,
            className: Classes.TREE_NODE_CONTENT,
            style: {
              paddingLeft: node.level * 20,
              height: "36px",
              alignItems: "center",
              paddingTop: "0px",
              paddingBottom: "0px"
            },
            children: [
              node.hasCaret ? /* @__PURE__ */ jsx(
                Icon,
                {
                  icon: node.isExpanded ? "chevron-down" : "chevron-right",
                  className: Classes.TREE_NODE_CARET,
                  onClick: (e) => {
                    e.stopPropagation();
                    handleNodeExpand(node);
                  }
                }
              ) : /* @__PURE__ */ jsx("span", { className: Classes.TREE_NODE_CARET_NONE }),
              node.icon && /* @__PURE__ */ jsx("span", { className: Classes.TREE_NODE_ICON, children: node.icon }),
              /* @__PURE__ */ jsx(
                "span",
                {
                  className: Classes.TREE_NODE_LABEL,
                  children: node.label
                }
              )
            ]
          }
        )
      },
      key
    );
  }, [flattenedNodes, handleNodeContextMenu, handleNodeClick, handleNodeDoubleClick, handleNodeExpand]);
  return /* @__PURE__ */ jsxs(
    "div",
    {
      ref: explorerRef,
      tabIndex: 0,
      "data-testid": "file-explorer",
      onMouseDown: (e) => {
        if (e.target.tagName !== "INPUT" && e.target.tagName !== "TEXTAREA") {
          explorerRef.current?.focus();
        }
      },
      onKeyDown: handleKeyDown,
      onPaste: handlePasteFromClipboard,
      onDragOver: (e) => handleDragOver(e, { type: "directory", path: "." }),
      onDrop: (e) => handleDrop(e, { type: "directory", path: "." }),
      style: {
        width: "100%",
        flexGrow: 1,
        minHeight: 0,
        display: "flex",
        flexDirection: "column",
        borderRight: "1px solid #ccc",
        backgroundColor: "#f5f8fa",
        outline: "none"
      },
      children: [
        /* @__PURE__ */ jsxs("div", { style: { padding: "10px", flexShrink: 0 }, children: [
          /* @__PURE__ */ jsxs(
            "div",
            {
              style: {
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                marginBottom: 10,
                flexWrap: "wrap",
                gap: 10
              },
              children: [
                /* @__PURE__ */ jsx(
                  "h4",
                  {
                    className: Classes.HEADING,
                    style: { margin: 0, whiteSpace: "nowrap", flexShrink: 1 },
                    children: "Project Files"
                  }
                ),
                /* @__PURE__ */ jsxs("div", { style: { display: "flex", gap: 2, flexWrap: "wrap" }, children: [
                  /* @__PURE__ */ jsx(
                    Button,
                    {
                      minimal: true,
                      small: true,
                      icon: "wrench",
                      loading: isBuilding,
                      title: "Run Build",
                      onClick: () => {
                        window.dispatchEvent(new CustomEvent("run-assembly-build"));
                      }
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    Button,
                    {
                      minimal: true,
                      small: true,
                      icon: "history",
                      title: "Recent Files",
                      onClick: () => {
                        window.location.hash = getFileUrl("projects", { extraParams: { view: "recent" } });
                      }
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    Button,
                    {
                      minimal: true,
                      small: true,
                      icon: "collapse-all",
                      title: "Collapse All",
                      onClick: () => setExpandedIds({})
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    Popover,
                    {
                      position: Position.BOTTOM_LEFT,
                      content: /* @__PURE__ */ jsxs(Menu, { children: [
                        /* @__PURE__ */ jsx(
                          MenuItem,
                          {
                            icon: "document",
                            text: "New Sequence",
                            "data-testid": "toolbar-new-sequence",
                            onClick: () => {
                              setTargetFolderPath(".");
                              setNewFileType("sequence");
                              setIsCreateFileDialogOpen(true);
                            }
                          }
                        ),
                        /* @__PURE__ */ jsx(
                          MenuItem,
                          {
                            icon: "code-block",
                            text: "New Assembly",
                            "data-testid": "toolbar-new-assembly",
                            onClick: () => {
                              setTargetFolderPath(".");
                              setNewFileType("assembly");
                              setIsCreateFileDialogOpen(true);
                            }
                          }
                        ),
                        /* @__PURE__ */ jsx(
                          MenuItem,
                          {
                            icon: "folder-new",
                            text: "New Folder",
                            "data-testid": "toolbar-new-folder",
                            onClick: () => {
                              setTargetFolderPath(".");
                              setIsFolderDialogOpen(true);
                            }
                          }
                        )
                      ] }),
                      children: /* @__PURE__ */ jsx(Button, { minimal: true, small: true, icon: "add", title: "New...", "data-testid": "toolbar-add-new" })
                    }
                  ),
                  /* @__PURE__ */ jsx(Tooltip, { content: "Git Commit", position: Position.BOTTOM, children: /* @__PURE__ */ jsx(
                    Button,
                    {
                      minimal: true,
                      small: true,
                      icon: "git-commit",
                      "data-testid": "toolbar-git-commit",
                      onClick: () => setIsGitCommitDialogOpen(true),
                      disabled: !isAnythingCommittable,
                      style: { color: isAnythingCommittable ? "#107310" : "inherit" }
                    }
                  ) }),
                  /* @__PURE__ */ jsx(
                    Popover,
                    {
                      position: Position.BOTTOM_LEFT,
                      content: /* @__PURE__ */ jsxs(Menu, { children: [
                        /* @__PURE__ */ jsx(
                          MenuItem,
                          {
                            icon: "changes",
                            text: "Git Status / Fetch",
                            onClick: handleGitStatus
                          }
                        ),
                        isAnythingCommittable && /* @__PURE__ */ jsx(
                          MenuItem,
                          {
                            icon: "git-commit",
                            text: "Git Commit",
                            "data-testid": "git-commit-menu-item",
                            onClick: () => setIsGitCommitDialogOpen(true)
                          }
                        ),
                        Object.values(gitStatus || {}).some(isUnstaged) && /* @__PURE__ */ jsx(
                          MenuItem,
                          {
                            icon: "plus",
                            text: "Git Add All",
                            onClick: handleGitAddAll
                          }
                        ),
                        /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "stretch" }, children: [
                          /* @__PURE__ */ jsx("div", { style: { flexGrow: 1 }, children: /* @__PURE__ */ jsx(
                            Tooltip,
                            {
                              content: "All commits already pushed",
                              disabled: gitAheadCount > 0 || gitBehindCount > 0,
                              position: Position.RIGHT,
                              hoverOpenDelay: 800,
                              targetTagName: "div",
                              children: /* @__PURE__ */ jsx(
                                MenuItem,
                                {
                                  icon: "refresh",
                                  text: `Sync Changes${gitAheadCount > 0 || gitBehindCount > 0 ? ` (${[
                                    gitAheadCount > 0 ? `${gitAheadCount} ahead` : null,
                                    gitBehindCount > 0 ? `${gitBehindCount} behind` : null
                                  ].filter(Boolean).join(", ")})` : ""}`,
                                  disabled: gitAheadCount === 0 && gitBehindCount === 0,
                                  onClick: handleGitSync
                                }
                              )
                            }
                          ) }),
                          /* @__PURE__ */ jsx(
                            Popover,
                            {
                              interactionKind: "hover",
                              hoverOpenDelay: 100,
                              content: /* @__PURE__ */ jsxs(Menu, { children: [
                                /* @__PURE__ */ jsx(
                                  MenuItem,
                                  {
                                    icon: "git-pull",
                                    text: "Git Pull",
                                    onClick: handleGitPull
                                  }
                                ),
                                /* @__PURE__ */ jsx("div", { children: /* @__PURE__ */ jsx(
                                  Tooltip,
                                  {
                                    content: "All commits already pushed",
                                    disabled: gitAheadCount > 0,
                                    position: Position.RIGHT,
                                    hoverOpenDelay: 800,
                                    targetTagName: "div",
                                    children: /* @__PURE__ */ jsx(
                                      MenuItem,
                                      {
                                        icon: "git-push",
                                        text: "Git Push",
                                        disabled: gitAheadCount === 0,
                                        onClick: handleGitPush
                                      }
                                    )
                                  }
                                ) }, "push-item")
                              ] }),
                              position: Position.RIGHT_TOP,
                              children: /* @__PURE__ */ jsx(
                                Button,
                                {
                                  minimal: true,
                                  icon: "caret-right",
                                  style: {
                                    height: "100%",
                                    minWidth: "30px",
                                    padding: 0,
                                    margin: 0,
                                    borderRadius: 0,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    borderLeft: "1px solid rgba(16, 22, 26, 0.15)"
                                  }
                                }
                              )
                            }
                          )
                        ] }, "sync")
                      ] }),
                      children: /* @__PURE__ */ jsx(
                        Button,
                        {
                          minimal: true,
                          small: true,
                          icon: /* @__PURE__ */ jsxs("div", { style: { position: "relative" }, children: [
                            /* @__PURE__ */ jsx(Icon, { icon: "git-branch" }),
                            (Object.values(gitStatus || {}).some(isUnstaged) || gitAheadCount > 0 || gitBehindCount > 0) && /* @__PURE__ */ jsx(
                              "div",
                              {
                                style: {
                                  position: "absolute",
                                  top: -2,
                                  right: -2,
                                  width: 8,
                                  height: 8,
                                  borderRadius: "50%",
                                  backgroundColor: "#DB3737",
                                  border: "1px solid white"
                                }
                              }
                            )
                          ] }),
                          title: "Git Tasks"
                        }
                      )
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    Button,
                    {
                      minimal: true,
                      small: true,
                      icon: "help",
                      title: "Tips",
                      onClick: () => setIsHelpDialogOpen(true)
                    }
                  )
                ] })
              ]
            }
          ),
          /* @__PURE__ */ jsxs("div", { style: { position: "relative" }, children: [
            /* @__PURE__ */ jsx(
              InputGroup,
              {
                "data-testid": "search-input",
                leftIcon: "search",
                placeholder: "Search by name/desc/DNA/AA...",
                value: searchQuery,
                onChange: (e) => {
                  const val = e.target.value;
                  setSearchQuery(val);
                  if (val === "" && searchQuery !== "") {
                    setExpandedIds(getParentsOfPath(currentFilePath));
                  }
                },
                onKeyDown: handleSearchKeyDown,
                inputRef: searchInputRef,
                rightElement: /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center" }, children: [
                  isSearchingSequence && /* @__PURE__ */ jsx(Spinner, { size: 16, style: { marginRight: 4 } }),
                  searchQuery ? /* @__PURE__ */ jsxs(Fragment, { children: [
                    /* @__PURE__ */ jsx(
                      "button",
                      {
                        className: Classes.BUTTON + " " + Classes.MINIMAL,
                        title: "View search results in table view",
                        "data-testid": "view-search-table-button",
                        onClick: () => {
                          window.location.hash = getFileUrl("projects", {
                            extraParams: { view: "table", search: searchQuery }
                          });
                        },
                        children: /* @__PURE__ */ jsx(Icon, { icon: "chevron-right" })
                      }
                    ),
                    /* @__PURE__ */ jsx(
                      "button",
                      {
                        className: Classes.BUTTON + " " + Classes.MINIMAL,
                        onClick: () => {
                          setSearchQuery("");
                          setExpandedIds(getParentsOfPath(currentFilePath));
                        },
                        children: /* @__PURE__ */ jsx(Icon, { icon: "cross" })
                      }
                    )
                  ] }) : void 0
                ] })
              }
            ),
            ghostSuggestion && !suggestionDismissed && /* @__PURE__ */ jsxs(
              "div",
              {
                "data-testid": "search-ghost-suggestion",
                "aria-hidden": "true",
                style: {
                  position: "absolute",
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  pointerEvents: "none",
                  display: "flex",
                  alignItems: "center",
                  paddingLeft: "30px",
                  paddingRight: "30px",
                  fontSize: "14px",
                  fontFamily: "-apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif",
                  whiteSpace: "pre",
                  overflow: "hidden",
                  color: "transparent",
                  zIndex: 2
                },
                children: [
                  /* @__PURE__ */ jsx("span", { children: searchQuery }),
                  /* @__PURE__ */ jsx("span", { style: { color: "#a0a0a0", opacity: 0.85 }, "data-testid": "ghost-suffix", children: ghostSuggestion.suffix })
                ]
              }
            )
          ] }),
          pinnedFolder && /* @__PURE__ */ jsxs("div", { style: { padding: "4px 8px", backgroundColor: "rgba(16, 107, 163, 0.1)", borderBottom: "1px solid #ddd", display: "flex", justifyContent: "space-between", alignItems: "center" }, children: [
            /* @__PURE__ */ jsxs("div", { style: { fontSize: "0.85em", fontWeight: "bold", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }, title: pinnedFolder, children: [
              "Pinned: ",
              pinnedFolder
            ] }),
            /* @__PURE__ */ jsx(Button, { icon: "cross", minimal: true, small: true, onClick: () => setPinnedFolder(null) })
          ] })
        ] }),
        /* @__PURE__ */ jsx(
          "div",
          {
            ref: scrollContainerRef,
            "data-testid": "scroll-container",
            onScroll: handleScroll,
            onContextMenu: handleEmptySpaceContextMenu,
            style: {
              flexGrow: 1,
              overflowY: "scroll",
              // Always show vertical scrollbar to stabilize measurement
              overflowX: "auto",
              outline: "none",
              position: "relative"
            },
            children: loading ? /* @__PURE__ */ jsx("div", { children: "Loading..." }) : /* @__PURE__ */ jsx("div", { style: { minWidth: "max-content", width: "100%", paddingBottom: "10px" }, children: /* @__PURE__ */ jsx(
              ReactList,
              {
                ref: reactListRef,
                itemRenderer: renderItem,
                length: flattenedNodes.length,
                type: "uniform",
                itemSizeGetter: (index) => 36,
                threshold: 500
              }
            ) })
          }
        ),
        /* @__PURE__ */ jsxs(
          Dialog,
          {
            isOpen: isFolderDialogOpen,
            onClose: () => setIsFolderDialogOpen(false),
            title: "Create New Folder",
            icon: "folder-new",
            canOutsideClickClose: true,
            children: [
              /* @__PURE__ */ jsxs("div", { className: Classes.DIALOG_BODY, children: [
                /* @__PURE__ */ jsxs("p", { children: [
                  "Create a new folder in: ",
                  /* @__PURE__ */ jsx("b", { children: targetFolderPath })
                ] }),
                /* @__PURE__ */ jsx(
                  InputGroup,
                  {
                    autoFocus: true,
                    placeholder: "Folder name",
                    value: newFolderName,
                    onChange: (e) => setNewFolderName(e.target.value),
                    onKeyDown: (e) => {
                      if (e.key === "Enter") handleCreateFolder();
                    }
                  }
                )
              ] }),
              /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER, children: /* @__PURE__ */ jsxs("div", { className: Classes.DIALOG_FOOTER_ACTIONS, children: [
                /* @__PURE__ */ jsx(Button, { onClick: () => setIsFolderDialogOpen(false), children: "Cancel" }),
                /* @__PURE__ */ jsx(
                  Button,
                  {
                    intent: Intent.PRIMARY,
                    onClick: handleCreateFolder,
                    disabled: !newFolderName.trim(),
                    "data-testid": "create-folder-confirm",
                    children: "Create"
                  }
                )
              ] }) })
            ]
          }
        ),
        /* @__PURE__ */ jsxs(
          Dialog,
          {
            isOpen: isAddgeneDialogOpen,
            onClose: () => setIsAddgeneDialogOpen(false),
            title: "Import from Addgene",
            icon: "database",
            canOutsideClickClose: true,
            children: [
              /* @__PURE__ */ jsxs("div", { className: Classes.DIALOG_BODY, children: [
                /* @__PURE__ */ jsxs("p", { children: [
                  "Import Addgene plasmid into: ",
                  /* @__PURE__ */ jsx("b", { children: targetFolderPath })
                ] }),
                /* @__PURE__ */ jsx(
                  InputGroup,
                  {
                    autoFocus: true,
                    placeholder: "Addgene Plasmid ID, Sequence ID, or URL (e.g. 121875 or 498453)",
                    value: addgeneId,
                    onChange: (e) => setAddgeneId(e.target.value),
                    onKeyDown: (e) => {
                      if (e.key === "Enter") executeAddgeneImport();
                    }
                  }
                )
              ] }),
              /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER, children: /* @__PURE__ */ jsxs("div", { className: Classes.DIALOG_FOOTER_ACTIONS, children: [
                /* @__PURE__ */ jsx(Button, { onClick: () => setIsAddgeneDialogOpen(false), children: "Cancel" }),
                /* @__PURE__ */ jsx(
                  Button,
                  {
                    intent: Intent.PRIMARY,
                    onClick: executeAddgeneImport,
                    disabled: !addgeneId.trim() || isAddgeneImporting,
                    loading: isAddgeneImporting,
                    children: "Import"
                  }
                )
              ] }) })
            ]
          }
        ),
        /* @__PURE__ */ jsxs(
          Dialog,
          {
            isOpen: isDeleteDialogOpen,
            onClose: () => setIsDeleteDialogOpen(false),
            title: "Confirm Deletion",
            icon: "trash",
            canOutsideClickClose: true,
            "data-testid": "delete-dialog",
            children: [
              /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_BODY, children: itemsToDelete.length === 1 ? /* @__PURE__ */ jsxs("p", { children: [
                "Are you sure you want to delete ",
                /* @__PURE__ */ jsx("b", { children: itemsToDelete[0]?.name }),
                "?",
                itemsToDelete[0]?.type === "directory" && " This will delete all its contents."
              ] }) : /* @__PURE__ */ jsxs("p", { children: [
                "Are you sure you want to delete ",
                /* @__PURE__ */ jsxs("b", { children: [
                  itemsToDelete.length,
                  " items"
                ] }),
                "?"
              ] }) }),
              /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER, children: /* @__PURE__ */ jsxs("div", { className: Classes.DIALOG_FOOTER_ACTIONS, children: [
                /* @__PURE__ */ jsx(Button, { onClick: () => setIsDeleteDialogOpen(false), children: "Cancel" }),
                /* @__PURE__ */ jsx(
                  Button,
                  {
                    intent: Intent.DANGER,
                    onClick: handleDeleteConfirm,
                    loading: isDeleting,
                    children: "Delete"
                  }
                )
              ] }) })
            ]
          }
        ),
        /* @__PURE__ */ jsx(
          Dialog,
          {
            isOpen: isImporting,
            title: "Importing Sequences",
            canOutsideClickClose: false,
            canEscapeKeyClose: false,
            isCloseButtonShown: false,
            children: /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_BODY, children: /* @__PURE__ */ jsxs(
              "div",
              {
                style: {
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  padding: 20
                },
                children: [
                  /* @__PURE__ */ jsx(Spinner, { size: 50 }),
                  /* @__PURE__ */ jsx("div", { style: { marginTop: 15 }, children: "Processing files..." })
                ]
              }
            ) })
          }
        ),
        /* @__PURE__ */ jsxs(
          Dialog,
          {
            isOpen: isCreateFileDialogOpen,
            onClose: () => {
              if (!isFileCreating) {
                setIsCreateFileDialogOpen(false);
                setAssemblyComponents([]);
              }
            },
            title: newFileType === "sequence" ? "Create New Sequence" : "Create New Assembly",
            icon: newFileType === "sequence" ? "document" : "code-block",
            canOutsideClickClose: true,
            children: [
              /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_BODY, children: isFileCreating ? /* @__PURE__ */ jsxs(
                "div",
                {
                  style: {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    padding: 20
                  },
                  children: [
                    /* @__PURE__ */ jsx(Spinner, { size: 50 }),
                    /* @__PURE__ */ jsx("div", { style: { marginTop: 10 }, children: "Creating file..." })
                  ]
                }
              ) : /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsxs("p", { children: [
                  "Create a new ",
                  newFileType,
                  " in: ",
                  /* @__PURE__ */ jsx("b", { children: targetFolderPath })
                ] }),
                assemblyComponents && assemblyComponents.length > 0 && /* @__PURE__ */ jsxs("p", { children: [
                  "Selected pieces: ",
                  /* @__PURE__ */ jsx("b", { children: assemblyComponents.join(", ") })
                ] }),
                /* @__PURE__ */ jsx(
                  InputGroup,
                  {
                    autoFocus: true,
                    placeholder: "File name",
                    value: newFileName,
                    onChange: (e) => setNewFileName(e.target.value),
                    onKeyDown: (e) => {
                      if (e.key === "Enter" && newFileName.trim() && !isFileCreating)
                        handleCreateFile();
                    }
                  }
                ),
                newFileType === "sequence" && /* @__PURE__ */ jsx("div", { style: { marginTop: 15 }, children: /* @__PURE__ */ jsxs(
                  RadioGroup,
                  {
                    label: "Topology",
                    onChange: (e) => setNewFileCircular(e.target.value === "circular"),
                    selectedValue: newFileCircular ? "circular" : "linear",
                    inline: true,
                    children: [
                      /* @__PURE__ */ jsx(Radio, { label: "Linear", value: "linear" }),
                      /* @__PURE__ */ jsx(Radio, { label: "Circular", value: "circular" })
                    ]
                  }
                ) })
              ] }) }),
              !isFileCreating && /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER, children: /* @__PURE__ */ jsxs("div", { className: Classes.DIALOG_FOOTER_ACTIONS, children: [
                /* @__PURE__ */ jsx(
                  Button,
                  {
                    onClick: () => {
                      setIsCreateFileDialogOpen(false);
                      setAssemblyComponents([]);
                    },
                    children: "Cancel"
                  }
                ),
                /* @__PURE__ */ jsx(
                  Button,
                  {
                    intent: Intent.PRIMARY,
                    onClick: handleCreateFile,
                    disabled: !newFileName.trim(),
                    "data-testid": "create-file-confirm",
                    children: "Create"
                  }
                ),
                "            "
              ] }) })
            ]
          }
        ),
        /* @__PURE__ */ jsxs(
          Dialog,
          {
            isOpen: isNewItemDialogOpen,
            onClose: () => {
              if (!isNewItemCreating) {
                setIsNewItemDialogOpen(false);
              }
            },
            title: "Create New Item",
            icon: "add",
            canOutsideClickClose: true,
            style: { width: "480px", maxWidth: "90vw", alignSelf: "flex-start", marginTop: "20vh" },
            "data-testid": "new-item-dialog",
            children: [
              /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_BODY, style: { minHeight: "220px" }, children: isNewItemCreating ? /* @__PURE__ */ jsxs(
                "div",
                {
                  style: {
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    padding: 20
                  },
                  children: [
                    /* @__PURE__ */ jsx(Spinner, { size: 50 }),
                    /* @__PURE__ */ jsx("div", { style: { marginTop: 10 }, children: "Creating item..." })
                  ]
                }
              ) : /* @__PURE__ */ jsxs(Fragment, { children: [
                /* @__PURE__ */ jsx("div", { style: { marginBottom: 15 }, children: /* @__PURE__ */ jsxs(
                  RadioGroup,
                  {
                    label: "Item Type",
                    selectedValue: newItemType,
                    onChange: (e) => setNewItemType(e.target.value),
                    inline: true,
                    children: [
                      /* @__PURE__ */ jsx(
                        Radio,
                        {
                          label: "DNA Sequence",
                          value: "sequence",
                          "data-testid": "new-item-type-sequence"
                        }
                      ),
                      /* @__PURE__ */ jsx(
                        Radio,
                        {
                          label: "Assembly",
                          value: "assembly",
                          "data-testid": "new-item-type-assembly"
                        }
                      ),
                      /* @__PURE__ */ jsx(
                        Radio,
                        {
                          label: "Folder",
                          value: "folder",
                          "data-testid": "new-item-type-folder"
                        }
                      )
                    ]
                  }
                ) }),
                /* @__PURE__ */ jsx("div", { style: { marginBottom: 15 }, children: /* @__PURE__ */ jsxs("label", { className: Classes.LABEL, children: [
                  "Parent Folder",
                  /* @__PURE__ */ jsx("div", { style: { marginTop: 4 }, children: /* @__PURE__ */ jsx(
                    FolderPicker,
                    {
                      value: newItemTargetFolder,
                      onChange: setNewItemTargetFolder
                    }
                  ) })
                ] }) }),
                /* @__PURE__ */ jsx("div", { style: { marginBottom: 15 }, children: /* @__PURE__ */ jsxs("label", { className: Classes.LABEL, children: [
                  "Name",
                  /* @__PURE__ */ jsx(
                    InputGroup,
                    {
                      autoFocus: true,
                      value: newItemName,
                      onChange: (e) => setNewItemName(e.target.value),
                      onKeyDown: (e) => {
                        if (e.key === "Enter" && newItemName.trim() && !isNewItemCreating) {
                          handleCreateNewItem();
                        }
                      },
                      "data-testid": "new-item-name-input"
                    }
                  )
                ] }) }),
                newItemType === "sequence" && /* @__PURE__ */ jsxs("div", { style: { marginTop: 10, marginBottom: 15 }, children: [
                  /* @__PURE__ */ jsxs(
                    "div",
                    {
                      onClick: () => setIsNewItemAdvancedOpen((prev) => !prev),
                      style: {
                        display: "inline-flex",
                        alignItems: "center",
                        cursor: "pointer",
                        userSelect: "none",
                        color: "#5c7080",
                        fontSize: "0.9em",
                        fontWeight: 600,
                        gap: 4
                      },
                      "data-testid": "new-item-advanced-toggle",
                      children: [
                        /* @__PURE__ */ jsx(
                          Icon,
                          {
                            icon: isNewItemAdvancedOpen ? "chevron-down" : "chevron-right",
                            iconSize: 14
                          }
                        ),
                        /* @__PURE__ */ jsx("span", { children: "Advanced" })
                      ]
                    }
                  ),
                  /* @__PURE__ */ jsx(Collapse, { isOpen: isNewItemAdvancedOpen, children: /* @__PURE__ */ jsx("div", { style: { marginTop: 10, paddingLeft: 18 }, children: /* @__PURE__ */ jsxs(
                    RadioGroup,
                    {
                      label: "Topology",
                      onChange: (e) => setNewItemCircular(e.target.value === "circular"),
                      selectedValue: newItemCircular ? "circular" : "linear",
                      inline: true,
                      children: [
                        /* @__PURE__ */ jsx(
                          Radio,
                          {
                            label: "Linear",
                            value: "linear",
                            "data-testid": "new-item-linear"
                          }
                        ),
                        /* @__PURE__ */ jsx(
                          Radio,
                          {
                            label: "Circular",
                            value: "circular",
                            "data-testid": "new-item-circular"
                          }
                        )
                      ]
                    }
                  ) }) })
                ] }),
                newItemType === "assembly" && /* @__PURE__ */ jsxs("div", { style: { marginTop: 10, marginBottom: 15 }, children: [
                  /* @__PURE__ */ jsxs(
                    "div",
                    {
                      onClick: () => setIsNewItemAdvancedOpen((prev) => !prev),
                      style: {
                        display: "inline-flex",
                        alignItems: "center",
                        cursor: "pointer",
                        userSelect: "none",
                        color: "#5c7080",
                        fontSize: "0.9em",
                        fontWeight: 600,
                        gap: 4
                      },
                      "data-testid": "new-item-advanced-toggle",
                      children: [
                        /* @__PURE__ */ jsx(
                          Icon,
                          {
                            icon: isNewItemAdvancedOpen ? "chevron-down" : "chevron-right",
                            iconSize: 14
                          }
                        ),
                        /* @__PURE__ */ jsx("span", { children: "Advanced" })
                      ]
                    }
                  ),
                  /* @__PURE__ */ jsx(Collapse, { isOpen: isNewItemAdvancedOpen, children: /* @__PURE__ */ jsx("div", { style: { marginTop: 10, paddingLeft: 18 }, children: /* @__PURE__ */ jsxs("label", { className: Classes.LABEL, children: [
                    "Template Method",
                    /* @__PURE__ */ jsx("div", { style: { marginTop: 4 }, children: /* @__PURE__ */ jsx(
                      HTMLSelect,
                      {
                        value: newItemAssemblyMethod,
                        onChange: (e) => setNewItemAssemblyMethod(e.target.value),
                        fill: true,
                        "data-testid": "new-item-assembly-method-select",
                        options: [
                          { label: "Fragment Synthesis (fragment_synthesis)", value: "fragment_synthesis" },
                          { label: "Restriction Ligation (restriction)", value: "restriction" },
                          { label: "Golden Gate (golden_gate)", value: "golden_gate" },
                          { label: "Gateway Cloning (gateway)", value: "gateway" },
                          { label: "Genscript Restriction (genscript_restriction)", value: "genscript_restriction" },
                          { label: "Genscript Seamless (genscript_seamless)", value: "genscript_seamless" },
                          { label: "Mutagenesis (mutagenesis)", value: "mutagenesis" },
                          { label: "Twist Clonal Synthesis (twist_clonal_synthesis)", value: "twist_clonal_synthesis" }
                        ]
                      }
                    ) })
                  ] }) }) })
                ] })
              ] }) }),
              /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER, children: /* @__PURE__ */ jsxs("div", { className: Classes.DIALOG_FOOTER_ACTIONS, children: [
                /* @__PURE__ */ jsx(
                  Button,
                  {
                    onClick: () => setIsNewItemDialogOpen(false),
                    disabled: isNewItemCreating,
                    "data-testid": "new-item-cancel-button",
                    children: "Cancel"
                  }
                ),
                /* @__PURE__ */ jsx(
                  Button,
                  {
                    intent: Intent.PRIMARY,
                    onClick: handleCreateNewItem,
                    disabled: !newItemName.trim() || isNewItemCreating,
                    loading: isNewItemCreating,
                    "data-testid": "new-item-create-button",
                    children: "Create"
                  }
                )
              ] }) })
            ]
          }
        ),
        /* @__PURE__ */ jsxs(
          Alert,
          {
            canEscapeKeyCancel: !isReverting,
            canOutsideClickCancel: !isReverting,
            cancelButtonText: "Cancel",
            confirmButtonText: "Revert",
            icon: "history",
            intent: Intent.WARNING,
            loading: isReverting,
            isOpen: isRevertAlertOpen,
            onCancel: handleRevertCancel,
            onConfirm: handleRevertConfirm,
            children: [
              /* @__PURE__ */ jsxs("p", { children: [
                "Are you sure you want to revert changes to ",
                /* @__PURE__ */ jsx("b", { children: itemToRevert }),
                "?"
              ] }),
              /* @__PURE__ */ jsx("p", { children: "This cannot be undone." })
            ]
          }
        ),
        /* @__PURE__ */ jsxs(
          Dialog,
          {
            isOpen: isGitStatusRawDialogOpen,
            onClose: () => setIsGitStatusRawDialogOpen(false),
            title: "Git Status",
            icon: "info-sign",
            canOutsideClickClose: true,
            style: { width: "800px" },
            "data-testid": "git-status-dialog",
            children: [
              /* @__PURE__ */ jsx(
                "div",
                {
                  className: Classes.DIALOG_BODY,
                  style: { minHeight: "200px", display: "flex", flexDirection: "column" },
                  children: isGitOperating ? /* @__PURE__ */ jsxs("div", { style: { padding: 40, textAlign: "center", flexGrow: 1, display: "flex", flexDirection: "column", justifyContent: "center" }, children: [
                    /* @__PURE__ */ jsx(Spinner, { size: Spinner.SIZE_LARGE }),
                    /* @__PURE__ */ jsx("div", { style: { marginTop: 20, fontSize: "16px" }, children: "Fetching status..." })
                  ] }) : /* @__PURE__ */ jsx("pre", { style: {
                    backgroundColor: "#f5f5f5",
                    padding: "15px",
                    borderRadius: "4px",
                    fontFamily: "monospace",
                    whiteSpace: "pre-wrap",
                    maxHeight: "600px",
                    overflow: "auto",
                    flexGrow: 1,
                    margin: 0
                  }, children: renderClickableGitStatus(gitStatusRaw) })
                }
              ),
              /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER, children: /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER_ACTIONS, children: /* @__PURE__ */ jsx(Button, { onClick: () => setIsGitStatusRawDialogOpen(false), children: "Close" }) }) })
            ]
          }
        ),
        /* @__PURE__ */ jsxs(
          Dialog,
          {
            isOpen: isPathDialogOpen,
            onClose: () => setIsPathDialogOpen(false),
            title: "File Details",
            icon: "info-sign",
            canOutsideClickClose: true,
            children: [
              /* @__PURE__ */ jsxs(
                "div",
                {
                  className: Classes.DIALOG_BODY,
                  onKeyDown: (e) => {
                    if (e.key === "Enter") {
                      const activeTag = document.activeElement.tagName.toLowerCase();
                      if (activeTag === "input" || activeTag === "textarea") return;
                      e.preventDefault();
                      e.stopPropagation();
                      setIsPathDialogOpen(false);
                    }
                  },
                  children: [
                    /* @__PURE__ */ jsxs("p", { children: [
                      "Full path of ",
                      /* @__PURE__ */ jsx("b", { children: selectedPath.split("/").pop() }),
                      ":"
                    ] }),
                    /* @__PURE__ */ jsx(
                      "pre",
                      {
                        style: {
                          padding: "10px",
                          backgroundColor: "#eee",
                          borderRadius: "4px",
                          whiteSpace: "pre-wrap",
                          wordBreak: "break-all",
                          marginBottom: "20px"
                        },
                        children: selectedPath
                      }
                    ),
                    /* @__PURE__ */ jsxs("div", { style: { marginBottom: "20px", display: "flex", flexWrap: "wrap", gap: "20px" }, children: [
                      /* @__PURE__ */ jsxs("div", { children: [
                        /* @__PURE__ */ jsx("p", { style: { fontWeight: "bold", marginBottom: "5px" }, children: "Date Created:" }),
                        /* @__PURE__ */ jsx("div", { style: { fontSize: "0.9em", color: "#666" }, children: isLoadingMetadata ? /* @__PURE__ */ jsx(Spinner, { size: 16 }) : dateCreated != null ? new Date(dateCreated * 1e3).toLocaleString() : "Unknown" })
                      ] }),
                      /* @__PURE__ */ jsxs("div", { children: [
                        /* @__PURE__ */ jsx("p", { style: { fontWeight: "bold", marginBottom: "5px" }, children: "Last Modified:" }),
                        /* @__PURE__ */ jsx("div", { style: { fontSize: "0.9em", color: "#666" }, children: isLoadingMetadata ? /* @__PURE__ */ jsx(Spinner, { size: 16 }) : dateModified != null ? new Date(dateModified * 1e3).toLocaleString() : "Unknown" })
                      ] }),
                      ALL_EDITABLE_EXTENSIONS.some((ext) => rawNodeData?.name?.endsWith(ext)) && /* @__PURE__ */ jsxs("div", { children: [
                        /* @__PURE__ */ jsx("p", { style: { fontWeight: "bold", marginBottom: "5px" }, children: "Sequence Length:" }),
                        /* @__PURE__ */ jsx("div", { style: { fontSize: "0.9em", color: "#666" }, children: isLoadingMetadata ? /* @__PURE__ */ jsx(Spinner, { size: 16 }) : sequenceLength != null ? `${sequenceLength.toLocaleString()} bp` : "Unknown" })
                      ] })
                    ] }),
                    (rawNodeData?.name?.endsWith(".gb") || rawNodeData?.name?.endsWith(".gbk") || rawNodeData?.name?.endsWith(".genbank") || rawNodeData?.name?.endsWith(".assembly")) && /* @__PURE__ */ jsxs(Fragment, { children: [
                      (rawNodeData?.name?.endsWith(".gb") || rawNodeData?.name?.endsWith(".gbk") || rawNodeData?.name?.endsWith(".genbank")) && /* @__PURE__ */ jsxs("div", { style: { marginBottom: "15px" }, children: [
                        /* @__PURE__ */ jsx("p", { style: { marginBottom: "5px" }, children: "LOCUS:" }),
                        /* @__PURE__ */ jsx(
                          InputGroup,
                          {
                            fill: true,
                            "data-testid": "locus-input",
                            value: selectedLocus,
                            onChange: (e) => {
                              const val = e.target.value.replace(/[^a-zA-Z0-9_]/g, "").slice(0, 16);
                              locusRef.current = val;
                              setSelectedLocus(val);
                            },
                            onKeyDown: (e) => {
                              if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                                e.preventDefault();
                                handleSaveDescription();
                              }
                            }
                          }
                        ),
                        rawNodeData?.name?.endsWith(".gen.gb") && /* @__PURE__ */ jsx("span", { style: { fontSize: "0.8em", color: "#888" }, children: "Assembly products derive LOCUS from the assembly name" })
                      ] }),
                      /* @__PURE__ */ jsx("p", { children: "Description (GenBank DEFINITION):" }),
                      /* @__PURE__ */ jsx(
                        TextArea,
                        {
                          "data-testid": "description-input",
                          fill: true,
                          growVertically: true,
                          large: true,
                          style: {
                            minHeight: "100px",
                            fontFamily: "monospace",
                            fontSize: "12px"
                          },
                          value: selectedDescription,
                          onChange: (e) => {
                            const filteredValue = e.target.value.replace(/[\n\r]+/g, " ");
                            descriptionRef.current = filteredValue;
                            setSelectedDescription(filteredValue);
                          },
                          onKeyDown: (e) => {
                            if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) {
                              e.preventDefault();
                              handleSaveDescription();
                            }
                          }
                        }
                      )
                    ] })
                  ]
                }
              ),
              /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER, children: /* @__PURE__ */ jsxs("div", { className: Classes.DIALOG_FOOTER_ACTIONS, children: [
                rawNodeData?.name?.endsWith(".gen.gb") && /* @__PURE__ */ jsx(
                  Button,
                  {
                    icon: "link",
                    onClick: () => {
                      const assemblyPath = rawNodeData.path.replace(
                        /\.gen\.gb$/,
                        ".assembly"
                      );
                      window.location.hash = getFileUrl(assemblyPath);
                      setIsPathDialogOpen(false);
                    },
                    children: "Go to Assembly"
                  }
                ),
                /* @__PURE__ */ jsx(Button, { onClick: () => setIsPathDialogOpen(false), children: "Close" }),
                (rawNodeData?.name?.endsWith(".gb") || rawNodeData?.name?.endsWith(".gbk") || rawNodeData?.name?.endsWith(".genbank") || rawNodeData?.name?.endsWith(".fasta") || rawNodeData?.name?.endsWith(".fa")) && /* @__PURE__ */ jsx(
                  Button,
                  {
                    intent: Intent.PRIMARY,
                    loading: isSavingDescription,
                    onClick: handleSaveDescription,
                    children: "Save Description"
                  }
                ),
                /* @__PURE__ */ jsx(
                  Button,
                  {
                    intent: Intent.NONE,
                    onClick: () => {
                      navigator.clipboard.writeText(selectedPath);
                    },
                    children: "Copy Path"
                  }
                )
              ] }) })
            ]
          }
        ),
        /* @__PURE__ */ jsxs(
          Dialog,
          {
            isOpen: isGitCommitDialogOpen,
            onClose: (e) => {
              if (isGitDiffDialogOpen) return;
              setIsGitCommitDialogOpen(false);
            },
            title: "Git Commit",
            icon: "git-commit",
            canOutsideClickClose: true,
            canEscapeKeyClose: false,
            style: {
              width: gitCommitDialogSize.width,
              height: gitCommitDialogSize.height,
              display: "flex",
              flexDirection: "column",
              position: "relative"
            },
            children: [
              /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_BODY, style: { flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden", paddingBottom: 0 }, children: /* @__PURE__ */ jsxs("div", { style: { padding: "0 20px", flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden" }, children: [
                /* @__PURE__ */ jsx("p", { style: { marginTop: "10px", marginBottom: "10px", fontWeight: "bold" }, children: "Enter commit message:" }),
                /* @__PURE__ */ jsx(
                  TextArea,
                  {
                    autoFocus: true,
                    fill: true,
                    growVertically: false,
                    placeholder: "Commit message...",
                    value: gitCommitMessage,
                    onChange: (e) => setGitCommitMessage(e.target.value),
                    style: { minHeight: "80px", height: "100px", flexShrink: 0, marginBottom: "15px" },
                    onKeyDown: (e) => {
                      if (e.ctrlKey && e.key === "Enter") {
                        if (gitCommitMessage.trim() && !isGitOperating) {
                          handleGitCommit();
                        }
                      }
                    }
                  }
                ),
                (() => {
                  if (!gitStatus) return null;
                  const isUiDisplayable = (path) => ALL_EDITABLE_EXTENSIONS.some((ext) => path.toLowerCase().endsWith(ext));
                  const allStaged = Object.entries(gitStatus).filter(([path, status]) => isStaged(status) && isUiDisplayable(path));
                  const allUnstaged = Object.entries(gitStatus).filter(([path, status]) => !isStaged(status) && isUnstaged(status) && isUiDisplayable(path));
                  const deduplicate = (entries) => {
                    const seen = /* @__PURE__ */ new Set();
                    return entries.filter(([path, status]) => {
                      const parts = status.split(":");
                      const absPath = parts[2] || path;
                      if (seen.has(absPath)) return false;
                      seen.add(absPath);
                      return true;
                    });
                  };
                  const staged = deduplicate(allStaged);
                  const unstaged = deduplicate(allUnstaged);
                  const showStaged = staged.length > 0 || unstaged.length > 0;
                  const filteredStaged = staged.filter(([path]) => {
                    if (!gitFilter.trim()) return true;
                    const term = gitFilter.trim().toLowerCase();
                    const filename = path.split("/").pop().toLowerCase();
                    return filename.includes(term) || path.toLowerCase().includes(term);
                  });
                  const filteredUnstaged = unstaged.filter(([path]) => {
                    if (!gitFilter.trim()) return true;
                    const term = gitFilter.trim().toLowerCase();
                    const filename = path.split("/").pop().toLowerCase();
                    return filename.includes(term) || path.toLowerCase().includes(term);
                  });
                  return /* @__PURE__ */ jsxs("div", { style: { marginTop: "15px", flexGrow: 1, display: "flex", flexDirection: "column", overflow: "hidden" }, children: [
                    /* @__PURE__ */ jsx("div", { style: { display: "flex", justifyContent: "flex-end", marginBottom: "10px" }, children: /* @__PURE__ */ jsx(
                      InputGroup,
                      {
                        small: true,
                        leftIcon: "search",
                        placeholder: "Filter files...",
                        value: gitFilter,
                        onChange: (e) => setGitFilter(e.target.value),
                        style: { width: "180px" },
                        rightElement: gitFilter ? /* @__PURE__ */ jsx(Button, { minimal: true, small: true, icon: "cross", onClick: () => setGitFilter("") }) : void 0
                      }
                    ) }),
                    /* @__PURE__ */ jsxs("div", { style: { display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "5px", width: "100%", paddingRight: "18px" }, children: [
                      /* @__PURE__ */ jsxs("span", { style: { fontWeight: "bold", fontSize: "13px" }, children: [
                        "Staged Files: ",
                        staged.length
                      ] }),
                      staged.length > 0 && /* @__PURE__ */ jsx(
                        Button,
                        {
                          minimal: true,
                          small: true,
                          icon: staged.some(([p]) => unstagingPaths.includes(p)) ? /* @__PURE__ */ jsx(Spinner, { size: 12 }) : "small-cross",
                          onClick: () => handleUnstageAll(filteredStaged.map(([p]) => p)),
                          disabled: staged.some(([p]) => unstagingPaths.includes(p)),
                          title: "Unstage All",
                          style: { color: "#5c7080", marginLeft: "auto" }
                        }
                      )
                    ] }),
                    /* @__PURE__ */ jsx("div", { style: {
                      maxHeight: "200px",
                      overflow: "auto",
                      fontSize: "12px",
                      backgroundColor: "#f8f9fa",
                      padding: "8px",
                      borderRadius: "4px",
                      border: "1px solid #e1e4e8",
                      marginBottom: "10px"
                    }, children: filteredStaged.length > 0 ? filteredStaged.map(([path, status]) => {
                      const isUnstaging = unstagingPaths.includes(path);
                      const parts = status.split(":");
                      const code = parts[0];
                      const sourcePath = parts[1];
                      const getBasename = (p) => p.split("/").pop();
                      const shortDisplayPath = sourcePath ? `${getBasename(sourcePath)} -> ${getBasename(path)}` : getBasename(path);
                      const fullDisplayPath = sourcePath ? `${sourcePath} -> ${path}` : path;
                      const displayCode = isStaged(code) ? code[0] : code === "??" ? "+" : code.trim();
                      return /* @__PURE__ */ jsxs(
                        "div",
                        {
                          className: "git-commit-row",
                          style: {
                            fontFamily: "monospace",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            whiteSpace: "nowrap",
                            padding: "4px 8px",
                            borderRadius: "4px"
                          },
                          children: [
                            /* @__PURE__ */ jsxs("div", { style: { display: "flex", overflow: "hidden", alignItems: "center" }, children: [
                              /* @__PURE__ */ jsx(
                                "span",
                                {
                                  title: getStatusDescription(code),
                                  style: { width: "35px", color: isStaged(code) ? "#107310" : "#d91e1e", fontWeight: "bold", cursor: "help" },
                                  children: displayCode
                                }
                              ),
                              fileInfoMap[path]?.isSymlink && /* @__PURE__ */ jsx(Icon, { icon: "link", size: 12, style: { marginRight: "5px", color: "#888" }, title: "Alias (Symlink)" }),
                              /* @__PURE__ */ jsx(
                                "span",
                                {
                                  title: fullDisplayPath,
                                  style: {
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    display: "block",
                                    cursor: "help",
                                    minWidth: 0
                                  },
                                  children: shortDisplayPath
                                }
                              )
                            ] }),
                            /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center" }, children: [
                              /* @__PURE__ */ jsx(
                                Button,
                                {
                                  minimal: true,
                                  small: true,
                                  icon: "undo",
                                  title: "Revert File",
                                  onClick: (e) => {
                                    e.stopPropagation();
                                    handleGitRevertFile(path, sourcePath);
                                  },
                                  style: { marginRight: "4px" }
                                }
                              ),
                              /* @__PURE__ */ jsx(
                                Button,
                                {
                                  minimal: true,
                                  small: true,
                                  icon: "help",
                                  title: "View Diff",
                                  onClick: (e) => {
                                    e.stopPropagation();
                                    handleGitDiff(path);
                                  },
                                  style: { marginRight: "4px" }
                                }
                              ),
                              /* @__PURE__ */ jsx(
                                Button,
                                {
                                  minimal: true,
                                  small: true,
                                  icon: isUnstaging ? /* @__PURE__ */ jsx(Spinner, { size: 12 }) : "small-cross",
                                  onClick: (e) => {
                                    e.stopPropagation();
                                    handleUnstage(path);
                                  },
                                  disabled: isUnstaging,
                                  title: "Unstage"
                                }
                              )
                            ] })
                          ]
                        },
                        path
                      );
                    }) : /* @__PURE__ */ jsx("p", { style: { fontStyle: "italic", color: "#888", margin: 0 }, children: staged.length > 0 ? "No staged changes matching filter" : "No staged changes" }) }),
                    /* @__PURE__ */ jsxs(
                      "div",
                      {
                        style: {
                          fontWeight: "bold",
                          marginBottom: "5px",
                          fontSize: "13px",
                          display: "flex",
                          justify: "space-between",
                          alignItems: "center",
                          width: "100%",
                          paddingRight: "18px"
                        },
                        children: [
                          /* @__PURE__ */ jsxs(
                            "div",
                            {
                              "data-testid": "unstaged-changes-toggle",
                              style: { cursor: "pointer", display: "flex", alignItems: "center" },
                              onClick: () => setIsUnstagedChangesExpanded(!isUnstagedChangesExpanded),
                              children: [
                                /* @__PURE__ */ jsx(Icon, { icon: isUnstagedChangesExpanded ? "chevron-down" : "chevron-right", style: { marginRight: 5 } }),
                                "Unstaged Changes: ",
                                unstaged.length
                              ]
                            }
                          ),
                          unstaged.length > 0 && /* @__PURE__ */ jsx(
                            Button,
                            {
                              minimal: true,
                              small: true,
                              icon: unstaged.some(([p]) => stagingPaths.includes(p)) ? /* @__PURE__ */ jsx(Spinner, { size: 12 }) : "plus",
                              onClick: (e) => {
                                e.stopPropagation();
                                handleStageAll(filteredUnstaged.map(([p]) => p));
                              },
                              disabled: unstaged.some(([p]) => stagingPaths.includes(p)),
                              title: gitFilter.trim() ? "Stage All Filtered" : "Stage All",
                              style: { color: "#d91e1e", fontWeight: "bold", marginLeft: "auto" }
                            }
                          )
                        ]
                      }
                    ),
                    isUnstagedChangesExpanded && /* @__PURE__ */ jsx("div", { style: {
                      maxHeight: "200px",
                      overflow: "auto",
                      fontSize: "12px",
                      backgroundColor: "#f8f9fa",
                      padding: "8px",
                      borderRadius: "4px",
                      border: "1px solid #e1e4e8"
                    }, children: filteredUnstaged.length > 0 ? filteredUnstaged.map(([path, status]) => {
                      const isStaging = stagingPaths.includes(path);
                      const parts = status.split(":");
                      const code = parts[0];
                      const sourcePath = parts[1];
                      const getBasename = (p) => p.split("/").pop();
                      const shortDisplayPath = sourcePath ? `${getBasename(sourcePath)} -> ${getBasename(path)}` : getBasename(path);
                      const fullDisplayPath = sourcePath ? `${sourcePath} -> ${path}` : path;
                      const displayCode = code === "??" ? "+" : code.trim().split("").join(",");
                      return /* @__PURE__ */ jsxs(
                        "div",
                        {
                          className: "git-commit-row",
                          style: {
                            fontFamily: "monospace",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "space-between",
                            whiteSpace: "nowrap",
                            padding: "4px 8px",
                            borderRadius: "4px"
                          },
                          children: [
                            /* @__PURE__ */ jsxs("div", { style: { display: "flex", overflow: "hidden", alignItems: "center" }, children: [
                              /* @__PURE__ */ jsx(
                                "span",
                                {
                                  title: getStatusDescription(code),
                                  style: { width: "35px", color: code[0] === "?" ? "#107310" : "#d91e1e", fontWeight: "bold", cursor: "help" },
                                  children: displayCode
                                }
                              ),
                              fileInfoMap[path]?.isSymlink && /* @__PURE__ */ jsx(Icon, { icon: "link", size: 12, style: { marginRight: "5px", color: "#888" }, title: "Alias (Symlink)" }),
                              /* @__PURE__ */ jsx(
                                "span",
                                {
                                  title: fullDisplayPath,
                                  style: {
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    display: "block",
                                    cursor: "help",
                                    minWidth: 0
                                  },
                                  children: shortDisplayPath
                                }
                              )
                            ] }),
                            /* @__PURE__ */ jsxs("div", { style: { display: "flex", alignItems: "center" }, children: [
                              /* @__PURE__ */ jsx(
                                Button,
                                {
                                  minimal: true,
                                  small: true,
                                  icon: "undo",
                                  title: "Revert File",
                                  onClick: (e) => {
                                    e.stopPropagation();
                                    handleGitRevertFile(path, sourcePath);
                                  },
                                  style: { marginRight: "4px" }
                                }
                              ),
                              /* @__PURE__ */ jsx(
                                Button,
                                {
                                  minimal: true,
                                  small: true,
                                  icon: "help",
                                  title: "View Diff",
                                  onClick: (e) => {
                                    e.stopPropagation();
                                    handleGitDiff(path);
                                  },
                                  style: { marginRight: "4px" }
                                }
                              ),
                              /* @__PURE__ */ jsx(
                                Button,
                                {
                                  minimal: true,
                                  small: true,
                                  icon: isStaging ? /* @__PURE__ */ jsx(Spinner, { size: 12 }) : "plus",
                                  onClick: (e) => {
                                    e.stopPropagation();
                                    handleStage(path);
                                  },
                                  disabled: isStaging,
                                  title: "Stage"
                                }
                              )
                            ] })
                          ]
                        },
                        path
                      );
                    }) : /* @__PURE__ */ jsx("p", { style: { fontStyle: "italic", color: "#888", margin: 0 }, children: unstaged.length > 0 ? "No unstaged changes matching filter" : "No unstaged changes" }) })
                  ] });
                })()
              ] }) }),
              /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER, children: /* @__PURE__ */ jsxs("div", { className: Classes.DIALOG_FOOTER_ACTIONS, children: [
                /* @__PURE__ */ jsx(Button, { onClick: () => setIsGitCommitDialogOpen(false), children: "Close" }),
                /* @__PURE__ */ jsx(
                  Button,
                  {
                    intent: Intent.PRIMARY,
                    onClick: handleGitCommit,
                    loading: isGitOperating,
                    disabled: !gitCommitMessage.trim() || stagingPaths.length > 0 || unstagingPaths.length > 0,
                    children: "Commit"
                  }
                )
              ] }) }),
              /* @__PURE__ */ jsx(
                "div",
                {
                  style: {
                    position: "absolute",
                    bottom: "0px",
                    right: "0px",
                    width: "35px",
                    height: "35px",
                    cursor: "nwse-resize",
                    zIndex: 2e3,
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "flex-end",
                    padding: "4px"
                  },
                  onMouseDown: (e) => {
                    e.stopPropagation();
                    handleGitCommitResize(e);
                  },
                  children: /* @__PURE__ */ jsx("div", { style: {
                    width: 0,
                    height: 0,
                    borderStyle: "solid",
                    borderWidth: "0 0 15px 15px",
                    borderColor: "transparent transparent #888 transparent",
                    pointerEvents: "none"
                  } })
                }
              )
            ]
          }
        ),
        /* @__PURE__ */ jsxs(
          Dialog,
          {
            isOpen: isMoveDialogOpen,
            onClose: () => setIsMoveDialogOpen(false),
            title: "Move Item",
            icon: "move",
            children: [
              /* @__PURE__ */ jsxs("div", { className: Classes.DIALOG_BODY, children: [
                /* @__PURE__ */ jsx("p", { children: "Select destination folder:" }),
                /* @__PURE__ */ jsx(
                  FolderPicker,
                  {
                    autoFocus: true,
                    value: moveDestination,
                    onChange: setMoveDestination
                  }
                ),
                itemsToMove.length > 0 && /* @__PURE__ */ jsxs("div", { style: { marginTop: "15px", fontSize: "12px", color: "#666" }, children: [
                  "Moving: ",
                  itemsToMove.map((i) => i.name).join(", ")
                ] })
              ] }),
              /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER, children: /* @__PURE__ */ jsxs("div", { className: Classes.DIALOG_FOOTER_ACTIONS, children: [
                /* @__PURE__ */ jsx(Button, { onClick: () => setIsMoveDialogOpen(false), children: "Cancel" }),
                /* @__PURE__ */ jsx(
                  Button,
                  {
                    intent: Intent.PRIMARY,
                    loading: isMoving,
                    onClick: handleMoveItems,
                    children: "Move"
                  }
                )
              ] }) })
            ]
          }
        ),
        /* @__PURE__ */ jsxs(
          Dialog,
          {
            isOpen: isCommandOutputDialogOpen,
            onClose: () => setIsCommandOutputDialogOpen(false),
            title: commandTitle,
            icon: "code",
            canOutsideClickClose: true,
            style: { width: "80%" },
            children: [
              /* @__PURE__ */ jsx(
                "div",
                {
                  className: Classes.DIALOG_BODY,
                  onKeyDown: (e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsCommandOutputDialogOpen(false);
                    }
                  },
                  children: isGitOperating ? /* @__PURE__ */ jsxs("div", { style: { padding: 40, textAlign: "center" }, children: [
                    /* @__PURE__ */ jsx(Spinner, { size: Spinner.SIZE_LARGE }),
                    /* @__PURE__ */ jsx("div", { style: { marginTop: 20, fontSize: "16px" }, children: "Running command..." })
                  ] }) : /* @__PURE__ */ jsx(
                    "pre",
                    {
                      tabIndex: 0,
                      style: {
                        padding: "10px",
                        backgroundColor: "#1a1a1b",
                        color: "#d1d1d1",
                        borderRadius: "4px",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                        maxHeight: "500px",
                        overflow: "auto",
                        fontFamily: "monospace",
                        fontSize: "12px"
                      },
                      children: commandOutput
                    }
                  )
                }
              ),
              /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER, children: /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER_ACTIONS, children: /* @__PURE__ */ jsx(Button, { onClick: () => setIsCommandOutputDialogOpen(false), children: "Close" }) }) })
            ]
          }
        ),
        isMinimapDialogOpen && /* @__PURE__ */ jsxs(
          Dialog,
          {
            isOpen: true,
            onClose: () => setIsMinimapDialogOpen(false),
            title: "Create SAM Alignment",
            icon: "align-left",
            children: [
              /* @__PURE__ */ jsxs("div", { className: Classes.DIALOG_BODY, children: [
                /* @__PURE__ */ jsx("p", { children: "Select the reference sequence for the alignment:" }),
                /* @__PURE__ */ jsx(
                  HTMLSelect,
                  {
                    fill: true,
                    value: minimapReferencePath,
                    onChange: (e) => setMinimapReferencePath(e.currentTarget.value),
                    options: minimapTargetPaths.filter((p) => /\.(gb|gbk|genbank|gen\.gb|fasta|fa)$/i.test(p)).map((p) => ({ label: p.split("/").pop(), value: p }))
                  }
                )
              ] }),
              /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER, children: /* @__PURE__ */ jsxs("div", { className: Classes.DIALOG_FOOTER_ACTIONS, children: [
                /* @__PURE__ */ jsx(Button, { onClick: () => setIsMinimapDialogOpen(false), children: "Cancel" }),
                /* @__PURE__ */ jsx(
                  Button,
                  {
                    intent: Intent.PRIMARY,
                    onClick: async () => {
                      try {
                        const response = await axios.post("/api/tools/minimap2", {
                          files: minimapTargetPaths,
                          reference: minimapReferencePath
                        });
                        let alignmentPath = response.data.path;
                        if (alignmentPath) {
                          const ab1Paths = minimapTargetPaths.filter((p) => p.toLowerCase().endsWith(".ab1"));
                          if (ab1Paths.length > 0) {
                            try {
                              const encodedSamPath = alignmentPath.split("/").map((segment) => encodeURIComponent(segment)).join("/");
                              const samRes = await axios.get(`/api/file/${encodedSamPath}?t=${Date.now()}`);
                              let samData = samRes.data;
                              for (const ab1Path of ab1Paths) {
                                const ab1Encoded = ab1Path.split("/").map((s) => encodeURIComponent(s)).join("/");
                                const ab1Blob = await axios.get(`/api/file/${ab1Encoded}?t=${Date.now()}`, { responseType: "blob" });
                                const filename = ab1Path.split("/").pop();
                                const results = await anyToJson(ab1Blob.data, { fileName: filename });
                                if (results[0] && results[0].success && results[0].parsedSequence && results[0].parsedSequence.chromatogramData) {
                                  let chromData = results[0].parsedSequence.chromatogramData;
                                  const nameWithoutExt = filename.replace(/\.[^/.]+$/, "");
                                  const parsedSeq = results[0].parsedSequence;
                                  const reverseChromatogramData = (chrom) => {
                                    const complement = { A: "T", T: "A", G: "C", C: "G", a: "t", t: "a", g: "c", c: "g", n: "n", N: "N" };
                                    const revStr = (s) => s.split("").reverse().join("");
                                    const compStr = (s) => s.split("").map((c) => complement[c] || c).join("");
                                    const rcStr = (s) => revStr(compStr(s));
                                    if (!chrom.aTrace || !chrom.tTrace || !chrom.gTrace || !chrom.cTrace) return chrom;
                                    const newChrom = {
                                      ...chrom,
                                      aTrace: [...chrom.tTrace].reverse(),
                                      tTrace: [...chrom.aTrace].reverse(),
                                      gTrace: [...chrom.cTrace].reverse(),
                                      cTrace: [...chrom.gTrace].reverse(),
                                      qualNums: chrom.qualNums ? [...chrom.qualNums].reverse() : void 0
                                    };
                                    if (typeof chrom.baseCalls === "string") {
                                      newChrom.baseCalls = rcStr(chrom.baseCalls);
                                    } else if (Array.isArray(chrom.baseCalls)) {
                                      newChrom.baseCalls = chrom.baseCalls.map((c) => complement[c] || c).reverse();
                                    }
                                    const traceLen = chrom.aTrace.length;
                                    if (Array.isArray(chrom.basePos)) {
                                      newChrom.basePos = chrom.basePos.map((pos) => traceLen - 1 - pos).reverse();
                                    }
                                    return newChrom;
                                  };
                                  if (samData.pairwiseAlignments) {
                                    samData.pairwiseAlignments = samData.pairwiseAlignments.map((pair) => {
                                      if (pair[1] && pair[1].sequenceData) {
                                        const sd = pair[1].sequenceData;
                                        const track = pair[1];
                                        const matches = sd.name === filename || sd.name === nameWithoutExt || sd.id === filename || sd.id === nameWithoutExt || sd.id === parsedSeq.id || sd.name === parsedSeq.name || sd.id === parsedSeq.name;
                                        if (matches) {
                                          const isReversed = track.isReversed || track.strand === -1 || track.alignmentData && track.alignmentData.strand === -1;
                                          track.chromatogramData = isReversed ? reverseChromatogramData(chromData) : chromData;
                                        }
                                      }
                                      return pair;
                                    });
                                  }
                                }
                              }
                              await axios.post(`/api/file/${encodedSamPath}`, samData);
                            } catch (e) {
                              console.error("Error post-processing ab1 traces:", e);
                              window.toastr?.warning("Alignment created, but failed to extract some chromatogram traces");
                            }
                          }
                        }
                        window.toastr?.success("Successfully generated SAM JSON alignment");
                        setIsMinimapDialogOpen(false);
                        await fetchFiles();
                        if (alignmentPath) {
                          window.location.hash = `#/Standalone?file=${alignmentPath}`;
                        }
                      } catch (e) {
                        window.toastr?.error(`Error generating alignment: ${e.response?.data?.error || e.message}`);
                      }
                    },
                    children: "Generate"
                  }
                )
              ] }) })
            ]
          }
        ),
        /* @__PURE__ */ jsxs(
          Dialog,
          {
            isOpen: isInversionDialogOpen,
            onClose: () => setIsInversionDialogOpen(false),
            title: "Check for Inversion",
            icon: "swap-horizontal",
            children: [
              /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_BODY, children: isCheckingInversion ? /* @__PURE__ */ jsxs("div", { style: { textAlign: "center", padding: 20 }, children: [
                /* @__PURE__ */ jsx(Spinner, { size: 50 }),
                /* @__PURE__ */ jsx("p", { style: { marginTop: 10 }, children: "Running minimap2 check..." })
              ] }) : /* @__PURE__ */ jsxs("div", { children: [
                /* @__PURE__ */ jsxs("p", { children: [
                  "Comparing ",
                  /* @__PURE__ */ jsx("b", { children: inversionFiles[0]?.split("/").pop() }),
                  " and",
                  " ",
                  /* @__PURE__ */ jsx("b", { children: inversionFiles[1]?.split("/").pop() })
                ] }),
                inversionCheckResult && inversionCheckResult.length > 0 ? /* @__PURE__ */ jsxs(
                  "div",
                  {
                    style: {
                      background: "#fff9f0",
                      border: "1px solid #d9822b",
                      padding: 10,
                      borderRadius: 3,
                      marginTop: 10
                    },
                    children: [
                      /* @__PURE__ */ jsxs("h5", { style: { color: "#d9822b", marginTop: 0 }, children: [
                        /* @__PURE__ */ jsx(Icon, { icon: "warning-sign", style: { marginRight: 8 } }),
                        "Inversions Detected:"
                      ] }),
                      /* @__PURE__ */ jsx("ul", { style: { paddingLeft: 20, marginBottom: 0 }, children: inversionCheckResult.map((w, i) => /* @__PURE__ */ jsx("li", { children: w }, i)) })
                    ]
                  }
                ) : /* @__PURE__ */ jsxs(
                  "div",
                  {
                    style: {
                      background: "#f0fdf4",
                      border: "1px solid #166534",
                      padding: 15,
                      borderRadius: 3,
                      marginTop: 10,
                      display: "flex",
                      alignItems: "center"
                    },
                    children: [
                      /* @__PURE__ */ jsx(
                        Icon,
                        {
                          icon: "tick-circle",
                          intent: "success",
                          size: 20,
                          style: { marginRight: 10 }
                        }
                      ),
                      /* @__PURE__ */ jsx("span", { children: "No large inversions (\u2265 200bp) were detected between these sequences." })
                    ]
                  }
                )
              ] }) }),
              /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER, children: /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER_ACTIONS, children: /* @__PURE__ */ jsx(Button, { onClick: () => setIsInversionDialogOpen(false), children: "Close" }) }) })
            ]
          }
        ),
        /* @__PURE__ */ jsx(
          Dialog,
          {
            isOpen: isHistoryDialogOpen,
            onClose: () => setIsHistoryDialogOpen(false),
            title: `Revision History: ${historyFilePath}`,
            icon: "history",
            canOutsideClickClose: true,
            style: { width: "80%", maxWidth: "800px", minHeight: "400px" },
            children: /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_BODY, children: isLoadingHistory ? /* @__PURE__ */ jsx("div", { style: { display: "flex", justifyContent: "center", padding: "40px" }, children: /* @__PURE__ */ jsx(Spinner, {}) }) : historyData.length === 0 ? /* @__PURE__ */ jsx("p", { children: "No revision history found." }) : /* @__PURE__ */ jsx("div", { style: { maxHeight: "60vh", overflowY: "auto" }, children: /* @__PURE__ */ jsxs("table", { className: "bp3-html-table bp3-html-table-striped bp3-html-table-bordered", style: { width: "100%" }, children: [
              /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsxs("tr", { children: [
                /* @__PURE__ */ jsx("th", { children: "Date" }),
                /* @__PURE__ */ jsx("th", { children: "Message" }),
                /* @__PURE__ */ jsx("th", { children: "Author" }),
                /* @__PURE__ */ jsx("th", { children: "Actions" })
              ] }) }),
              /* @__PURE__ */ jsx("tbody", { children: historyData.map((commit, idx) => /* @__PURE__ */ jsxs("tr", { children: [
                /* @__PURE__ */ jsx("td", { style: { whiteSpace: "nowrap" }, children: commit.date }),
                /* @__PURE__ */ jsx("td", { children: commit.message }),
                /* @__PURE__ */ jsx("td", { children: commit.author }),
                /* @__PURE__ */ jsxs("td", { style: { whiteSpace: "nowrap" }, children: [
                  /* @__PURE__ */ jsx(
                    Button,
                    {
                      small: true,
                      intent: Intent.PRIMARY,
                      icon: "eye-open",
                      onClick: () => handleViewRevision(commit.hash),
                      style: { marginRight: 8 },
                      children: "View"
                    }
                  ),
                  /* @__PURE__ */ jsx(
                    Button,
                    {
                      small: true,
                      intent: Intent.WARNING,
                      icon: "undo",
                      disabled: idx === 0 && (!gitStatus || !gitStatus[historyFilePath]),
                      loading: restoringCommit === commit.hash,
                      onClick: () => handleRestoreRevision(commit.hash),
                      children: "Restore"
                    }
                  )
                ] })
              ] }, commit.hash)) })
            ] }) }) })
          }
        ),
        /* @__PURE__ */ jsx(
          Dialog,
          {
            icon: "comment",
            title: "File Comments",
            isOpen: isCommentsDialogOpen,
            onClose: () => setIsCommentsDialogOpen(false),
            style: { width: "500px", paddingBottom: 0 },
            children: /* @__PURE__ */ jsxs("div", { className: Classes.DIALOG_BODY, children: [
              /* @__PURE__ */ jsx("div", { style: { marginBottom: 15 }, children: /* @__PURE__ */ jsx("strong", { children: commentsFilePath }) }),
              isLoadingComments ? /* @__PURE__ */ jsx("div", { style: { display: "flex", justifyContent: "center", padding: "20px" }, children: /* @__PURE__ */ jsx(Spinner, { size: 30 }) }) : /* @__PURE__ */ jsx("div", { style: {
                maxHeight: "250px",
                overflowY: "auto",
                padding: "8px",
                borderRadius: "4px",
                border: "1px solid #e1e4e8",
                marginBottom: "15px"
              }, children: fileComments.length === 0 ? /* @__PURE__ */ jsx("div", { style: { color: "#888", fontStyle: "italic", textAlign: "center", padding: "20px" }, children: "No comments yet." }) : fileComments.map((comment, idx) => /* @__PURE__ */ jsxs(
                "div",
                {
                  className: "git-commit-row",
                  style: {
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    padding: "6px 8px",
                    borderRadius: "4px"
                  },
                  children: [
                    /* @__PURE__ */ jsx("div", { style: { flex: 1, whiteSpace: "pre-wrap", marginRight: "10px" }, children: comment }),
                    /* @__PURE__ */ jsx(
                      Button,
                      {
                        icon: "trash",
                        minimal: true,
                        intent: "danger",
                        onClick: () => handleDeleteComment(idx),
                        title: "Delete Comment"
                      }
                    )
                  ]
                },
                idx
              )) }),
              /* @__PURE__ */ jsxs("div", { style: { display: "flex", flexDirection: "column", gap: "10px" }, children: [
                /* @__PURE__ */ jsx(
                  TextArea,
                  {
                    fill: true,
                    placeholder: "Add a new comment...",
                    value: newCommentText,
                    onChange: (e) => setNewCommentText(e.target.value),
                    rows: 3
                  }
                ),
                /* @__PURE__ */ jsx(
                  Button,
                  {
                    intent: "primary",
                    text: "Add Comment",
                    icon: "add",
                    onClick: handleAddComment,
                    disabled: !newCommentText.trim(),
                    style: { alignSelf: "flex-end" }
                  }
                )
              ] })
            ] })
          }
        ),
        /* @__PURE__ */ jsxs(
          Dialog,
          {
            isOpen: isGitDiffDialogOpen,
            onClose: () => setIsGitDiffDialogOpen(false),
            title: `Git Diff: ${gitDiffPath}`,
            icon: "code",
            canOutsideClickClose: true,
            canEscapeKeyClose: false,
            portalClassName: "git-diff-dialog-portal",
            style: { width: "95%", maxWidth: "1600px" },
            children: [
              /* @__PURE__ */ jsx(
                "div",
                {
                  className: Classes.DIALOG_BODY,
                  onKeyDown: (e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      e.stopPropagation();
                      setIsGitDiffDialogOpen(false);
                    }
                  },
                  children: /* @__PURE__ */ jsx(
                    "pre",
                    {
                      tabIndex: 0,
                      style: {
                        padding: "10px",
                        backgroundColor: "#1e1e1e",
                        color: "#d4d4d4",
                        borderRadius: "4px",
                        whiteSpace: "pre-wrap",
                        wordBreak: "break-all",
                        maxHeight: "80vh",
                        overflow: "auto",
                        fontFamily: "'Fira Code', 'Cascadia Code', monospace",
                        fontSize: "12px",
                        lineHeight: "1.5"
                      },
                      children: gitDiff.split("\n").map((line, i) => {
                        let color = "inherit";
                        if (line.startsWith("+") && !line.startsWith("+++"))
                          color = "#4ec9b0";
                        if (line.startsWith("-") && !line.startsWith("---"))
                          color = "#f44747";
                        if (line.startsWith("@@")) color = "#569cd6";
                        return /* @__PURE__ */ jsx("div", { style: { color }, children: line }, i);
                      })
                    }
                  )
                }
              ),
              /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER, children: /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER_ACTIONS, children: /* @__PURE__ */ jsx(Button, { onClick: () => setIsGitDiffDialogOpen(false), children: "Close" }) }) })
            ]
          }
        ),
        /* @__PURE__ */ jsxs(
          Dialog,
          {
            isOpen: isHelpDialogOpen,
            onClose: () => setIsHelpDialogOpen(false),
            title: "Tips",
            icon: "help",
            canOutsideClickClose: true,
            style: {
              width: tipsDialogSize.width,
              height: tipsDialogSize.height,
              display: "flex",
              flexDirection: "column",
              position: "relative"
            },
            children: [
              /* @__PURE__ */ jsxs("div", { className: Classes.DIALOG_BODY, style: { flexGrow: 1, overflowY: "auto" }, children: [
                /* @__PURE__ */ jsx("h5", { className: Classes.HEADING, style: { marginBottom: 8 }, children: "Keyboard Shortcuts" }),
                /* @__PURE__ */ jsxs("table", { className: Classes.HTML_TABLE, style: { width: "100%", marginBottom: 20 }, children: [
                  /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsxs("tr", { children: [
                    /* @__PURE__ */ jsx("th", { style: { width: "180px", whiteSpace: "nowrap" }, children: "Action" }),
                    /* @__PURE__ */ jsx("th", { children: "Shortcut" })
                  ] }) }),
                  /* @__PURE__ */ jsxs("tbody", { children: [
                    /* @__PURE__ */ jsxs("tr", { children: [
                      /* @__PURE__ */ jsx("td", { children: "Import Sequence" }),
                      /* @__PURE__ */ jsxs("td", { children: [
                        /* @__PURE__ */ jsx("kbd", { children: "Alt" }),
                        " + ",
                        /* @__PURE__ */ jsx("kbd", { children: "U" })
                      ] })
                    ] }),
                    /* @__PURE__ */ jsxs("tr", { children: [
                      /* @__PURE__ */ jsx("td", { children: "Git Commit" }),
                      /* @__PURE__ */ jsxs("td", { children: [
                        /* @__PURE__ */ jsx("kbd", { children: "Alt" }),
                        " + ",
                        /* @__PURE__ */ jsx("kbd", { children: "C" })
                      ] })
                    ] }),
                    /* @__PURE__ */ jsxs("tr", { children: [
                      /* @__PURE__ */ jsx("td", { children: "Search Files" }),
                      /* @__PURE__ */ jsxs("td", { children: [
                        /* @__PURE__ */ jsx("kbd", { children: "Alt" }),
                        " + ",
                        /* @__PURE__ */ jsx("kbd", { children: "K" })
                      ] })
                    ] }),
                    /* @__PURE__ */ jsxs("tr", { children: [
                      /* @__PURE__ */ jsx("td", { children: "Toggle Project Files" }),
                      /* @__PURE__ */ jsxs("td", { children: [
                        /* @__PURE__ */ jsx("kbd", { children: "Alt" }),
                        " + ",
                        /* @__PURE__ */ jsx("kbd", { children: "P" })
                      ] })
                    ] }),
                    /* @__PURE__ */ jsxs("tr", { children: [
                      /* @__PURE__ */ jsx("td", { children: "New Item" }),
                      /* @__PURE__ */ jsxs("td", { children: [
                        /* @__PURE__ */ jsx("kbd", { children: "Alt" }),
                        " + ",
                        /* @__PURE__ */ jsx("kbd", { children: "N" })
                      ] })
                    ] }),
                    /* @__PURE__ */ jsxs("tr", { children: [
                      /* @__PURE__ */ jsx("td", { children: "Show Tips" }),
                      /* @__PURE__ */ jsxs("td", { children: [
                        /* @__PURE__ */ jsx("kbd", { children: "Alt" }),
                        " + ",
                        /* @__PURE__ */ jsx("kbd", { children: "?" })
                      ] })
                    ] })
                  ] })
                ] }),
                /* @__PURE__ */ jsx("h5", { className: Classes.HEADING, style: { marginBottom: 8 }, children: "Advanced Search Filters" }),
                /* @__PURE__ */ jsxs("table", { className: Classes.HTML_TABLE, style: { width: "100%" }, children: [
                  /* @__PURE__ */ jsx("thead", { children: /* @__PURE__ */ jsxs("tr", { children: [
                    /* @__PURE__ */ jsx("th", { style: { width: "180px", whiteSpace: "nowrap" }, children: "Feature" }),
                    /* @__PURE__ */ jsx("th", { children: "Syntax & Example" })
                  ] }) }),
                  /* @__PURE__ */ jsxs("tbody", { children: [
                    /* @__PURE__ */ jsxs("tr", { children: [
                      /* @__PURE__ */ jsx("td", { style: { whiteSpace: "nowrap" }, children: "Inventory Filter" }),
                      /* @__PURE__ */ jsxs("td", { children: [
                        /* @__PURE__ */ jsx("code", { style: { display: "inline", whiteSpace: "nowrap" }, children: "in_inventory:true" }),
                        " or ",
                        /* @__PURE__ */ jsx("code", { style: { display: "inline", whiteSpace: "nowrap" }, children: "in_inventory:false" }),
                        " (aliases: ",
                        /* @__PURE__ */ jsx("code", { style: { display: "inline", whiteSpace: "nowrap" }, children: "yes" }),
                        ", ",
                        /* @__PURE__ */ jsx("code", { style: { display: "inline", whiteSpace: "nowrap" }, children: "no" }),
                        ")"
                      ] })
                    ] }),
                    /* @__PURE__ */ jsxs("tr", { children: [
                      /* @__PURE__ */ jsx("td", { style: { whiteSpace: "nowrap" }, children: "Boolean AND" }),
                      /* @__PURE__ */ jsxs("td", { children: [
                        "Separate terms with space (e.g. ",
                        /* @__PURE__ */ jsx("code", { style: { display: "inline", whiteSpace: "nowrap" }, children: "pDTX promoter" }),
                        ")"
                      ] })
                    ] }),
                    /* @__PURE__ */ jsxs("tr", { children: [
                      /* @__PURE__ */ jsx("td", { style: { whiteSpace: "nowrap" }, children: "Boolean OR" }),
                      /* @__PURE__ */ jsxs("td", { children: [
                        "Use ",
                        /* @__PURE__ */ jsx("code", { style: { display: "inline", whiteSpace: "nowrap" }, children: "or" }),
                        " between terms (e.g. ",
                        /* @__PURE__ */ jsx("code", { style: { display: "inline", whiteSpace: "nowrap" }, children: "attr2 or attl3" }),
                        ")"
                      ] })
                    ] }),
                    /* @__PURE__ */ jsxs("tr", { children: [
                      /* @__PURE__ */ jsx("td", { style: { whiteSpace: "nowrap" }, children: "Folder Filter" }),
                      /* @__PURE__ */ jsxs("td", { children: [
                        /* @__PURE__ */ jsx("code", { style: { display: "inline", whiteSpace: "nowrap" }, children: "folder:folder_name" }),
                        " to filter by parent folder"
                      ] })
                    ] })
                  ] })
                ] })
              ] }),
              /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER, children: /* @__PURE__ */ jsx("div", { className: Classes.DIALOG_FOOTER_ACTIONS, children: /* @__PURE__ */ jsx(Button, { onClick: () => setIsHelpDialogOpen(false), children: "Close" }) }) }),
              /* @__PURE__ */ jsx(
                "div",
                {
                  style: {
                    position: "absolute",
                    bottom: "0px",
                    right: "0px",
                    width: "35px",
                    height: "35px",
                    cursor: "nwse-resize",
                    zIndex: 2e3,
                    display: "flex",
                    alignItems: "flex-end",
                    justifyContent: "flex-end",
                    padding: "4px"
                  },
                  onMouseDown: (e) => {
                    e.stopPropagation();
                    handleTipsResize(e);
                  },
                  children: /* @__PURE__ */ jsx("div", { style: {
                    width: 0,
                    height: 0,
                    borderStyle: "solid",
                    borderWidth: "0 0 15px 15px",
                    borderColor: "transparent transparent #888 transparent",
                    pointerEvents: "none"
                  } })
                }
              )
            ]
          }
        )
      ]
    }
  );
};
export default FileExplorer;
